const ANALYSIS_DEFAULT_LANG = "en-GB";

function normalizeLookupKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/[‐-‒–—−]/g, "-")
    .replace(/\s+/g, " ")
    .replace(/\s*\/\s*/g, "/");
}

function compactLookupKey(value) {
  return normalizeLookupKey(value).replace(/\s+/g, "");
}

function normalizeEsportsGameLookupText(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[‐-‒–—−]/g, "-")
    .replace(/\u00A0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function inferEsportsGameFromText(value) {
  const t = normalizeEsportsGameLookupText(value);
  if (!t) return "";

  // Prefer explicit phrases / unambiguous tokens only. Avoid broad fuzzy guessing.
  if (/\bcounter\s*-\s*strike\s*2\b/i.test(t) || /\bcounter\s+strike\s*2\b/i.test(t) || /\bcs2\b/i.test(t)) {
    return "CS2";
  }
  if (/\bdota\s*2\b/i.test(t) || /\bdota2\b/i.test(t)) return "Dota 2";
  if (/\bleague\s*of\s*legends\b/i.test(t) || /\blol\b/i.test(t)) return "League of Legends";
  if (/\bvalorant\b/i.test(t)) return "Valorant";
  if (/\bmobile\s*legends\b/i.test(t) || /\bmlbb\b/i.test(t) || /\bmobile\s*legends\s*b(?:ang)?\s*b(?:ang)?\b/i.test(t)) {
    return "Mobile Legends";
  }
  if (/\bpubg\b/i.test(t) || /\bplayerunknown'?s\s+battlegrounds\b/i.test(t)) return "PUBG";
  if (/\barena\s+of\s+valor\b/i.test(t)) return "Arena of Valor";
  if (/\bwild\s+rift\b/i.test(t)) return "Wild Rift";
  if (/\bhonor\s+of\s+kings\b/i.test(t)) return "Honor of Kings";
  if (/\brainbow\s+six\b/i.test(t) || /\brainbow\s+6\b/i.test(t) || /\br6s\b/i.test(t)) return "Rainbow Six";
  if (/\boverwatch\s*2\b/i.test(t) || /\bow2\b/i.test(t)) return "Overwatch 2";

  return "";
}

function normalizeEsportsGameLabel(item) {
  if (!item) return "";
  if (typeof item === "string") return inferEsportsGameFromText(item);
  if (typeof item !== "object") return inferEsportsGameFromText(String(item));

  const ev = item?.event || item?.fixture?.event;
  const fx = item?.fixture;

  function collectGroupCandidates(values) {
    const out = new Set();
    for (let i = 0; i < values.length; i += 1) {
      const v = values[i];
      if (v == null || v === "") continue;
      const inferred = inferEsportsGameFromText(v);
      if (inferred) out.add(inferred);
    }
    return out;
  }

  // Note: `item.game` can be stale/mis-assigned; use conflict-aware consensus across sources (not first-match wins).
  const groupA = collectGroupCandidates([item?.esportGame, item?.gameName, item?.game, ev?.game, fx?.game]);
  const groupB = collectGroupCandidates([
    item?.league,
    item?.tournament,
    item?.competition,
    item?.tournamentName,
    item?.competitionName,
    item?.leagueName,
    fx?.leagueName,
    fx?.competition,
    fx?.competitionName,
    fx?.tournamentName,
    ev?.leagueName,
    ev?.competition,
    ev?.competitionName,
    ev?.tournamentName
  ]);
  const groupC = collectGroupCandidates([item?.match, item?.matchName, item?.eventName, item?.event_name]);

  const all = new Set([...groupA, ...groupB, ...groupC]);
  if (all.size === 1) return Array.from(all)[0] || "";

  const supportByGame = new Map();
  const bump = (game) => supportByGame.set(game, (supportByGame.get(game) || 0) + 1);
  const applyGroupSupport = (group) => {
    group.forEach((game) => bump(game));
  };
  applyGroupSupport(groupA);
  applyGroupSupport(groupB);
  applyGroupSupport(groupC);

  // Conservative resolution:
  // - Return if a canonical game is supported by >= 2 independent source groups.
  // - Otherwise, avoid guessing (ties/unresolved conflicts -> "").
  let bestGame = "";
  let bestSupport = 0;
  let tie = false;
  supportByGame.forEach((count, game) => {
    if (count > bestSupport) {
      bestSupport = count;
      bestGame = game;
      tie = false;
    } else if (count === bestSupport && count > 0) {
      tie = true;
    }
  });

  if (!tie && bestSupport >= 2) return bestGame;

  return "";
}

// League normalization is intentionally separate from the general translation
// normalization (markets/selections), so we can safely handle separators like
// ":" and "·" without affecting other translation systems.
function normalizeLeagueLookupKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/[‐-‒–—−]/g, "-")
    .replace(/\u00A0/g, " ")
    // Normalize common league separators into a deterministic token.
    .replace(/\s*:\s*/g, " - ")
    .replace(/\s*·\s*/g, " - ")
    // Only normalize " - " when there is actual whitespace around the dash.
    // This keeps aliases like "j-league" stable while still handling
    // patterns like "Euro U21 - Qualification".
    .replace(/\s+-\s+/g, " - ")
    // Collapse repeated whitespace.
    .replace(/\s+/g, " ")
    .trim();
}

function compactLeagueLookupKey(value) {
  return normalizeLeagueLookupKey(value).replace(/\s+/g, "");
}

function isDev() {
  return typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production";
}

const __analysisUnmappedCache = new Set();

function warnUnmapped(type, raw, normalized) {
  if (!isDev()) return;
  const key = `${type}::${normalized}`;
  if (__analysisUnmappedCache.has(key)) return;
  __analysisUnmappedCache.add(key);
  console.warn(`[analysisDataMaps] Unmapped ${type}:`, {
    raw,
    normalized
  });
}

function buildNormalizedMap(source) {
  const out = {};
  Object.keys(source || {}).forEach((key) => {
    out[normalizeLookupKey(key)] = source[key];
    out[compactLookupKey(key)] = source[key];
  });
  return out;
}

function buildNormalizedLeagueMap(source) {
  const out = {};
  Object.keys(source || {}).forEach((key) => {
    out[normalizeLeagueLookupKey(key)] = source[key];
    out[compactLeagueLookupKey(key)] = source[key];
  });
  return out;
}

const LEAGUE_TRANSLATIONS = {
  "Premier League": {
    "en-GB": "Premier League",
    "ja-JP": "プレミアリーグ",
    "it-IT": "Premier League",
    "es-ES": "Premier League",
    "ru-RU": "Премьер-лига",
    "fr-FR": "Premier League",
    "de-DE": "Premier League",
    "tr-TR": "Premier League",
    "zh-CN": "英超",
    "th-TH": "พรีเมียร์ลีก",
    "vi-VN": "Ngoại hạng Anh",
    "ko-KR": "프리미어리그",
    "en-SG": "Premier League",
    "id-ID": "Premier League",
    "nl-NL": "Premier League"
  },
  "English Premier League": {
    "en-GB": "English Premier League",
    "ja-JP": "プレミアリーグ",
    "it-IT": "Premier League",
    "es-ES": "Premier League",
    "ru-RU": "Премьер-лига",
    "fr-FR": "Premier League",
    "de-DE": "Premier League",
    "tr-TR": "Premier League",
    "zh-CN": "英超",
    "th-TH": "พรีเมียร์ลีก",
    "vi-VN": "Ngoại hạng Anh",
    "ko-KR": "프리미어리그",
    "en-SG": "English Premier League",
    "id-ID": "English Premier League",
    "nl-NL": "English Premier League"
  },
  "La Liga": {
    "en-GB": "La Liga",
    "ja-JP": "ラ・リーガ",
    "it-IT": "La Liga",
    "es-ES": "La Liga",
    "ru-RU": "Ла Лига",
    "fr-FR": "La Liga",
    "de-DE": "La Liga",
    "tr-TR": "La Liga",
    "zh-CN": "西甲",
    "th-TH": "ลาลีกา",
    "vi-VN": "La Liga",
    "ko-KR": "라리가",
    "en-SG": "La Liga",
    "id-ID": "La Liga",
    "nl-NL": "La Liga"
  },
  "Bundesliga": {
    "en-GB": "Bundesliga",
    "ja-JP": "ブンデスリーガ",
    "it-IT": "Bundesliga",
    "es-ES": "Bundesliga",
    "ru-RU": "Бундеслига",
    "fr-FR": "Bundesliga",
    "de-DE": "Bundesliga",
    "tr-TR": "Bundesliga",
    "zh-CN": "德甲",
    "th-TH": "บุนเดสลีกา",
    "vi-VN": "Bundesliga",
    "ko-KR": "분데스리가",
    "en-SG": "Bundesliga",
    "id-ID": "Bundesliga",
    "nl-NL": "Bundesliga"
  },
  "Serie A": {
    "en-GB": "Serie A",
    "ja-JP": "セリエA",
    "it-IT": "Serie A",
    "es-ES": "Serie A",
    "ru-RU": "Серия А",
    "fr-FR": "Serie A",
    "de-DE": "Serie A",
    "tr-TR": "Serie A",
    "zh-CN": "意甲",
    "th-TH": "กัลโช่ เซเรีย อา",
    "vi-VN": "Serie A",
    "ko-KR": "세리에 A",
    "en-SG": "Serie A",
    "id-ID": "Serie A",
    "nl-NL": "Serie A"
  },
  "Ligue 1": {
    "en-GB": "Ligue 1",
    "ja-JP": "リーグ・アン",
    "it-IT": "Ligue 1",
    "es-ES": "Ligue 1",
    "ru-RU": "Лига 1",
    "fr-FR": "Ligue 1",
    "de-DE": "Ligue 1",
    "tr-TR": "Ligue 1",
    "zh-CN": "法甲",
    "th-TH": "ลีกเอิง",
    "vi-VN": "Ligue 1",
    "ko-KR": "리그 1",
    "en-SG": "Ligue 1",
    "id-ID": "Ligue 1",
    "nl-NL": "Ligue 1"
  },
  "Champions League": {
    "en-GB": "Champions League",
    "ja-JP": "チャンピオンズリーグ",
    "it-IT": "Champions League",
    "es-ES": "Champions League",
    "ru-RU": "Лига чемпионов",
    "fr-FR": "Ligue des champions",
    "de-DE": "Champions League",
    "tr-TR": "Şampiyonlar Ligi",
    "zh-CN": "欧冠",
    "th-TH": "ยูฟ่าแชมเปียนส์ลีก",
    "vi-VN": "Champions League",
    "ko-KR": "챔피언스리그",
    "en-SG": "Champions League",
    "id-ID": "Champions League",
    "nl-NL": "Champions League"
  },
  "Europa League": {
    "en-GB": "Europa League",
    "ja-JP": "ヨーロッパリーグ",
    "it-IT": "Europa League",
    "es-ES": "Europa League",
    "ru-RU": "Лига Европы",
    "fr-FR": "Ligue Europa",
    "de-DE": "Europa League",
    "tr-TR": "Avrupa Ligi",
    "zh-CN": "欧联",
    "th-TH": "ยูโรปาลีก",
    "vi-VN": "Europa League",
    "ko-KR": "유로파리그",
    "en-SG": "Europa League",
    "id-ID": "Europa League",
    "nl-NL": "Europa League"
  },
  "Conference League": {
    "en-GB": "Conference League",
    "ja-JP": "カンファレンスリーグ",
    "it-IT": "Conference League",
    "es-ES": "Conference League",
    "ru-RU": "Лига конференций",
    "fr-FR": "Ligue Conférence",
    "de-DE": "Conference League",
    "tr-TR": "Konferans Ligi",
    "zh-CN": "欧协联",
    "th-TH": "ยูโรปาคอนเฟอเรนซ์ลีก",
    "vi-VN": "Conference League",
    "ko-KR": "컨퍼런스리그",
    "en-SG": "Conference League",
    "id-ID": "Conference League",
    "nl-NL": "Conference League"
  },
  MLS: {
    "en-GB": "MLS",
    "ja-JP": "MLS",
    "it-IT": "MLS",
    "es-ES": "MLS",
    "ru-RU": "MLS",
    "fr-FR": "MLS",
    "de-DE": "MLS",
    "tr-TR": "MLS",
    "zh-CN": "美职联",
    "th-TH": "MLS",
    "vi-VN": "MLS",
    "ko-KR": "MLS",
    "en-SG": "MLS",
    "id-ID": "MLS",
    "nl-NL": "MLS"
  },
  "EFL Championship": {
    "en-GB": "EFL Championship",
    "ja-JP": "EFLチャンピオンシップ",
    "it-IT": "EFL Championship",
    "es-ES": "Championship",
    "ru-RU": "Чемпионшип",
    "fr-FR": "Championship",
    "de-DE": "Championship",
    "tr-TR": "Championship",
    "zh-CN": "英冠",
    "th-TH": "แชมเปียนชิป",
    "vi-VN": "Hạng Nhất Anh",
    "ko-KR": "EFL 챔피언십",
    "en-SG": "EFL Championship",
    "id-ID": "EFL Championship",
    "nl-NL": "Championship"
  },
  "J1 League": {
    "en-GB": "J1 League",
    "ja-JP": "J1リーグ",
    "it-IT": "J1 League",
    "es-ES": "J1 League",
    "ru-RU": "J1 Лига",
    "fr-FR": "J1 League",
    "de-DE": "J1 League",
    "tr-TR": "J1 Ligi",
    "zh-CN": "J1联赛",
    "th-TH": "เจลีก ดิวิชัน 1",
    "vi-VN": "J1 League",
    "ko-KR": "J1리그",
    "en-SG": "J1 League",
    "id-ID": "J1 League",
    "nl-NL": "J1 League"
  },
  "Serie B Brazil": {
    "en-GB": "Serie B Brazil",
    "ja-JP": "ブラジル・セリエB",
    "it-IT": "Serie B Brasile",
    "es-ES": "Serie B Brasil",
    "ru-RU": "Серия B Бразилия",
    "fr-FR": "Serie B Brésil",
    "de-DE": "Serie B Brasilien",
    "tr-TR": "Brezilya Serie B",
    "zh-CN": "巴西乙",
    "th-TH": "บราซิล เซเรีย บี",
    "vi-VN": "Serie B Brazil",
    "ko-KR": "브라질 세리에 B",
    "en-SG": "Serie B Brazil",
    "id-ID": "Serie B Brasil",
    "nl-NL": "Serie B Brazilië"
  },
  NBA: {
    "en-GB": "NBA",
    "ja-JP": "NBA",
    "it-IT": "NBA",
    "es-ES": "NBA",
    "ru-RU": "НБА",
    "fr-FR": "NBA",
    "de-DE": "NBA",
    "tr-TR": "NBA",
    "zh-CN": "NBA",
    "th-TH": "NBA",
    "vi-VN": "NBA",
    "ko-KR": "NBA",
    "en-SG": "NBA",
    "id-ID": "NBA",
    "nl-NL": "NBA"
  },
  ATP: {
    "en-GB": "ATP",
    "ja-JP": "ATP",
    "it-IT": "ATP",
    "es-ES": "ATP",
    "ru-RU": "ATP",
    "fr-FR": "ATP",
    "de-DE": "ATP",
    "tr-TR": "ATP",
    "zh-CN": "ATP",
    "th-TH": "ATP",
    "vi-VN": "ATP",
    "ko-KR": "ATP",
    "en-SG": "ATP",
    "id-ID": "ATP",
    "nl-NL": "ATP"
  },
  WTA: {
    "en-GB": "WTA",
    "ja-JP": "WTA",
    "it-IT": "WTA",
    "es-ES": "WTA",
    "ru-RU": "WTA",
    "fr-FR": "WTA",
    "de-DE": "WTA",
    "tr-TR": "WTA",
    "zh-CN": "WTA",
    "th-TH": "WTA",
    "vi-VN": "WTA",
    "ko-KR": "WTA",
    "en-SG": "WTA",
    "id-ID": "WTA",
    "nl-NL": "WTA"
  },
  MLB: {
    "en-GB": "MLB",
    "ja-JP": "MLB",
    "it-IT": "MLB",
    "es-ES": "MLB",
    "ru-RU": "MLB",
    "fr-FR": "MLB",
    "de-DE": "MLB",
    "tr-TR": "MLB",
    "zh-CN": "MLB",
    "th-TH": "MLB",
    "vi-VN": "MLB",
    "ko-KR": "MLB",
    "en-SG": "MLB",
    "id-ID": "MLB",
    "nl-NL": "MLB"
  },
  NHL: {
    "en-GB": "NHL",
    "ja-JP": "NHL",
    "it-IT": "NHL",
    "es-ES": "NHL",
    "ru-RU": "NHL",
    "fr-FR": "NHL",
    "de-DE": "NHL",
    "tr-TR": "NHL",
    "zh-CN": "NHL",
    "th-TH": "NHL",
    "vi-VN": "NHL",
    "ko-KR": "NHL",
    "en-SG": "NHL",
    "id-ID": "NHL",
    "nl-NL": "NHL"
  },
  AHL: {
    "en-GB": "AHL",
    "ja-JP": "AHL",
    "it-IT": "AHL",
    "es-ES": "AHL",
    "ru-RU": "AHL",
    "fr-FR": "AHL",
    "de-DE": "AHL",
    "tr-TR": "AHL",
    "zh-CN": "AHL",
    "th-TH": "AHL",
    "vi-VN": "AHL",
    "ko-KR": "AHL",
    "en-SG": "AHL",
    "id-ID": "AHL",
    "nl-NL": "AHL"
  },
  WNBA: {
    "en-GB": "WNBA",
    "ja-JP": "WNBA",
    "it-IT": "WNBA",
    "es-ES": "WNBA",
    "ru-RU": "WNBA",
    "fr-FR": "WNBA",
    "de-DE": "WNBA",
    "tr-TR": "WNBA",
    "zh-CN": "WNBA",
    "th-TH": "WNBA",
    "vi-VN": "WNBA",
    "ko-KR": "WNBA",
    "en-SG": "WNBA",
    "id-ID": "WNBA",
    "nl-NL": "WNBA"
  },
  NCAAB: {
    "en-GB": "NCAAB",
    "ja-JP": "NCAAB",
    "it-IT": "NCAAB",
    "es-ES": "NCAAB",
    "ru-RU": "NCAAB",
    "fr-FR": "NCAAB",
    "de-DE": "NCAAB",
    "tr-TR": "NCAAB",
    "zh-CN": "NCAAB",
    "th-TH": "NCAAB",
    "vi-VN": "NCAAB",
    "ko-KR": "NCAAB",
    "en-SG": "NCAAB",
    "id-ID": "NCAAB",
    "nl-NL": "NCAAB"
  },
  NFL: {
    "en-GB": "NFL",
    "ja-JP": "NFL",
    "it-IT": "NFL",
    "es-ES": "NFL",
    "ru-RU": "NFL",
    "fr-FR": "NFL",
    "de-DE": "NFL",
    "tr-TR": "NFL",
    "zh-CN": "NFL",
    "th-TH": "NFL",
    "vi-VN": "NFL",
    "ko-KR": "NFL",
    "en-SG": "NFL",
    "id-ID": "NFL",
    "nl-NL": "NFL"
  },
  NCAAF: {
    "en-GB": "NCAAF",
    "ja-JP": "NCAAF",
    "it-IT": "NCAAF",
    "es-ES": "NCAAF",
    "ru-RU": "NCAAF",
    "fr-FR": "NCAAF",
    "de-DE": "NCAAF",
    "tr-TR": "NCAAF",
    "zh-CN": "NCAAF",
    "th-TH": "NCAAF",
    "vi-VN": "NCAAF",
    "ko-KR": "NCAAF",
    "en-SG": "NCAAF",
    "id-ID": "NCAAF",
    "nl-NL": "NCAAF"
  },
  UFC: {
    "en-GB": "UFC",
    "ja-JP": "UFC",
    "it-IT": "UFC",
    "es-ES": "UFC",
    "ru-RU": "UFC",
    "fr-FR": "UFC",
    "de-DE": "UFC",
    "tr-TR": "UFC",
    "zh-CN": "UFC",
    "th-TH": "UFC",
    "vi-VN": "UFC",
    "ko-KR": "UFC",
    "en-SG": "UFC",
    "id-ID": "UFC",
    "nl-NL": "UFC"
  },
  "PGA Tour": {
    "en-GB": "PGA Tour",
    "ja-JP": "PGAツアー",
    "it-IT": "PGA Tour",
    "es-ES": "PGA Tour",
    "ru-RU": "Тур PGA",
    "fr-FR": "PGA Tour",
    "de-DE": "PGA Tour",
    "tr-TR": "PGA Tour",
    "zh-CN": "PGA巡回赛",
    "th-TH": "PGA Tour",
    "vi-VN": "PGA Tour",
    "ko-KR": "PGA 투어",
    "en-SG": "PGA Tour",
    "id-ID": "PGA Tour",
    "nl-NL": "PGA Tour"
  },
  "ITF Women": {
    "en-GB": "ITF Women",
    "ja-JP": "ITF 女子",
    "it-IT": "ITF Donne",
    "es-ES": "ITF Femenino",
    "ru-RU": "ITF — женщины",
    "fr-FR": "ITF Femmes",
    "de-DE": "ITF Damen",
    "tr-TR": "ITF Kadınlar",
    "zh-CN": "ITF女子",
    "th-TH": "ITF หญิง",
    "vi-VN": "ITF Nữ",
    "ko-KR": "ITF 여자",
    "en-SG": "ITF Women",
    "id-ID": "ITF Putri",
    "nl-NL": "ITF Vrouwen"
  },
  "ITF Men": {
    "en-GB": "ITF Men",
    "ja-JP": "ITF 男子",
    "it-IT": "ITF Uomini",
    "es-ES": "ITF Masculino",
    "ru-RU": "ITF — мужчины",
    "fr-FR": "ITF Hommes",
    "de-DE": "ITF Herren",
    "tr-TR": "ITF Erkekler",
    "zh-CN": "ITF男子",
    "th-TH": "ITF ชาย",
    "vi-VN": "ITF Nam",
    "ko-KR": "ITF 남자",
    "en-SG": "ITF Men",
    "id-ID": "ITF Putra",
    "nl-NL": "ITF Mannen"
  },
  Challenger: {
    "en-GB": "Challenger",
    "ja-JP": "チャレンジャー",
    "it-IT": "Challenger",
    "es-ES": "Challenger",
    "ru-RU": "Челленджер",
    "fr-FR": "Challenger",
    "de-DE": "Challenger",
    "tr-TR": "Challenger",
    "zh-CN": "挑战赛",
    "th-TH": "แชลเลนเจอร์",
    "vi-VN": "Challenger",
    "ko-KR": "챌린저",
    "en-SG": "Challenger",
    "id-ID": "Challenger",
    "nl-NL": "Challenger"
  },
  "CONCACAF Champions Cup": {
    "en-GB": "CONCACAF Champions Cup",
    "ja-JP": "CONCACAFチャンピオンズカップ",
    "it-IT": "CONCACAF Champions Cup",
    "es-ES": "Copa de Campeones CONCACAF",
    "ru-RU": "Кубок чемпионов КОНКАКАФ",
    "fr-FR": "CONCACAF Champions Cup",
    "de-DE": "CONCACAF Champions Cup",
    "tr-TR": "CONCACAF Şampiyonlar Kupası",
    "zh-CN": "中北美冠杯",
    "th-TH": "CONCACAF แชมเปียนส์คัพ",
    "vi-VN": "CONCACAF Champions Cup",
    "ko-KR": "CONCACAF 챔피언스컵",
    "en-SG": "CONCACAF Champions Cup",
    "id-ID": "CONCACAF Champions Cup",
    "nl-NL": "CONCACAF Champions Cup"
  },
  "CONMEBOL Copa Sudamericana": {
    "en-GB": "CONMEBOL Copa Sudamericana",
    "ja-JP": "コパ・スダメリカーナ",
    "it-IT": "CONMEBOL Copa Sudamericana",
    "es-ES": "Copa Sudamericana",
    "ru-RU": "Кубок Южной Америки КОНМЕБОЛ",
    "fr-FR": "CONMEBOL Copa Sudamericana",
    "de-DE": "CONMEBOL Copa Sudamericana",
    "tr-TR": "CONMEBOL Copa Sudamericana",
    "zh-CN": "南美杯",
    "th-TH": "โคปาซูดาเมริกานา",
    "vi-VN": "CONMEBOL Copa Sudamericana",
    "ko-KR": "코파 수다메리카나",
    "en-SG": "CONMEBOL Copa Sudamericana",
    "id-ID": "CONMEBOL Copa Sudamericana",
    "nl-NL": "CONMEBOL Copa Sudamericana"
  },
  "Copa Libertadores": {
    "en-GB": "Copa Libertadores",
    "ja-JP": "コパ・リベルタドーレス",
    "it-IT": "Copa Libertadores",
    "es-ES": "Copa Libertadores",
    "ru-RU": "Кубок Либертадорес",
    "fr-FR": "Copa Libertadores",
    "de-DE": "Copa Libertadores",
    "tr-TR": "Copa Libertadores",
    "zh-CN": "解放者杯",
    "th-TH": "โคปาลิเบร์ตาดอเรส",
    "vi-VN": "Copa Libertadores",
    "ko-KR": "코파 리베르타도레스",
    "en-SG": "Copa Libertadores",
    "id-ID": "Copa Libertadores",
    "nl-NL": "Copa Libertadores"
  },
  "Friendly International": {
    "en-GB": "Friendly International",
    "ja-JP": "国際親善試合",
    "it-IT": "Internationals Amichevoli",
    "es-ES": "Amistosos Internacionales",
    "ru-RU": "Международные товарищеские матчи",
    "fr-FR": "Matchs amicaux internationaux",
    "de-DE": "Internationale Freundschaftsspiele",
    "tr-TR": "Uluslararası Hazırlık Maçları",
    "zh-CN": "国际友谊赛",
    "th-TH": "ฟุตบอลอุ่นเครื่องนานาชาติ",
    "vi-VN": "Giao hữu quốc tế",
    "ko-KR": "국제 친선 경기",
    "en-SG": "Friendly International",
    "id-ID": "Persahabatan Internasional",
    "nl-NL": "Internationale Vriendschappelijke Wedstrijden"
  },
  "Euro U21 - Qualification": {
    "en-GB": "Euro U21 - Qualification",
    "ja-JP": "欧州U21予選",
    "it-IT": "Qualificazioni Euro U21",
    "es-ES": "Clasificación Euro U21",
    "ru-RU": "Отборочные матчи Евро U21",
    "fr-FR": "Qualifications Euro U21",
    "de-DE": "U21-Qualifikation der EM",
    "tr-TR": "Euro U21 Eleme",
    "zh-CN": "欧洲U21预选赛",
    "th-TH": "รอบคัดเลือกยูโร ยู21",
    "vi-VN": "Vòng loại Euro U21",
    "ko-KR": "유로 U21 예선",
    "en-SG": "Euro U21 - Qualification",
    "id-ID": "Kualifikasi Euro U21",
    "nl-NL": "Euro U21 Kwalificatie"
  },
  "World Cup - Qualification - Promotion": {
    "en-GB": "World Cup - Qualification - Promotion",
    "ja-JP": "ワールドカップ予選 - 昇格",
    "it-IT": "Qualificazioni Mondiali - Promozione",
    "es-ES": "Clasificación Mundial - Promoción",
    "ru-RU": "Отбор ЧМ - Повышение",
    "fr-FR": "Qualifications Coupe du Monde - Promotion",
    "de-DE": "WM-Qualifikation - Aufstieg",
    "tr-TR": "Dünya Kupası - Elemeler - Yükselme",
    "zh-CN": "世界杯预选赛 - 升级",
    "th-TH": "รอบคัดเลือกฟุตบอลโลก - เลื่อนชั้น",
    "vi-VN": "Vòng loại World Cup - Thăng hạng",
    "ko-KR": "월드컵 예선 - 승격",
    "en-SG": "World Cup - Qualification - Promotion",
    "id-ID": "Kualifikasi Piala Dunia - Promosi",
    "nl-NL": "WK-kwalificatie - Promotie"
  },
  "Copa de Primera - Apertura": {
    "en-GB": "Copa de Primera - Apertura",
    "ja-JP": "コパ・デ・プリメーラ - アペルトゥーラ",
    "it-IT": "Coppa de Primera - Apertura",
    "es-ES": "Copa de Primera - Apertura",
    "ru-RU": "Кубок Первого дивизиона - Апертура",
    "fr-FR": "Coupe de Primera - Apertura",
    "de-DE": "Copa de Primera - Apertura",
    "tr-TR": "Copa de Primera - Apertura",
    "zh-CN": "普里梅拉杯 - 阿佩尔图拉",
    "th-TH": "โคปา เด ปริเมรา - อาปערטูรา",
    "vi-VN": "Copa de Primera - Apertura",
    "ko-KR": "코파 데 프리메라 - 아페르투라",
    "en-SG": "Copa de Primera - Apertura",
    "id-ID": "Copa de Primera - Apertura",
    "nl-NL": "Copa de Primera - Apertura"
  }
};

const LEAGUE_ALIASES = {
  "uefa champions league": "Champions League",
  "champions league": "Champions League",
  "uefa europa league": "Europa League",
  "europa league": "Europa League",
  "uefa conference league": "Conference League",
  "uefa europa conference league": "Conference League",
  "conference league": "Conference League",
  "mls": "MLS",
  "major league soccer": "MLS",
  "efl championship": "EFL Championship",
  "j1 league": "J1 League",
  "j league": "J1 League",
  "j-league": "J1 League",
  "serie b brazil": "Serie B Brazil",
  "brazil serie b": "Serie B Brazil",
  "brazilian serie b": "Serie B Brazil",
  nba: "NBA",
  ncaab: "NCAAB",
  nfl: "NFL",
  ncaaf: "NCAAF",
  ufc: "UFC",
  "pga tour": "PGA Tour",
  pga: "PGA Tour",
  atp: "ATP",
  wta: "WTA",
  "euro u21 qualification": "Euro U21 - Qualification",
  "world cup qualification - promotion": "World Cup - Qualification - Promotion",
  "world cup qualification promotion": "World Cup - Qualification - Promotion",
  "copa de primera - apertura": "Copa de Primera - Apertura",
  "copa de primera apertura": "Copa de Primera - Apertura",
  epl: "English Premier League",
  "english premier league": "English Premier League",
  "la liga": "La Liga",
  laliga: "La Liga",
  "serie a": "Serie A",
  seriea: "Serie A",
  bundesliga: "Bundesliga",
  mlb: "MLB",
  nhl: "NHL",
  ahl: "AHL",
  wnba: "WNBA",
  "itf women": "ITF Women",
  "itf men": "ITF Men",
  itfwomen: "ITF Women",
  itfmen: "ITF Men",
  challenger: "Challenger",
  "concacaf champions cup": "CONCACAF Champions Cup",
  "concacaf champions league": "CONCACAF Champions Cup",
  concacafchampionscup: "CONCACAF Champions Cup",
  concacafchampionsleague: "CONCACAF Champions Cup",
  "concacaf - champions cup": "CONCACAF Champions Cup",
  "concacaf - champions league": "CONCACAF Champions Cup",
  itf_women: "ITF Women",
  itf_men: "ITF Men",
  "conmebol copa sudamericana": "CONMEBOL Copa Sudamericana",
  conmebolcopasudamericana: "CONMEBOL Copa Sudamericana",
  "copa libertadores": "Copa Libertadores",
  copalibertadores: "Copa Libertadores"
};

const COUNTRY_NAME_TRANSLATIONS = {
  Argentina: {
    "en-GB": "Argentina",
    "ja-JP": "アルゼンチン",
    "ru-RU": "Аргентина",
    "zh-CN": "阿根廷",
    "th-TH": "อาร์เจนตินา",
    "vi-VN": "Argentina",
    "ko-KR": "아르헨티나"
  },
  Australia: {
    "en-GB": "Australia",
    "ja-JP": "オーストラリア",
    "ru-RU": "Австралия",
    "zh-CN": "澳大利亚",
    "th-TH": "ออสเตรเลีย",
    "vi-VN": "Úc",
    "ko-KR": "호주"
  },
  Belgium: {
    "en-GB": "Belgium",
    "ja-JP": "ベルギー",
    "ru-RU": "Бельгия",
    "zh-CN": "比利时",
    "th-TH": "เบลเยียม",
    "vi-VN": "Bỉ",
    "ko-KR": "벨기에"
  },
  Brazil: {
    "en-GB": "Brazil",
    "ja-JP": "ブラジル",
    "ru-RU": "Бразилия",
    "zh-CN": "巴西",
    "th-TH": "บราซิล",
    "vi-VN": "Brazil",
    "ko-KR": "브라질"
  },
  China: {
    "en-GB": "China",
    "ja-JP": "中国",
    "ru-RU": "Китай",
    "zh-CN": "中国",
    "th-TH": "จีน",
    "vi-VN": "Trung Quốc",
    "ko-KR": "중국"
  },
  England: {
    "en-GB": "England",
    "ja-JP": "イングランド",
    "ru-RU": "Англия",
    "zh-CN": "英格兰",
    "th-TH": "อังกฤษ",
    "vi-VN": "Anh",
    "ko-KR": "잉글랜드"
  },
  France: {
    "en-GB": "France",
    "ja-JP": "フランス",
    "ru-RU": "Франция",
    "zh-CN": "法国",
    "th-TH": "ฝรั่งเศส",
    "vi-VN": "Pháp",
    "ko-KR": "프랑스"
  },
  Germany: {
    "en-GB": "Germany",
    "ja-JP": "ドイツ",
    "ru-RU": "Германия",
    "zh-CN": "德国",
    "th-TH": "เยอรมนี",
    "vi-VN": "Đức",
    "ko-KR": "독일"
  },
  Italy: {
    "en-GB": "Italy",
    "ja-JP": "イタリア",
    "ru-RU": "Италия",
    "zh-CN": "意大利",
    "th-TH": "อิตาลี",
    "vi-VN": "Ý",
    "ko-KR": "이탈리아"
  },
  Greece: {
    "en-GB": "Greece",
    "ja-JP": "ギリシャ",
    "ru-RU": "Греция",
    "zh-CN": "希腊",
    "th-TH": "กรีซ",
    "vi-VN": "Hy Lạp",
    "ko-KR": "그리스"
  },
  Japan: {
    "en-GB": "Japan",
    "ja-JP": "日本",
    "ru-RU": "Япония",
    "zh-CN": "日本",
    "th-TH": "ญี่ปุ่น",
    "vi-VN": "Nhật Bản",
    "ko-KR": "일본"
  },
  Mexico: {
    "en-GB": "Mexico",
    "ja-JP": "メキシコ",
    "ru-RU": "Мексика",
    "zh-CN": "墨西哥",
    "th-TH": "เม็กซิโก",
    "vi-VN": "Mexico",
    "ko-KR": "멕시코"
  },
  Netherlands: {
    "en-GB": "Netherlands",
    "ja-JP": "オランダ",
    "ru-RU": "Нидерланды",
    "zh-CN": "荷兰",
    "th-TH": "เนเธอร์แลนด์",
    "vi-VN": "Hà Lan",
    "ko-KR": "네덜란드"
  },
  Poland: {
    "en-GB": "Poland",
    "ja-JP": "ポーランド",
    "ru-RU": "Польша",
    "zh-CN": "波兰",
    "th-TH": "โปแลนด์",
    "vi-VN": "Ba Lan",
    "ko-KR": "폴란드"
  },
  Portugal: {
    "en-GB": "Portugal",
    "ja-JP": "ポルトガル",
    "ru-RU": "Португалия",
    "zh-CN": "葡萄牙",
    "th-TH": "โปรตุเกส",
    "vi-VN": "Bồ Đào Nha",
    "ko-KR": "포르투갈"
  },
  Scotland: {
    "en-GB": "Scotland",
    "ja-JP": "スコットランド",
    "ru-RU": "Шотландия",
    "zh-CN": "苏格兰",
    "th-TH": "สกอตแลนด์",
    "vi-VN": "Scotland",
    "ko-KR": "스코틀랜드"
  },
  SouthKorea: {
    "en-GB": "South Korea",
    "ja-JP": "韓国",
    "ru-RU": "Южная Корея",
    "zh-CN": "韩国",
    "th-TH": "เกาหลีใต้",
    "vi-VN": "Hàn Quốc",
    "ko-KR": "대한민국"
  },
  Spain: {
    "en-GB": "Spain",
    "ja-JP": "スペイン",
    "ru-RU": "Испания",
    "zh-CN": "西班牙",
    "th-TH": "สเปน",
    "vi-VN": "Tây Ban Nha",
    "ko-KR": "스페인"
  },
  Turkey: {
    "en-GB": "Turkey",
    "ja-JP": "トルコ",
    "ru-RU": "Турция",
    "zh-CN": "土耳其",
    "th-TH": "ตุรกี",
    "vi-VN": "Thổ Nhĩ Kỳ",
    "ko-KR": "튀르키예"
  },
  USA: {
    "en-GB": "USA",
    "ja-JP": "アメリカ",
    "ru-RU": "США",
    "zh-CN": "美国",
    "th-TH": "สหรัฐอเมริกา",
    "vi-VN": "Mỹ",
    "ko-KR": "미국"
  },
  World: {
    "en-GB": "World",
    "ja-JP": "世界",
    "it-IT": "Mondo",
    "es-ES": "Mundo",
    "ru-RU": "Мир",
    "fr-FR": "Monde",
    "de-DE": "Welt",
    "tr-TR": "Dünya",
    "zh-CN": "世界",
    "th-TH": "โลก",
    "vi-VN": "Thế giới",
    "ko-KR": "세계",
    "en-SG": "World",
    "id-ID": "Dunia",
    "nl-NL": "Wereld"
  },
  Europe: {
    "en-GB": "Europe",
    "ja-JP": "ヨーロッパ",
    "it-IT": "Europa",
    "es-ES": "Europa",
    "ru-RU": "Европа",
    "fr-FR": "Europe",
    "de-DE": "Europa",
    "tr-TR": "Avrupa",
    "zh-CN": "欧洲",
    "th-TH": "ยุโรป",
    "vi-VN": "Châu Âu",
    "ko-KR": "유럽",
    "en-SG": "Europe",
    "id-ID": "Eropa",
    "nl-NL": "Europa"
  },
  Paraguay: {
    "en-GB": "Paraguay",
    "ja-JP": "パラグアイ",
    "it-IT": "Paraguay",
    "es-ES": "Paraguay",
    "ru-RU": "Парагвай",
    "fr-FR": "Paraguay",
    "de-DE": "Paraguay",
    "tr-TR": "Paraguay",
    "zh-CN": "巴拉圭",
    "th-TH": "ปารากวัย",
    "vi-VN": "Paraguay",
    "ko-KR": "파라과이",
    "en-SG": "Paraguay",
    "id-ID": "Paraguay",
    "nl-NL": "Paraguay"
  }
};

const COUNTRY_ALIASES = {
  argentina: "Argentina",
  argentinian: "Argentina",
  australia: "Australia",
  australian: "Australia",
  belgium: "Belgium",
  belgian: "Belgium",
  brazil: "Brazil",
  brazilian: "Brazil",
  china: "China",
  chinese: "China",
  england: "England",
  english: "England",
  france: "France",
  french: "France",
  germany: "Germany",
  german: "Germany",
  italy: "Italy",
  italian: "Italy",
  greece: "Greece",
  greek: "Greece",
  hellas: "Greece",
  japan: "Japan",
  japanese: "Japan",
  mexico: "Mexico",
  mexican: "Mexico",
  netherlands: "Netherlands",
  dutch: "Netherlands",
  holland: "Netherlands",
  poland: "Poland",
  polish: "Poland",
  portugal: "Portugal",
  portuguese: "Portugal",
  scotland: "Scotland",
  scottish: "Scotland",
  spain: "Spain",
  spanish: "Spain",
  turkey: "Turkey",
  turkish: "Turkey",
  usa: "USA",
  us: "USA",
  "united states": "USA",
  america: "USA",
  "south korea": "SouthKorea",
  korea: "SouthKorea",
  korean: "SouthKorea",
  world: "World",
  europe: "Europe",
  paraguay: "Paraguay",
  paraguayan: "Paraguay"
};

const KEEP_AS_IS_MAJOR_LEAGUES = new Set([
  "Premier League",
  "English Premier League",
  "La Liga",
  "Bundesliga",
  "Serie A",
  "Ligue 1",
  "Champions League",
  "Europa League",
  "Conference League",
  "NBA",
  "ATP",
  "WTA",
  "MLB",
  "NHL",
  "WNBA",
  "NCAAB",
  "NFL",
  "NCAAF",
  "UFC",
  "PGA Tour",
  // Branded / globally recognizable (also explicitly translated).
  "MLS"
]);

function shouldKeepLeagueAsIs(resolvedKey) {
  const key = String(resolvedKey || "").trim();
  if (!key) return false;
  return KEEP_AS_IS_MAJOR_LEAGUES.has(key);
}

function stripLeagueStageSuffix(rawRemainder) {
  const s = String(rawRemainder || "").trim();
  if (!s) return "";
  // Keep conservative: only strip explicit "play off" / "playoff" stage annotations.
  return s.replace(/\(\s*play\s*off[s]?\s*\)$/i, "").replace(/\s+/g, " ").trim();
}

function translateCountrySuffixedLeagueLabel(leagueLabel, countryKey, lang) {
  const league = String(leagueLabel || "").trim();
  const country = String(countryKey || "").trim();
  if (!league || !country) return "";
  const countryLabel = getCountryTranslation(country, lang);
  if (!countryLabel) return "";
  if (league.toLowerCase().includes(String(countryLabel).toLowerCase())) return league;
  return `${league} - ${countryLabel}`.trim();
}

function inferDomesticCountryKeyFromLeague(raw) {
  const n = normalizeLeagueLookupKey(raw);
  if (!n) return null;
  // Avoid misclassifying Brazil Serie B as Italy.
  if (/\b(serie\s*b\s*brazil|brazil\s+serie\s*b)\b/i.test(n)) return null;

  if (/\b(eredivisie|eerste\s+divisie|tweede\s+divisie)\b/i.test(n)) return "Netherlands";
  if (
    /\b(national\s+league|league\s+one|league\s+two|efl\s+championship|english\s+championship)\b/i.test(n)
  ) {
    return "England";
  }
  if (/\bserie\s*b\b/i.test(n) || /\bserie\s*c\b/i.test(n)) return "Italy";
  if (/\b2\.\s*bundesliga\b/i.test(n) || /\bbundesliga\s*2\b/i.test(n)) return "Germany";
  if (/\bligue\s*2\b/i.test(n)) return "France";
  if (/\bsuper\s+league\s*1\b/i.test(n)) return "Greece";

  return null;
}

function translateRemainderLeagueLabel(rawRemainder, lang) {
  const remainderRaw = stripLeagueStageSuffix(rawRemainder);
  if (!remainderRaw) return "";

  // First: try direct league translations for the remainder itself.
  const n = normalizeLeagueLookupKey(remainderRaw);
  const c = compactLeagueLookupKey(remainderRaw);
  const direct = LEAGUE_TRANSLATIONS_MAP[n] || LEAGUE_TRANSLATIONS_MAP[c];
  if (direct) return direct[lang] || direct[ANALYSIS_DEFAULT_LANG] || remainderRaw;

  // Then: handle known domestic patterns, including "Super League 1".
  const r = normalizeLeagueLookupKey(remainderRaw);
  if (/^super league\s*\d+\b/i.test(r)) {
    // Keep label conservative here: users expect the league name intact (often already English/branded).
    return prettifyLeagueCodeFallback(remainderRaw) || remainderRaw;
  }

  return prettifyLeagueCodeFallback(remainderRaw) || remainderRaw;
}

// Deterministic aliases for pattern keys that vary slightly from backend
// formatting (e.g. missing "-" separators).
const LEAGUE_PATTERN_ALIASES = {
  "euro u21 qualification": "euro u21 - qualification",
  "world cup qualification - promotion": "world cup - qualification - promotion",
  "world cup qualification promotion": "world cup - qualification - promotion",
  "copa de primera - apertura": "copa de primera - apertura",
  "copa de primera apertura": "copa de primera - apertura"
};

const LEAGUE_PATTERN_TRANSLATIONS = {
  championship: {
    "en-GB": "Championship",
    "ja-JP": "チャンピオンシップ",
    "ru-RU": "Чемпионшип",
    "zh-CN": "冠军联赛级别",
    "th-TH": "แชมเปียนชิป",
    "vi-VN": "Hạng Nhất",
    "ko-KR": "챔피언십"
  },
  "league one": {
    "en-GB": "League One",
    "ja-JP": "リーグ1",
    "ru-RU": "Лига 1",
    "zh-CN": "甲级联赛 1",
    "th-TH": "ลีก วัน",
    "vi-VN": "Hạng Hai",
    "ko-KR": "리그 1"
  },
  "friendly international": {
    "en-GB": "Friendly International",
    "ja-JP": "国際親善試合",
    "it-IT": "Internationals Amichevoli",
    "es-ES": "Amistosos Internacionales",
    "ru-RU": "Международные товарищеские матчи",
    "fr-FR": "Matchs amicaux internationaux",
    "de-DE": "Internationale Freundschaftsspiele",
    "tr-TR": "Uluslararası Hazırlık Maçları",
    "zh-CN": "国际友谊赛",
    "th-TH": "ฟุตบอลอุ่นเครื่องนานาชาติ",
    "vi-VN": "Giao hữu quốc tế",
    "ko-KR": "국제 친선 경기",
    "en-SG": "Friendly International",
    "id-ID": "Persahabatan Internasional",
    "nl-NL": "Internationale Vriendschappelijke Wedstrijden"
  },
  "euro u21 - qualification": {
    "en-GB": "Euro U21 - Qualification",
    "ja-JP": "欧州U21予選",
    "it-IT": "Qualificazioni Euro U21",
    "es-ES": "Clasificación Euro U21",
    "ru-RU": "Отборочные матчи Евро U21",
    "fr-FR": "Qualifications Euro U21",
    "de-DE": "U21-Qualifikation der EM",
    "tr-TR": "Euro U21 Eleme",
    "zh-CN": "欧洲U21预选赛",
    "th-TH": "รอบคัดเลือกยูโร ยู21",
    "vi-VN": "Vòng loại Euro U21",
    "ko-KR": "유로 U21 예선",
    "en-SG": "Euro U21 - Qualification",
    "id-ID": "Kualifikasi Euro U21",
    "nl-NL": "Euro U21 Kwalificatie"
  },
  "world cup - qualification - promotion": {
    "en-GB": "World Cup - Qualification - Promotion",
    "ja-JP": "ワールドカップ予選 - 昇格",
    "it-IT": "Qualificazioni Mondiali - Promozione",
    "es-ES": "Clasificación Mundial - Promoción",
    "ru-RU": "Отбор ЧМ - Повышение",
    "fr-FR": "Qualifications Coupe du Monde - Promotion",
    "de-DE": "WM-Qualifikation - Aufstieg",
    "tr-TR": "Dünya Kupası - Elemeler - Yükselme",
    "zh-CN": "世界杯预选赛 - 升级",
    "th-TH": "รอบคัดเลือกฟุตบอลโลก - เลื่อนชั้น",
    "vi-VN": "Vòng loại World Cup - Thăng hạng",
    "ko-KR": "월드컵 예선 - 승격",
    "en-SG": "World Cup - Qualification - Promotion",
    "id-ID": "Kualifikasi Piala Dunia - Promosi",
    "nl-NL": "WK-kwalificatie - Promotie"
  },
  "copa de primera - apertura": {
    "en-GB": "Copa de Primera - Apertura",
    "ja-JP": "コパ・デ・プリメーラ - アペルトゥーラ",
    "it-IT": "Coppa de Primera - Apertura",
    "es-ES": "Copa de Primera - Apertura",
    "ru-RU": "Кубок Первого дивизиона - Апертура",
    "fr-FR": "Coupe de Primera - Apertura",
    "de-DE": "Copa de Primera - Apertura",
    "tr-TR": "Copa de Primera - Apertura",
    "zh-CN": "普里梅拉杯 - 阿佩尔图拉",
    "th-TH": "โคปา เด ปริเมรา - อาปערטูรา",
    "vi-VN": "Copa de Primera - Apertura",
    "ko-KR": "코파 데 프리메라 - 아페르투라",
    "en-SG": "Copa de Primera - Apertura",
    "id-ID": "Copa de Primera - Apertura",
    "nl-NL": "Copa de Primera - Apertura"
  },
  "league two": {
    "en-GB": "League Two",
    "ja-JP": "リーグ2",
    "ru-RU": "Лига 2",
    "zh-CN": "乙级联赛 2",
    "th-TH": "ลีก ทู",
    "vi-VN": "Hạng Ba",
    "ko-KR": "리그 2"
  },
  "division 1": {
    "en-GB": "Division 1",
    "ja-JP": "ディビジョン1",
    "ru-RU": "Дивизион 1",
    "zh-CN": "第1级别",
    "th-TH": "ดิวิชัน 1",
    "vi-VN": "Hạng 1",
    "ko-KR": "디비전 1"
  },
  "division 2": {
    "en-GB": "Division 2",
    "ja-JP": "ディビジョン2",
    "ru-RU": "Дивизион 2",
    "zh-CN": "第2级别",
    "th-TH": "ดิวิชัน 2",
    "vi-VN": "Hạng 2",
    "ko-KR": "디비전 2"
  },
  "division 3": {
    "en-GB": "Division 3",
    "ja-JP": "ディビジョン3",
    "ru-RU": "Дивизион 3",
    "zh-CN": "第3级别",
    "th-TH": "ดิวิชัน 3",
    "vi-VN": "Hạng 3",
    "ko-KR": "디비전 3"
  },
  "division 4": {
    "en-GB": "Division 4",
    "ja-JP": "ディビジョン4",
    "ru-RU": "Дивизион 4",
    "zh-CN": "第4级别",
    "th-TH": "ดิวิชัน 4",
    "vi-VN": "Hạng 4",
    "ko-KR": "디비전 4"
  },
  "serie a": {
    "en-GB": "Serie A",
    "ja-JP": "セリエA",
    "ru-RU": "Серия A",
    "zh-CN": "甲级",
    "th-TH": "เซเรีย อา",
    "vi-VN": "Serie A",
    "ko-KR": "세리에 A"
  },
  "serie b": {
    "en-GB": "Serie B",
    "ja-JP": "セリエB",
    "ru-RU": "Серия B",
    "zh-CN": "乙级",
    "th-TH": "เซเรีย บี",
    "vi-VN": "Serie B",
    "ko-KR": "세리에 B"
  },
  "serie c": {
    "en-GB": "Serie C",
    "ja-JP": "セリエC",
    "ru-RU": "Серия C",
    "zh-CN": "丙级",
    "th-TH": "เซเรีย ซี",
    "vi-VN": "Serie C",
    "ko-KR": "세리에 C"
  },
  "bundesliga 2": {
    "en-GB": "Bundesliga 2",
    "ja-JP": "ブンデスリーガ2",
    "ru-RU": "Бундеслига 2",
    "zh-CN": "德乙级别",
    "th-TH": "บุนเดสลีกา 2",
    "vi-VN": "Bundesliga 2",
    "ko-KR": "분데스리가 2"
  },
  "ligue 2": {
    "en-GB": "Ligue 2",
    "ja-JP": "リーグ2",
    "ru-RU": "Лига 2",
    "zh-CN": "法乙级别",
    "th-TH": "ลีกเอิง 2",
    "vi-VN": "Ligue 2",
    "ko-KR": "리그 2"
  },
  "laliga 2": {
    "en-GB": "LaLiga 2",
    "ja-JP": "ラ・リーガ2",
    "ru-RU": "Ла Лига 2",
    "zh-CN": "西乙",
    "th-TH": "ลาลีกา 2",
    "vi-VN": "LaLiga 2",
    "ko-KR": "라리가 2"
  },
  cup: {
    "en-GB": "Cup",
    "ja-JP": "カップ",
    "ru-RU": "Кубок",
    "zh-CN": "杯赛",
    "th-TH": "คัพ",
    "vi-VN": "Cúp",
    "ko-KR": "컵"
  },
  league: {
    "en-GB": "League",
    "ja-JP": "リーグ",
    "ru-RU": "Лига",
    "zh-CN": "联赛",
    "th-TH": "ลีก",
    "vi-VN": "Giải",
    "ko-KR": "리그"
  },
  "super league": {
    "en-GB": "Super League",
    "ja-JP": "スーパーリーグ",
    "ru-RU": "Суперлига",
    "zh-CN": "超级联赛",
    "th-TH": "ซูเปอร์ลีก",
    "vi-VN": "Siêu Giải",
    "ko-KR": "슈퍼리그"
  },
  "reserve league": {
    "en-GB": "Reserve League",
    "ja-JP": "リザーブリーグ",
    "ru-RU": "Резервная лига",
    "zh-CN": "预备队联赛",
    "th-TH": "ลีกสำรอง",
    "vi-VN": "Giải Dự bị",
    "ko-KR": "리저브 리그"
  },
  reserve: {
    "en-GB": "Reserve",
    "ja-JP": "リザーブ",
    "ru-RU": "Резерв",
    "zh-CN": "预备队",
    "th-TH": "สำรอง",
    "vi-VN": "Dự bị",
    "ko-KR": "리저브"
  },
  women: {
    "en-GB": "Women",
    "ja-JP": "女子",
    "ru-RU": "Женщины",
    "zh-CN": "女子",
    "th-TH": "หญิง",
    "vi-VN": "Nữ",
    "ko-KR": "여자"
  },
  youth: {
    "en-GB": "Youth",
    "ja-JP": "ユース",
    "ru-RU": "Молодёжная",
    "zh-CN": "青年",
    "th-TH": "เยาวชน",
    "vi-VN": "Trẻ",
    "ko-KR": "유스"
  },
  u19: {
    "en-GB": "U19",
    "ja-JP": "U19",
    "ru-RU": "U19",
    "zh-CN": "U19",
    "th-TH": "U19",
    "vi-VN": "U19",
    "ko-KR": "U19"
  },
  u20: {
    "en-GB": "U20",
    "ja-JP": "U20",
    "ru-RU": "U20",
    "zh-CN": "U20",
    "th-TH": "U20",
    "vi-VN": "U20",
    "ko-KR": "U20"
  },
  u21: {
    "en-GB": "U21",
    "ja-JP": "U21",
    "ru-RU": "U21",
    "zh-CN": "U21",
    "th-TH": "U21",
    "vi-VN": "U21",
    "ko-KR": "U21"
  },
  u23: {
    "en-GB": "U23",
    "ja-JP": "U23",
    "ru-RU": "U23",
    "zh-CN": "U23",
    "th-TH": "U23",
    "vi-VN": "U23",
    "ko-KR": "U23"
  }
};

const SPORT_TRANSLATIONS = {
  football: {
    "en-GB": "Football",
    "ja-JP": "サッカー",
    "it-IT": "Calcio",
    "es-ES": "Fútbol",
    "ru-RU": "Футбол",
    "fr-FR": "Football",
    "de-DE": "Fußball",
    "tr-TR": "Futbol",
    "zh-CN": "足球",
    "th-TH": "ฟุตบอล",
    "vi-VN": "Bóng đá",
    "ko-KR": "축구",
    "en-SG": "Football",
    "id-ID": "Sepak Bola",
    "nl-NL": "Voetbal"
  },
  tennis: {
    "en-GB": "Tennis",
    "ja-JP": "テニス",
    "it-IT": "Tennis",
    "es-ES": "Tenis",
    "ru-RU": "Теннис",
    "fr-FR": "Tennis",
    "de-DE": "Tennis",
    "tr-TR": "Tenis",
    "zh-CN": "网球",
    "th-TH": "เทนนิส",
    "vi-VN": "Tennis",
    "ko-KR": "테니스",
    "en-SG": "Tennis",
    "id-ID": "Tenis",
    "nl-NL": "Tennis"
  },
  basketball: {
    "en-GB": "Basketball",
    "ja-JP": "バスケット",
    "it-IT": "Basket",
    "es-ES": "Basketball",
    "ru-RU": "Баскетбол",
    "fr-FR": "Basket",
    "de-DE": "Basket",
    "tr-TR": "Basketbol",
    "zh-CN": "篮球",
    "th-TH": "บาสเกตบอล",
    "vi-VN": "Bóng rổ",
    "ko-KR": "농구",
    "en-SG": "Basketball",
    "id-ID": "Basket",
    "nl-NL": "Basketbal"
  },
  esports: {
    "en-GB": "Esports",
    "ja-JP": "eスポーツ",
    "it-IT": "Esports",
    "es-ES": "Esports",
    "ru-RU": "Киберспорт",
    "fr-FR": "Esports",
    "de-DE": "Esports",
    "tr-TR": "Esports",
    "zh-CN": "电竞",
    "th-TH": "อีสปอร์ต",
    "vi-VN": "Esports",
    "ko-KR": "e스포츠",
    "en-SG": "Esports",
    "id-ID": "Esports",
    "nl-NL": "Esports"
  }
};

const SELECTION_TRANSLATIONS = {
  home: {
    "en-GB": "Home",
    "ja-JP": "ホーム",
    "it-IT": "Casa",
    "es-ES": "Local",
    "ru-RU": "Хозяева",
    "fr-FR": "Domicile",
    "de-DE": "Heim",
    "tr-TR": "Ev Sahibi",
    "zh-CN": "主队",
    "th-TH": "เจ้าบ้าน",
    "vi-VN": "Chủ Nhà",
    "ko-KR": "홈",
    "en-SG": "Home",
    "id-ID": "Tuan Rumah",
    "nl-NL": "Thuis"
  },
  away: {
    "en-GB": "Away",
    "ja-JP": "アウェイ",
    "it-IT": "Trasferta",
    "es-ES": "Visitante",
    "ru-RU": "Гости",
    "fr-FR": "Extérieur",
    "de-DE": "Auswärts",
    "tr-TR": "Deplasman",
    "zh-CN": "客队",
    "th-TH": "ทีมเยือน",
    "vi-VN": "Khách",
    "ko-KR": "원정",
    "en-SG": "Away",
    "id-ID": "Tandang",
    "nl-NL": "Uit"
  },
  draw: {
    "en-GB": "Draw",
    "ja-JP": "引き分け",
    "it-IT": "Pareggio",
    "es-ES": "Empate",
    "ru-RU": "Ничья",
    "fr-FR": "Match nul",
    "de-DE": "Unentschieden",
    "tr-TR": "Beraberlik",
    "zh-CN": "平局",
    "th-TH": "เสมอ",
    "vi-VN": "Hoà",
    "ko-KR": "무승부",
    "en-SG": "Draw",
    "id-ID": "Seri",
    "nl-NL": "Gelijkspel"
  },
  over: {
    "en-GB": "Over",
    "ja-JP": "オーバー",
    "it-IT": "Over",
    "es-ES": "Más de",
    "ru-RU": "Больше",
    "fr-FR": "Plus de",
    "de-DE": "Über",
    "tr-TR": "Üst",
    "zh-CN": "大",
    "th-TH": "สูง",
    "vi-VN": "Tài",
    "ko-KR": "오버",
    "en-SG": "Over",
    "id-ID": "Atas",
    "nl-NL": "Over"
  },
  under: {
    "en-GB": "Under",
    "ja-JP": "アンダー",
    "it-IT": "Under",
    "es-ES": "Menos de",
    "ru-RU": "Меньше",
    "fr-FR": "Moins de",
    "de-DE": "Unter",
    "tr-TR": "Alt",
    "zh-CN": "小",
    "th-TH": "ต่ำ",
    "vi-VN": "Xỉu",
    "ko-KR": "언더",
    "en-SG": "Under",
    "id-ID": "Bawah",
    "nl-NL": "Under"
  },
  "home over": {
    "en-GB": "Home — Over",
    "ja-JP": "ホーム — オーバー",
    "it-IT": "Casa — Over",
    "es-ES": "Local — Más de",
    "ru-RU": "Хозяева — Больше",
    "fr-FR": "Domicile — Plus de",
    "de-DE": "Heim — Über",
    "tr-TR": "Ev Sahibi — Üst",
    "zh-CN": "主队 — 大",
    "th-TH": "เจ้าบ้าน — สูง",
    "vi-VN": "Chủ Nhà — Tài",
    "ko-KR": "홈 — 오버",
    "en-SG": "Home — Over",
    "id-ID": "Tuan Rumah — Atas",
    "nl-NL": "Thuis — Over"
  },
  "home under": {
    "en-GB": "Home — Under",
    "ja-JP": "ホーム — アンダー",
    "it-IT": "Casa — Under",
    "es-ES": "Local — Menos de",
    "ru-RU": "Хозяева — Меньше",
    "fr-FR": "Domicile — Moins de",
    "de-DE": "Heim — Unter",
    "tr-TR": "Ev Sahibi — Alt",
    "zh-CN": "主队 — 小",
    "th-TH": "เจ้าบ้าน — ต่ำ",
    "vi-VN": "Chủ Nhà — Xỉu",
    "ko-KR": "홈 — 언더",
    "en-SG": "Home — Under",
    "id-ID": "Tuan Rumah — Bawah",
    "nl-NL": "Thuis — Under"
  },
  "away over": {
    "en-GB": "Away — Over",
    "ja-JP": "アウェイ — オーバー",
    "it-IT": "Trasferta — Over",
    "es-ES": "Visitante — Más de",
    "ru-RU": "Гости — Больше",
    "fr-FR": "Extérieur — Plus de",
    "de-DE": "Auswärts — Über",
    "tr-TR": "Deplasman — Üst",
    "zh-CN": "客队 — 大",
    "th-TH": "ทีมเยือน — สูง",
    "vi-VN": "Khách — Tài",
    "ko-KR": "원정 — 오버",
    "en-SG": "Away — Over",
    "id-ID": "Tandang — Atas",
    "nl-NL": "Uit — Over"
  },
  "away under": {
    "en-GB": "Away — Under",
    "ja-JP": "アウェイ — アンダー",
    "it-IT": "Trasferta — Under",
    "es-ES": "Visitante — Menos de",
    "ru-RU": "Гости — Меньше",
    "fr-FR": "Extérieur — Moins de",
    "de-DE": "Auswärts — Unter",
    "tr-TR": "Deplasman — Alt",
    "zh-CN": "客队 — 小",
    "th-TH": "ทีมเยือน — ต่ำ",
    "vi-VN": "Khách — Xỉu",
    "ko-KR": "원정 — 언더",
    "en-SG": "Away — Under",
    "id-ID": "Tandang — Bawah",
    "nl-NL": "Uit — Under"
  },
   yes: {
    "en-GB": "Yes",
    "ja-JP": "はい",
    "it-IT": "Sì",
    "es-ES": "Sí",
    "ru-RU": "Да",
    "fr-FR": "Oui",
    "de-DE": "Ja",
    "tr-TR": "Evet",
    "zh-CN": "是",
    "th-TH": "ใช่",
    "vi-VN": "Có",
    "ko-KR": "예",
    "en-SG": "Yes",
    "id-ID": "Ya",
    "nl-NL": "Ja"
  },
  no: {
    "en-GB": "No",
    "ja-JP": "いいえ",
    "it-IT": "No",
    "es-ES": "No",
    "ru-RU": "Нет",
    "fr-FR": "Non",
    "de-DE": "Nein",
    "tr-TR": "Hayır",
    "zh-CN": "否",
    "th-TH": "ไม่",
    "vi-VN": "Không",
    "ko-KR": "아니오",
    "en-SG": "No",
    "id-ID": "Tidak",
    "nl-NL": "Nee"
  },
  odd: {
    "en-GB": "Odd",
    "ja-JP": "奇数",
    "it-IT": "Dispari",
    "es-ES": "Impar",
    "ru-RU": "Нечет",
    "fr-FR": "Impair",
    "de-DE": "Ungerade",
    "tr-TR": "Tek",
    "zh-CN": "单",
    "th-TH": "คี่",
    "vi-VN": "Lẻ",
    "ko-KR": "홀",
    "en-SG": "Odd",
    "id-ID": "Ganjil",
    "nl-NL": "Oneven"
  },
  even: {
    "en-GB": "Even",
    "ja-JP": "偶数",
    "it-IT": "Pari",
    "es-ES": "Par",
    "ru-RU": "Чет",
    "fr-FR": "Pair",
    "de-DE": "Gerade",
    "tr-TR": "Çift",
    "zh-CN": "双",
    "th-TH": "คู่",
    "vi-VN": "Chẵn",
    "ko-KR": "짝",
    "en-SG": "Even",
    "id-ID": "Genap",
    "nl-NL": "Even"
  }
};

const MARKET_TRANSLATIONS = {
  "1x2": {
    "en-GB": "1X2",
    "ja-JP": "1X2",
    "it-IT": "1X2",
    "es-ES": "1X2",
    "ru-RU": "1X2",
    "fr-FR": "1X2",
    "de-DE": "1X2",
    "tr-TR": "1X2",
    "zh-CN": "1X2",
    "th-TH": "1X2",
    "vi-VN": "1X2",
    "ko-KR": "1X2",
    "en-SG": "1X2",
    "id-ID": "1X2",
    "nl-NL": "1X2"
  },
  moneyline: {
    "en-GB": "Moneyline",
    "ja-JP": "マネーライン",
    "it-IT": "Moneyline",
    "es-ES": "Moneyline",
    "ru-RU": "Moneyline",
    "fr-FR": "Moneyline",
    "de-DE": "Moneyline",
    "tr-TR": "Moneyline",
    "zh-CN": "独赢",
    "th-TH": "มันนี่ไลน์",
    "vi-VN": "Kèo Thắng",
    "ko-KR": "머니라인",
    "en-SG": "Moneyline",
    "id-ID": "Moneyline",
    "nl-NL": "Moneyline"
  },
  "map moneyline": {
    "en-GB": "Map Winner",
    "ja-JP": "マップ勝者",
    "it-IT": "Vincente mappa",
    "es-ES": "Ganador del mapa",
    "ru-RU": "Победитель карты",
    "fr-FR": "Vainqueur de la carte",
    "de-DE": "Kartensieger",
    "tr-TR": "Harita Kazananı",
    "zh-CN": "地图胜者",
    "th-TH": "ผู้ชนะแผนที่",
    "vi-VN": "Kèo Thắng Bản Đồ",
    "ko-KR": "맵 승자",
    "en-SG": "Map Winner",
    "id-ID": "Pemenang Map",
    "nl-NL": "Mapwinnaar"
  },
  "map winner": {
    "en-GB": "Map Winner",
    "ja-JP": "マップ勝者",
    "it-IT": "Vincente mappa",
    "es-ES": "Ganador del mapa",
    "ru-RU": "Победитель карты",
    "fr-FR": "Vainqueur de la carte",
    "de-DE": "Kartensieger",
    "tr-TR": "Harita Kazananı",
    "zh-CN": "地图胜者",
    "th-TH": "ผู้ชนะแผนที่",
    "vi-VN": "Kèo Thắng Bản Đồ",
    "ko-KR": "맵 승자",
    "en-SG": "Map Winner",
    "id-ID": "Pemenang Map",
    "nl-NL": "Mapwinnaar"
  },
  "moneyline 3 way": {
    "en-GB": "Moneyline 3-Way",
    "ja-JP": "3ウェイ・マネーライン",
    "it-IT": "Moneyline 3 esiti",
    "es-ES": "Moneyline 3 vías",
    "ru-RU": "Манилин 3 исхода",
    "fr-FR": "Moneyline 3 issues",
    "de-DE": "Moneyline 3-Weg",
    "tr-TR": "Moneyline 3 Yol",
    "zh-CN": "独赢三项",
    "th-TH": "มันนี่ไลน์ 3 ทาง",
    "vi-VN": "Kèo Thắng 3 Cửa",
    "ko-KR": "3웨이 머니라인",
    "en-SG": "Moneyline 3-Way",
    "id-ID": "Moneyline 3 Arah",
    "nl-NL": "Moneyline 3-weg"
  },
  "match winner": {
    "en-GB": "Match Winner",
    "ja-JP": "勝者",
    "it-IT": "Vincente Match",
    "es-ES": "Ganador del Partido",
    "ru-RU": "Победитель Матча",
    "fr-FR": "Vainqueur du Match",
    "de-DE": "Matchsieger",
    "tr-TR": "Maç Kazananı",
    "zh-CN": "比赛胜者",
    "th-TH": "ผู้ชนะการแข่งขัน",
    "vi-VN": "Kèo Thắng",
    "ko-KR": "경기 승자",
    "en-SG": "Match Winner",
    "id-ID": "Pemenang Pertandingan",
    "nl-NL": "Wedstrijdwinnaar"
  },
  winner: {
    "en-GB": "Winner",
    "ja-JP": "勝者",
    "it-IT": "Vincente",
    "es-ES": "Ganador",
    "ru-RU": "Победитель",
    "fr-FR": "Vainqueur",
    "de-DE": "Sieger",
    "tr-TR": "Kazanan",
    "zh-CN": "胜者",
    "th-TH": "ผู้ชนะ",
    "vi-VN": "Kèo Thắng",
    "ko-KR": "승자",
    "en-SG": "Winner",
    "id-ID": "Pemenang",
    "nl-NL": "Winnaar"
  },
  handicap: {
    "en-GB": "Handicap",
    "ja-JP": "ハンディキャップ",
    "it-IT": "Handicap",
    "es-ES": "Hándicap",
    "ru-RU": "Фора",
    "fr-FR": "Handicap",
    "de-DE": "Handicap",
    "tr-TR": "Handikap",
    "zh-CN": "让球",
    "th-TH": "แฮนดิแคป",
    "vi-VN": "Kèo Chấp",
    "ko-KR": "핸디캡",
    "en-SG": "Handicap",
    "id-ID": "Handicap",
    "nl-NL": "Handicap"
  },
  "asian handicap": {
    "en-GB": "Asian Handicap",
    "ja-JP": "アジアンハンディキャップ",
    "it-IT": "Asian Handicap",
    "es-ES": "Hándicap Asiático",
    "ru-RU": "Азиатская Фора",
    "fr-FR": "Handicap Asiatique",
    "de-DE": "Asian Handicap",
    "tr-TR": "Asya Handikap",
    "zh-CN": "亚洲让球",
    "th-TH": "เอเชียนแฮนดิแคป",
    "vi-VN": "Chấp Châu Á",
    "ko-KR": "아시안 핸디캡",
    "en-SG": "Asian Handicap",
    "id-ID": "Asian Handicap",
    "nl-NL": "Asian Handicap"
  },
  overunder: {
    "en-GB": "Over/Under",
    "ja-JP": "オーバー/アンダー",
    "it-IT": "Over/Under",
    "es-ES": "Más/Menos",
    "ru-RU": "Тотал Больше/Меньше",
    "fr-FR": "Plus/Moins",
    "de-DE": "Über/Unter",
    "tr-TR": "Alt/Üst",
    "zh-CN": "大小球",
    "th-TH": "สูง/ต่ำ",
    "vi-VN": "Tài/Xỉu Tổng",
    "ko-KR": "오버/언더",
    "en-SG": "Over/Under",
    "id-ID": "Atas/Bawah",
    "nl-NL": "Over/Under"
  },
  "over/under": {
    "en-GB": "Over/Under",
    "ja-JP": "オーバー/アンダー",
    "it-IT": "Over/Under",
    "es-ES": "Más/Menos",
    "ru-RU": "Тотал Больше/Меньше",
    "fr-FR": "Plus/Moins",
    "de-DE": "Über/Unter",
    "tr-TR": "Alt/Üst",
    "zh-CN": "大小球",
    "th-TH": "สูง/ต่ำ",
    "vi-VN": "Tài/Xỉu Tổng",
    "ko-KR": "오버/언더",
    "en-SG": "Over/Under",
    "id-ID": "Atas/Bawah",
    "nl-NL": "Over/Under"
  },
  totals: {
    "en-GB": "Totals",
    "ja-JP": "トータル",
    "it-IT": "Totali",
    "es-ES": "Totales",
    "ru-RU": "Тоталы",
    "fr-FR": "Totaux",
    "de-DE": "Totals",
    "tr-TR": "Toplamlar",
    "zh-CN": "总分",
    "th-TH": "สกอร์รวม",
    "vi-VN": "Tài/Xỉu Tổng",
    "ko-KR": "총점",
    "en-SG": "Totals",
    "id-ID": "Total",
    "nl-NL": "Totalen"
  },
  "total points": {
    "en-GB": "Total Points",
    "ja-JP": "総得点",
    "it-IT": "Punti totali",
    "es-ES": "Total de puntos",
    "ru-RU": "Тотал очков",
    "fr-FR": "Total points",
    "de-DE": "Punkte gesamt",
    "tr-TR": "Toplam Sayı",
    "zh-CN": "总得分",
    "th-TH": "คะแนนรวม",
    "vi-VN": "Tài/Xỉu Tổng",
    "ko-KR": "총 득점",
    "en-SG": "Total Points",
    "id-ID": "Total Poin",
    "nl-NL": "Totaal punten"
  },
  btts: {
    "en-GB": "BTTS",
    "ja-JP": "両チーム得点",
    "it-IT": "Entrambe Segnano",
    "es-ES": "Ambos Marcan",
    "ru-RU": "Обе Забьют",
    "fr-FR": "Les Deux Équipes Marquent",
    "de-DE": "Beide Teams Treffen",
    "tr-TR": "Karşılıklı Gol Var",
    "zh-CN": "双方进球",
    "th-TH": "ทั้งสองทีมทำประตู",
    "vi-VN": "Cả Hai Đội Ghi Bàn",
    "ko-KR": "양팀 득점",
    "en-SG": "BTTS",
    "id-ID": "Kedua Tim Mencetak Gol",
    "nl-NL": "Beide Teams Scoren"
  },
  "draw no bet": {
    "en-GB": "Draw No Bet",
    "ja-JP": "ドローなし",
    "it-IT": "Pareggio rimborsato",
    "es-ES": "Empate anula apuesta",
    "ru-RU": "Победа без ничьей",
    "fr-FR": "Match nul remboursé",
    "de-DE": "Unentschieden gewinnt nicht",
    "tr-TR": "Beraberlikte İade",
    "zh-CN": "不让球胜平负（走水退款）",
    "th-TH": "เสมอคืนเงิน",
    "vi-VN": "Hoà Trả Tiền",
    "ko-KR": "무승부 제외 승패",
    "en-SG": "Draw No Bet",
    "id-ID": "Tanpa Seri",
    "nl-NL": "Gelijkspel = inzet terug"
  },
  "draw no bet first half": {
    "en-GB": "Draw No Bet — First Half",
    "ja-JP": "ドローなし（前半）",
    "it-IT": "DNB — Primo Tempo",
    "es-ES": "Empate anula — 1ª parte",
    "ru-RU": "Победа без ничьей — 1-й тайм",
    "fr-FR": "Match nul remboursé — 1re mi-temps",
    "de-DE": "Unentschieden gewinnt nicht — 1. Halbzeit",
    "tr-TR": "Beraberlikte İade — İlk Yarı",
    "zh-CN": "不让球（上半场）",
    "th-TH": "เสมอคืนเงิน — ครึ่งแรก",
    "vi-VN": "Hòa Hoàn Tiền — Hiệp 1",
    "ko-KR": "무승부 제외 — 전반",
    "en-SG": "Draw No Bet — First Half",
    "id-ID": "Tanpa Seri — Babak 1",
    "nl-NL": "Gelijkspel = terug — 1e helft"
  },
  "draw no bet second half": {
    "en-GB": "Draw No Bet — Second Half",
    "ja-JP": "ドローなし（後半）",
    "it-IT": "DNB — Secondo Tempo",
    "es-ES": "Empate anula — 2ª parte",
    "ru-RU": "Победа без ничьей — 2-й тайм",
    "fr-FR": "Match nul remboursé — 2e mi-temps",
    "de-DE": "Unentschieden gewinnt nicht — 2. Halbzeit",
    "tr-TR": "Beraberlikte İade — İkinci Yarı",
    "zh-CN": "不让球（下半场）",
    "th-TH": "เสมอคืนเงิน — ครึ่งหลัง",
    "vi-VN": "Hòa Hoàn Tiền — Hiệp 2",
    "ko-KR": "무승부 제외 — 후반",
    "en-SG": "Draw No Bet — Second Half",
    "id-ID": "Tanpa Seri — Babak 2",
    "nl-NL": "Gelijkspel = terug — 2e helft"
  },
  "double chance": {
    "en-GB": "Double Chance",
    "ja-JP": "ダブルチャンス",
    "it-IT": "Doppia Chance",
    "es-ES": "Doble Oportunidad",
    "ru-RU": "Двойной шанс",
    "fr-FR": "Double Chance",
    "de-DE": "Doppelte Chance",
    "tr-TR": "Çifte Şans",
    "zh-CN": "双重机会",
    "th-TH": "โอกาสคู่",
    "vi-VN": "Cơ Hội Kép",
    "ko-KR": "더블찬스",
    "en-SG": "Double Chance",
    "id-ID": "Peluang Ganda",
    "nl-NL": "Dubbele kans"
  },
  "first half winner": {
    "en-GB": "First Half Winner",
    "ja-JP": "前半勝者",
    "it-IT": "Vincente Primo Tempo",
    "es-ES": "Ganador 1ª parte",
    "ru-RU": "Победитель 1-го тайма",
    "fr-FR": "Vainqueur 1re mi-temps",
    "de-DE": "Sieger 1. Halbzeit",
    "tr-TR": "İlk Yarı Kazananı",
    "zh-CN": "半场胜负",
    "th-TH": "ผู้ชนะครึ่งแรก",
    "vi-VN": "Thắng Hiệp 1",
    "ko-KR": "전반 승자",
    "en-SG": "First Half Winner",
    "id-ID": "Pemenang Babak 1",
    "nl-NL": "Winnaar 1e helft"
  },
  "second half winner": {
    "en-GB": "Second Half Winner",
    "ja-JP": "後半勝者",
    "it-IT": "Vincente Secondo Tempo",
    "es-ES": "Ganador 2ª parte",
    "ru-RU": "Победитель 2-го тайма",
    "fr-FR": "Vainqueur 2e mi-temps",
    "de-DE": "Sieger 2. Halbzeit",
    "tr-TR": "İkinci Yarı Kazananı",
    "zh-CN": "下半场胜负",
    "th-TH": "ผู้ชนะครึ่งหลัง",
    "vi-VN": "Thắng Hiệp 2",
    "ko-KR": "후반 승자",
    "en-SG": "Second Half Winner",
    "id-ID": "Pemenang Babak 2",
    "nl-NL": "Winnaar 2e helft"
  },
  "first 10 min winner": {
    "en-GB": "First 10 Minutes — Winner",
    "ja-JP": "開始10分の勝者",
    "it-IT": "Vincente Primi 10 Minuti",
    "es-ES": "Ganador primeros 10 minutos",
    "ru-RU": "Победитель первых 10 минут",
    "fr-FR": "Vainqueur 10 premières minutes",
    "de-DE": "Sieger erste 10 Minuten",
    "tr-TR": "İlk 10 Dakika Kazananı",
    "zh-CN": "前10分钟胜者",
    "th-TH": "ผู้ชนะ 10 นาทีแรก",
    "vi-VN": "Thắng 10 phút đầu",
    "ko-KR": "전반 10분 승자",
    "en-SG": "First 10 Minutes — Winner",
    "id-ID": "Pemenang 10 Menit Pertama",
    "nl-NL": "Winnaar eerste 10 minuten"
  },
  "first 15 min winner": {
    "en-GB": "First 15 Minutes — Winner",
    "ja-JP": "開始15分の勝者",
    "it-IT": "Vincente Primi 15 Minuti",
    "es-ES": "Ganador primeros 15 minutos",
    "ru-RU": "Победитель первых 15 минут",
    "fr-FR": "Vainqueur 15 premières minutes",
    "de-DE": "Sieger erste 15 Minuten",
    "tr-TR": "İlk 15 Dakika Kazananı",
    "zh-CN": "前15分钟胜者",
    "th-TH": "ผู้ชนะ 15 นาทีแรก",
    "vi-VN": "Thắng 15 phút đầu",
    "ko-KR": "전반 15분 승자",
    "en-SG": "First 15 Minutes — Winner",
    "id-ID": "Pemenang 15 Menit Pertama",
    "nl-NL": "Winnaar eerste 15 minuten"
  },
  "first 5 min winner": {
    "en-GB": "First 5 Minutes — Winner",
    "ja-JP": "開始5分の勝者",
    "it-IT": "Vincente Primi 5 Minuti",
    "es-ES": "Ganador primeros 5 minutos",
    "ru-RU": "Победитель первых 5 минут",
    "fr-FR": "Vainqueur 5 premières minutes",
    "de-DE": "Sieger erste 5 Minuten",
    "tr-TR": "İlk 5 Dakika Kazananı",
    "zh-CN": "前5分钟胜者",
    "th-TH": "ผู้ชนะ 5 นาทีแรก",
    "vi-VN": "Thắng 5 phút đầu",
    "ko-KR": "전반 5분 승자",
    "en-SG": "First 5 Minutes — Winner",
    "id-ID": "Pemenang 5 Menit Pertama",
    "nl-NL": "Winnaar eerste 5 minuten"
  },
  "1x2 30 minutes": {
    "en-GB": "1X2 — 30 Minutes",
    "ja-JP": "1X2（30分）",
    "it-IT": "1X2 — 30 Minuti",
    "es-ES": "1X2 — 30 minutos",
    "ru-RU": "1X2 — 30 минут",
    "fr-FR": "1X2 — 30 minutes",
    "de-DE": "1X2 — 30 Minuten",
    "tr-TR": "1X2 — 30 Dakika",
    "zh-CN": "1X2（30分钟）",
    "th-TH": "1X2 — 30 นาที",
    "vi-VN": "1X2 — 30 phút",
    "ko-KR": "1X2 — 30분",
    "en-SG": "1X2 — 30 Minutes",
    "id-ID": "1X2 — 30 Menit",
    "nl-NL": "1X2 — 30 minuten"
  },
  "1x2 60 minutes": {
    "en-GB": "1X2 — 60 Minutes",
    "ja-JP": "1X2（60分）",
    "it-IT": "1X2 — 60 Minuti",
    "es-ES": "1X2 — 60 minutos",
    "ru-RU": "1X2 — 60 минут",
    "fr-FR": "1X2 — 60 minutes",
    "de-DE": "1X2 — 60 Minuten",
    "tr-TR": "1X2 — 60 Dakika",
    "zh-CN": "1X2（60分钟）",
    "th-TH": "1X2 — 60 นาที",
    "vi-VN": "1X2 — 60 phút",
    "ko-KR": "1X2 — 60분",
    "en-SG": "1X2 — 60 Minutes",
    "id-ID": "1X2 — 60 Menit",
    "nl-NL": "1X2 — 60 minuten"
  },
  "to win either half": {
    "en-GB": "To Win Either Half",
    "ja-JP": "いずれかのハーフで勝利",
    "it-IT": "Vincere un Tempo",
    "es-ES": "Ganar cualquier parte",
    "ru-RU": "Победа в одном из таймов",
    "fr-FR": "Gagner une mi-temps",
    "de-DE": "Eine Halbzeit gewinnen",
    "tr-TR": "Herhangi Bir Yarıyı Kazanma",
    "zh-CN": "任一半场获胜",
    "th-TH": "ชนะครึ่งใดครึ่งหนึ่ง",
    "vi-VN": "Thắng Một Trong Hai Hiệp",
    "ko-KR": "전·후반 중 한 시점 승리",
    "en-SG": "To Win Either Half",
    "id-ID": "Menang di Salah Satu Babak",
    "nl-NL": "Eén helft winnen"
  },
  "win both halves": {
    "en-GB": "Win Both Halves",
    "ja-JP": "両ハーフ制覇",
    "it-IT": "Vincere Entrambi i Tempi",
    "es-ES": "Ganar ambas partes",
    "ru-RU": "Победа в обоих таймах",
    "fr-FR": "Gagner les deux mi-temps",
    "de-DE": "Beide Halbzeiten gewinnen",
    "tr-TR": "Her İki Yarıyı Kazanma",
    "zh-CN": "双半场全胜",
    "th-TH": "ชนะทั้งสองครึ่ง",
    "vi-VN": "Thắng Cả Hai Hiệp",
    "ko-KR": "양쪽 하프 모두 승리",
    "en-SG": "Win Both Halves",
    "id-ID": "Menang Kedua Babak",
    "nl-NL": "Beide helften winnen"
  },
  "home win both halves": {
    "en-GB": "Home — Win Both Halves",
    "ja-JP": "ホームが両ハーフ制覇",
    "it-IT": "Casa — Vincere Entrambi i Tempi",
    "es-ES": "Local — Ganar ambas partes",
    "ru-RU": "Хозяева — победа в обоих таймах",
    "fr-FR": "Domicile — Gagner les deux mi-temps",
    "de-DE": "Heim — Beide Halbzeiten gewinnen",
    "tr-TR": "Ev — Her İki Yarıyı Kazanma",
    "zh-CN": "主队双半场全胜",
    "th-TH": "เจ้าบ้าน — ชนะทั้งสองครึ่ง",
    "vi-VN": "Chủ Nhà — Thắng Cả Hai Hiệp",
    "ko-KR": "홈 — 양 하프 승리",
    "en-SG": "Home — Win Both Halves",
    "id-ID": "Kandang — Menang Kedua Babak",
    "nl-NL": "Thuis — beide helften winnen"
  },
  "away win both halves": {
    "en-GB": "Away — Win Both Halves",
    "ja-JP": "アウェイが両ハーフ制覇",
    "it-IT": "Trasferta — Vincere Entrambi i Tempi",
    "es-ES": "Visitante — Ganar ambas partes",
    "ru-RU": "Гости — победа в обоих таймах",
    "fr-FR": "Extérieur — Gagner les deux mi-temps",
    "de-DE": "Auswärts — Beide Halbzeiten gewinnen",
    "tr-TR": "Deplasman — Her İki Yarıyı Kazanma",
    "zh-CN": "客队双半场全胜",
    "th-TH": "ทีมเยือน — ชนะทั้งสองครึ่ง",
    "vi-VN": "Đội Khách — Thắng Cả Hai Hiệp",
    "ko-KR": "원정 — 양 하프 승리",
    "en-SG": "Away — Win Both Halves",
    "id-ID": "Tandang — Menang Kedua Babak",
    "nl-NL": "Uit — beide helften winnen"
  },
  "team to score first": {
    "en-GB": "Team To Score First",
    "ja-JP": "先制得点チーム",
    "it-IT": "Squadra che Segna per Prima",
    "es-ES": "Equipo que marca primero",
    "ru-RU": "Кто забьёт первым",
    "fr-FR": "Équipe qui ouvre le score",
    "de-DE": "Team erzielt zuerst ein Tor",
    "tr-TR": "İlk Golü Atan Takım",
    "zh-CN": "先进球队伍",
    "th-TH": "ทีมที่ยิงก่อน",
    "vi-VN": "Đội ghi bàn trước",
    "ko-KR": "먼저 득점하는 팀",
    "en-SG": "Team To Score First",
    "id-ID": "Tim Mencetak Gol Pertama",
    "nl-NL": "Team scoort als eerste"
  },
  "team to score last": {
    "en-GB": "Team To Score Last",
    "ja-JP": "最終得点チーム",
    "it-IT": "Squadra che Segna per Ultima",
    "es-ES": "Equipo que marca último",
    "ru-RU": "Кто забьёт последним",
    "fr-FR": "Équipe qui marque en dernier",
    "de-DE": "Team erzielt zuletzt ein Tor",
    "tr-TR": "Son Golü Atan Takım",
    "zh-CN": "最后进球球队",
    "th-TH": "ทีมที่ยิงล่าสุด",
    "vi-VN": "Đội ghi bàn cuối",
    "ko-KR": "마지막 득점 팀",
    "en-SG": "Team To Score Last",
    "id-ID": "Tim Mencetak Gol Terakhir",
    "nl-NL": "Team scoort als laatste"
  },
  "first team to score": {
    "en-GB": "First Team To Score",
    "ja-JP": "先制点（3択）",
    "it-IT": "Prima Squadra a Segnare",
    "es-ES": "Primer equipo en marcar",
    "ru-RU": "Первая команда, забившая гол",
    "fr-FR": "Première équipe à marquer",
    "de-DE": "Erstes Team mit Tor",
    "tr-TR": "İlk Golü Atan Takım (3 Yol)",
    "zh-CN": "先进球队伍（三项）",
    "th-TH": "ทีมที่ยิงก่อน (3 ทาง)",
    "vi-VN": "Đội ghi bàn đầu tiên (3 cửa)",
    "ko-KR": "먼저 득점 팀 (3웨이)",
    "en-SG": "First Team To Score",
    "id-ID": "Tim Pertama Mencetak Gol",
    "nl-NL": "Eerste team dat scoort"
  },
  "first team to score 3 way first half": {
    "en-GB": "First Team To Score (3-Way) — First Half",
    "ja-JP": "先制点3択（前半）",
    "it-IT": "Prima Squadra a Segnare (3) — 1° Tempo",
    "es-ES": "Primer equipo en marcar (3) — 1ª parte",
    "ru-RU": "Кто забьёт первым (3 исхода) — 1-й тайм",
    "fr-FR": "1re équipe à marquer (3) — 1re mi-temps",
    "de-DE": "Erstes Team mit Tor (3-Weg) — 1. HZ",
    "tr-TR": "İlk Gol (3 Yol) — İlk Yarı",
    "zh-CN": "先进球队伍（三项）— 上半场",
    "th-TH": "ทีมยิงก่อน (3 ทาง) — ครึ่งแรก",
    "vi-VN": "Đội Ghi Trước (3 Cửa) — Hiệp 1",
    "ko-KR": "선제골 팀 (3웨이) — 전반",
    "en-SG": "First Team To Score (3-Way) — First Half",
    "id-ID": "Tim Pertama Gol (3 Arah) — Babak 1",
    "nl-NL": "Eerste team dat scoort (3-way) — 1e helft"
  },
  "home team score a goal": {
    "en-GB": "Home Team To Score",
    "ja-JP": "ホームが得点",
    "it-IT": "Casa Segna",
    "es-ES": "Local marca gol",
    "ru-RU": "Хозяева забьют",
    "fr-FR": "Domicile marque",
    "de-DE": "Heimteam trifft",
    "tr-TR": "Ev Sahibi Gol Atar",
    "zh-CN": "主队进球",
    "th-TH": "เจ้าบ้านยิงได้",
    "vi-VN": "Chủ nhà ghi bàn",
    "ko-KR": "홈팀 득점",
    "en-SG": "Home Team To Score",
    "id-ID": "Tim Kandang Mencetak Gol",
    "nl-NL": "Thuisploeg scoort"
  },
  "away team score a goal": {
    "en-GB": "Away Team To Score",
    "ja-JP": "アウェイが得点",
    "it-IT": "Trasferta Segna",
    "es-ES": "Visitante marca gol",
    "ru-RU": "Гости забьют",
    "fr-FR": "Extérieur marque",
    "de-DE": "Auswärtsteam trifft",
    "tr-TR": "Deplasman Gol Atar",
    "zh-CN": "客队进球",
    "th-TH": "ทีมเยือนยิงได้",
    "vi-VN": "Đội khách ghi bàn",
    "ko-KR": "원정팀 득점",
    "en-SG": "Away Team To Score",
    "id-ID": "Tim Tandang Mencetak Gol",
    "nl-NL": "Uitploeg scoort"
  },
  "home team will score in both halves": {
    "en-GB": "Home — To Score In Both Halves",
    "ja-JP": "ホームが両ハーフで得点",
    "it-IT": "Casa Segna in Entrambi i Tempi",
    "es-ES": "Local marca en ambas partes",
    "ru-RU": "Хозяева забьют в обоих таймах",
    "fr-FR": "Domicile marque dans les deux mi-temps",
    "de-DE": "Heim trifft in beiden Halbzeiten",
    "tr-TR": "Ev — Her İki Yarıda Gol",
    "zh-CN": "主队上下半场都有进球",
    "th-TH": "เจ้าบ้านยิงทั้งสองครึ่ง",
    "vi-VN": "Chủ Nhà Ghi Bàn Cả Hai Hiệp",
    "ko-KR": "홈 — 양쪽 하프 득점",
    "en-SG": "Home — To Score In Both Halves",
    "id-ID": "Kandang Gol di Kedua Babak",
    "nl-NL": "Thuis scoort in beide helften"
  },
  "away team will score in both halves": {
    "en-GB": "Away — To Score In Both Halves",
    "ja-JP": "アウェイが両ハーフで得点",
    "it-IT": "Trasferta Segna in Entrambi i Tempi",
    "es-ES": "Visitante marca en ambas partes",
    "ru-RU": "Гости забьют в обоих таймах",
    "fr-FR": "Extérieur marque dans les deux mi-temps",
    "de-DE": "Auswärts trifft in beiden Halbzeiten",
    "tr-TR": "Deplasman — Her İki Yarıda Gol",
    "zh-CN": "客队上下半场都有进球",
    "th-TH": "ทีมเยือนยิงทั้งสองครึ่ง",
    "vi-VN": "Đội Khách Ghi Bàn Cả Hai Hiệp",
    "ko-KR": "원정 — 양쪽 하프 득점",
    "en-SG": "Away — To Score In Both Halves",
    "id-ID": "Tandang Gol di Kedua Babak",
    "nl-NL": "Uit scoort in beide helften"
  },
  "to score in both halves": {
    "en-GB": "To Score In Both Halves",
    "ja-JP": "両ハーフで得点",
    "it-IT": "Segnare in Entrambi i Tempi",
    "es-ES": "Marcar en ambas partes",
    "ru-RU": "Забить в обоих таймах",
    "fr-FR": "Marquer dans les deux mi-temps",
    "de-DE": "In beiden Halbzeiten treffen",
    "tr-TR": "Her İki Yarıda Gol",
    "zh-CN": "双半场进球",
    "th-TH": "ยิงทั้งสองครึ่ง",
    "vi-VN": "Ghi Bàn Cả Hai Hiệp",
    "ko-KR": "양 하프 득점",
    "en-SG": "To Score In Both Halves",
    "id-ID": "Mencetak di Kedua Babak",
    "nl-NL": "Scoort in beide helften"
  },
  "both teams to score first half": {
    "en-GB": "Both Teams To Score — First Half",
    "ja-JP": "両チーム得点（前半）",
    "it-IT": "Entrambe Segnano — 1° Tempo",
    "es-ES": "Ambos marcan — 1ª parte",
    "ru-RU": "Обе забьют — 1-й тайм",
    "fr-FR": "Les deux marquent — 1re mi-temps",
    "de-DE": "Beide treffen — 1. HZ",
    "tr-TR": "KG Var — İlk Yarı",
    "zh-CN": "双方进球（上半场）",
    "th-TH": "คู่คี่ทั้งคู่ — ครึ่งแรก",
    "vi-VN": "Cả Hai Đội Ghi Bàn — Hiệp 1",
    "ko-KR": "양팀 득점 — 전반",
    "en-SG": "Both Teams To Score — First Half",
    "id-ID": "Kedua Tim Gol — Babak 1",
    "nl-NL": "Beide teams scoren — 1e helft"
  },
  "total goals/both teams to score": {
    "en-GB": "Total Goals / Both Teams To Score",
    "ja-JP": "総得点 / 両チーム得点",
    "it-IT": "Totale Gol / Entrambe Segnano",
    "es-ES": "Total goles / Ambos marcan",
    "ru-RU": "Тотал голов / Обе забьют",
    "fr-FR": "Total buts / Les deux marquent",
    "de-DE": "Tore gesamt / Beide treffen",
    "tr-TR": "Toplam Gol / KG Var",
    "zh-CN": "总进球 / 双方进球",
    "th-TH": "สกอร์รวม / ทั้งคู่ยิง",
    "vi-VN": "Tổng bàn / cả hai đội ghi bàn",
    "ko-KR": "총골 / 양팀 득점",
    "en-SG": "Total Goals / Both Teams To Score",
    "id-ID": "Total Gol / Kedua Tim Gol",
    "nl-NL": "Totaal doelpunten / beide teams scoren"
  },
  "win to nil": {
    "en-GB": "Win To Nil",
    "ja-JP": "無失点勝利",
    "it-IT": "Vittoria a Rete Inviolata",
    "es-ES": "Victoria a cero",
    "ru-RU": "Победа на ноль",
    "fr-FR": "Victoire sans encaisser",
    "de-DE": "Sieg zu Null",
    "tr-TR": "Sıfıra Kazanma",
    "zh-CN": "零封胜",
    "th-TH": "ชนะคลีนชีต",
    "vi-VN": "Thắng trắng",
    "ko-KR": "클린시트 승리",
    "en-SG": "Win To Nil",
    "id-ID": "Menang Tanpa Kebobolan",
    "nl-NL": "Winst zonder tegendoelpunt"
  },
  "win to nil home": {
    "en-GB": "Win To Nil — Home",
    "ja-JP": "ホーム無失点勝利",
    "it-IT": "Vittoria a Rete Inviolata — Casa",
    "es-ES": "Victoria a cero — Local",
    "ru-RU": "Победа хозяев на ноль",
    "fr-FR": "Victoire sans encaisser — Domicile",
    "de-DE": "Heimsieg zu Null",
    "tr-TR": "Ev Sahibi Sıfıra Kazanır",
    "zh-CN": "主队零封胜",
    "th-TH": "เจ้าบ้านชนะคลีนชีต",
    "vi-VN": "Chủ Nhà Thắng Trắng",
    "ko-KR": "홈 클린시트 승",
    "en-SG": "Win To Nil — Home",
    "id-ID": "Menang Tanpa Kebobolan — Kandang",
    "nl-NL": "Thuis wint zonder tegendoelpunt"
  },
  "win to nil away": {
    "en-GB": "Win To Nil — Away",
    "ja-JP": "アウェイ無失点勝利",
    "it-IT": "Vittoria a Rete Inviolata — Trasferta",
    "es-ES": "Victoria a cero — Visitante",
    "ru-RU": "Победа гостей на ноль",
    "fr-FR": "Victoire sans encaisser — Extérieur",
    "de-DE": "Auswärtssieg zu Null",
    "tr-TR": "Deplasman Sıfıra Kazanır",
    "zh-CN": "客队零封胜",
    "th-TH": "ทีมเยือนชนะคลีนชีต",
    "vi-VN": "Đội Khách Thắng Trắng",
    "ko-KR": "원정 클린시트 승",
    "en-SG": "Win To Nil — Away",
    "id-ID": "Menang Tanpa Kebobolan — Tandang",
    "nl-NL": "Uit wint zonder tegendoelpunt"
  },
  "team total": {
    "en-GB": "Single Team Total",
    "ja-JP": "単独チームトータル",
    "it-IT": "Totale squadra singola",
    "es-ES": "Total de equipo único",
    "ru-RU": "Индивидуальный тотал команды",
    "fr-FR": "Total équipe unique",
    "de-DE": "Einzelteam-Gesamt",
    "tr-TR": "Tek Takım Toplamı",
    "zh-CN": "单队总分",
    "th-TH": "ทีมเดี่ยวสกอร์รวม",
    "vi-VN": "Tài/Xỉu Đội",
    "ko-KR": "단일 팀 총점",
    "en-SG": "Single Team Total",
    "id-ID": "Total Tim Tunggal",
    "nl-NL": "Enkel team totaal"
  },
  "1st half": {
    "en-GB": "1st Half", "ja-JP": "前半", "it-IT": "Primo Tempo",
    "es-ES": "Primera Parte", "ru-RU": "1-й тайм", "fr-FR": "1re mi-temps",
    "de-DE": "1. Halbzeit", "tr-TR": "İlk Yarı", "zh-CN": "上半场",
    "th-TH": "ครึ่งแรก", "vi-VN": "Hiệp 1", "ko-KR": "전반",
    "en-SG": "1st Half", "id-ID": "Babak Pertama", "nl-NL": "1e Helft"
  },
  "2nd half": {
    "en-GB": "2nd Half", "ja-JP": "後半", "it-IT": "Secondo Tempo",
    "es-ES": "Segunda Parte", "ru-RU": "2-й тайм", "fr-FR": "2e mi-temps",
    "de-DE": "2. Halbzeit", "tr-TR": "İkinci Yarı", "zh-CN": "下半场",
    "th-TH": "ครึ่งหลัง", "vi-VN": "Hiệp 2", "ko-KR": "후반",
    "en-SG": "2nd Half", "id-ID": "Babak Kedua", "nl-NL": "2e Helft"
  },
  "1st quarter": {
    "en-GB": "1st Quarter", "ja-JP": "第1Q", "it-IT": "1° Quarto",
    "es-ES": "1er Cuarto", "ru-RU": "1-я четверть", "fr-FR": "1er quart-temps",
    "de-DE": "1. Viertel", "tr-TR": "1. Çeyrek", "zh-CN": "第1节",
    "th-TH": "ควอเตอร์ 1", "vi-VN": "Quý 1", "ko-KR": "1쿼터",
    "en-SG": "1st Quarter", "id-ID": "Kuarter 1", "nl-NL": "1e Kwart"
  },
  "2nd quarter": {
    "en-GB": "2nd Quarter", "ja-JP": "第2Q", "it-IT": "2° Quarto",
    "es-ES": "2° Cuarto", "ru-RU": "2-я четверть", "fr-FR": "2e quart-temps",
    "de-DE": "2. Viertel", "tr-TR": "2. Çeyrek", "zh-CN": "第2节",
    "th-TH": "ควอเตอร์ 2", "vi-VN": "Quý 2", "ko-KR": "2쿼터",
    "en-SG": "2nd Quarter", "id-ID": "Kuarter 2", "nl-NL": "2e Kwart"
  },
  "player prop": {
    "en-GB": "Player Prop", "ja-JP": "選手プロップ", "it-IT": "Prop Giocatore",
    "es-ES": "Prop Jugador", "ru-RU": "Проп игрока", "fr-FR": "Prop Joueur",
    "de-DE": "Spieler-Prop", "tr-TR": "Oyuncu Prop", "zh-CN": "球员特殊盘",
    "th-TH": "พร็อพผู้เล่น", "vi-VN": "Kèo Cầu Thủ", "ko-KR": "플레이어 프롭",
    "en-SG": "Player Prop", "id-ID": "Prop Pemain", "nl-NL": "Speler Prop"
  },
  "game prop": {
    "en-GB": "Game Prop", "ja-JP": "ゲームプロップ", "it-IT": "Prop Partita",
    "es-ES": "Prop Partido", "ru-RU": "Проп матча", "fr-FR": "Prop Match",
    "de-DE": "Spiel-Prop", "tr-TR": "Maç Prop", "zh-CN": "比赛特殊盘",
    "th-TH": "เกมพร็อพ", "vi-VN": "Kèo Trận Đấu", "ko-KR": "게임 프롭",
    "en-SG": "Game Prop", "id-ID": "Prop Pertandingan", "nl-NL": "Spel Prop"
  },
  "3rd quarter": {
    "en-GB": "3rd Quarter", "ja-JP": "第3Q", "it-IT": "3° Quarto",
    "es-ES": "3er Cuarto", "ru-RU": "3-я четверть", "fr-FR": "3e quart-temps",
    "de-DE": "3. Viertel", "tr-TR": "3. Çeyrek", "zh-CN": "第3节",
    "th-TH": "ควอเตอร์ 3", "vi-VN": "Quý 3", "ko-KR": "3쿼터",
    "en-SG": "3rd Quarter", "id-ID": "Kuarter 3", "nl-NL": "3e Kwart"
  },
  "4th quarter": {
    "en-GB": "4th Quarter", "ja-JP": "第4Q", "it-IT": "4° Quarto",
    "es-ES": "4° Cuarto", "ru-RU": "4-я четверть", "fr-FR": "4e quart-temps",
    "de-DE": "4. Viertel", "tr-TR": "4. Çeyrek", "zh-CN": "第4节",
    "th-TH": "ควอเตอร์ 4", "vi-VN": "Quý 4", "ko-KR": "4쿼터",
    "en-SG": "4th Quarter", "id-ID": "Kuarter 4", "nl-NL": "4e Kwart"
  },
  "1st period": {
    "en-GB": "1st Period", "ja-JP": "第1ピリオド", "it-IT": "1° Periodo",
    "es-ES": "1er Período", "ru-RU": "1-й период", "fr-FR": "1re période",
    "de-DE": "1. Periode", "tr-TR": "1. Periyot", "zh-CN": "第1节",
    "th-TH": "เพียริออด 1", "vi-VN": "Hiệp 1", "ko-KR": "1피리어드",
    "en-SG": "1st Period", "id-ID": "Periode 1", "nl-NL": "1e Periode"
  },
  "2nd period": {
    "en-GB": "2nd Period", "ja-JP": "第2ピリオド", "it-IT": "2° Periodo",
    "es-ES": "2° Período", "ru-RU": "2-й период", "fr-FR": "2e période",
    "de-DE": "2. Periode", "tr-TR": "2. Periyot", "zh-CN": "第2节",
    "th-TH": "เพียริออด 2", "vi-VN": "Hiệp 2", "ko-KR": "2피리어드",
    "en-SG": "2nd Period", "id-ID": "Periode 2", "nl-NL": "2e Periode"
  },
  "3rd period": {
    "en-GB": "3rd Period", "ja-JP": "第3ピリオド", "it-IT": "3° Periodo",
    "es-ES": "3er Período", "ru-RU": "3-й период", "fr-FR": "3e période",
    "de-DE": "3. Periode", "tr-TR": "3. Periyot", "zh-CN": "第3节",
    "th-TH": "เพียริออด 3", "vi-VN": "Hiệp 3", "ko-KR": "3피리어드",
    "en-SG": "3rd Period", "id-ID": "Periode 3", "nl-NL": "3e Periode"
  },
  future: {
    "en-GB": "Futures", "ja-JP": "先物", "it-IT": "Scommesse Future",
    "es-ES": "Apuestas Futuras", "ru-RU": "Долгосрочные ставки", "fr-FR": "Paris Long Terme",
    "de-DE": "Zukunftswetten", "tr-TR": "Uzun Vadeli Bahis", "zh-CN": "长期投注",
    "th-TH": "พนันระยะยาว", "vi-VN": "Kèo Dài Hạn", "ko-KR": "선물 베팅",
    "en-SG": "Futures", "id-ID": "Taruhan Masa Depan", "nl-NL": "Toekomstweddenschappen"
  },
  "team total over under": {
    "en-GB": "Single Team Total — Over/Under",
    "ja-JP": "単独チームトータル O/U",
    "it-IT": "Totale squadra singola — Over/Under",
    "es-ES": "Total equipo único — Más/Menos",
    "ru-RU": "ИТ команды — больше/меньше",
    "fr-FR": "Total équipe unique — Plus/Moins",
    "de-DE": "Einzelteam-Gesamt — Über/Unter",
    "tr-TR": "Tek Takım Toplamı — Alt/Üst",
    "zh-CN": "单队大小",
    "th-TH": "ทีมเดี่ยวสกอร์ — สูง/ต่ำ",
    "vi-VN": "Tài Xỉu Tổng Điểm Đội Đơn",
    "ko-KR": "단일 팀 총점 — 오버/언더",
    "en-SG": "Single Team Total — Over/Under",
    "id-ID": "Total Tim Tunggal — O/U",
    "nl-NL": "Enkel team totaal — over/under"
  },
  "home team exact goals number": {
    "en-GB": "Home — Exact Goals Number",
    "ja-JP": "ホーム正確な得点数",
    "it-IT": "Casa — Numero Esatto Gol",
    "es-ES": "Local — Goles exactos",
    "ru-RU": "Хозяева — точное число голов",
    "fr-FR": "Domicile — Nombre exact de buts",
    "de-DE": "Heim — Exakte Toranzahl",
    "tr-TR": "Ev — Tam Gol Sayısı",
    "zh-CN": "主队精确进球数",
    "th-TH": "เจ้าบ้าน — จำนวนประตูตรง",
    "vi-VN": "Chủ nhà — số bàn chính xác",
    "ko-KR": "홈 — 정확한 득점 수",
    "en-SG": "Home — Exact Goals Number",
    "id-ID": "Kandang — Jumlah Gol Tepat",
    "nl-NL": "Thuis — exact aantal doelpunten"
  },
  "away team exact goals number": {
    "en-GB": "Away — Exact Goals Number",
    "ja-JP": "アウェイ正確な得点数",
    "it-IT": "Trasferta — Numero Esatto Gol",
    "es-ES": "Visitante — Goles exactos",
    "ru-RU": "Гости — точное число голов",
    "fr-FR": "Extérieur — Nombre exact de buts",
    "de-DE": "Auswärts — Exakte Toranzahl",
    "tr-TR": "Deplasman — Tam Gol Sayısı",
    "zh-CN": "客队精确进球数",
    "th-TH": "ทีมเยือน — จำนวนประตูตรง",
    "vi-VN": "Đội khách — số bàn chính xác",
    "ko-KR": "원정 — 정확한 득점 수",
    "en-SG": "Away — Exact Goals Number",
    "id-ID": "Tandang — Jumlah Gol Tepat",
    "nl-NL": "Uit — exact aantal doelpunten"
  },
  "exact goals number": {
    "en-GB": "Exact Goals Number",
    "ja-JP": "正確な得点总数",
    "it-IT": "Numero Esatto Gol",
    "es-ES": "Goles exactos",
    "ru-RU": "Точное число голов",
    "fr-FR": "Nombre exact de buts",
    "de-DE": "Exakte Toranzahl",
    "tr-TR": "Tam Gol Sayısı",
    "zh-CN": "精确总进球数",
    "th-TH": "จำนวนประตูตรง",
    "vi-VN": "Số bàn chính xác",
    "ko-KR": "정확한 총골 수",
    "en-SG": "Exact Goals Number",
    "id-ID": "Jumlah Gol Tepat",
    "nl-NL": "Exact aantal doelpunten"
  },
  "first half exact goals number": {
    "en-GB": "Exact Goals — First Half",
    "ja-JP": "前半の正確な得点数",
    "it-IT": "Gol Esatti — 1° Tempo",
    "es-ES": "Goles exactos — 1ª parte",
    "ru-RU": "Точное число голов — 1-й тайм",
    "fr-FR": "Buts exacts — 1re mi-temps",
    "de-DE": "Exakte Tore — 1. Halbzeit",
    "tr-TR": "Tam Gol — İlk Yarı",
    "zh-CN": "上半场精确进球数",
    "th-TH": "จำนวนประตูตรง — ครึ่งแรก",
    "vi-VN": "Số Bàn Chính Xác — Hiệp 1",
    "ko-KR": "정확한 득점 — 전반",
    "en-SG": "Exact Goals — First Half",
    "id-ID": "Gol Tepat — Babak 1",
    "nl-NL": "Exacte doelpunten — 1e helft"
  },
  "total corners": {
    "en-GB": "Total Corners",
    "ja-JP": "コーナーキック総数",
    "it-IT": "Totale Calci d'Angolo",
    "es-ES": "Total de córners",
    "ru-RU": "Тотал угловых",
    "fr-FR": "Total corners",
    "de-DE": "Gesamt Eckbälle",
    "tr-TR": "Toplam Korner",
    "zh-CN": "角球总数",
    "th-TH": "เตะมุมรวม",
    "vi-VN": "Tổng Phạt Góc",
    "ko-KR": "코너킥 합계",
    "en-SG": "Total Corners",
    "id-ID": "Total Tendangan Sudut",
    "nl-NL": "Totaal hoekschoppen"
  },
  "total corners first half": {
    "en-GB": "Total Corners — First Half",
    "ja-JP": "コーナー総数（前半）",
    "it-IT": "Totale Calci d'Angolo — 1° Tempo",
    "es-ES": "Total córners — 1ª parte",
    "ru-RU": "Тотал угловых — 1-й тайм",
    "fr-FR": "Total corners — 1re mi-temps",
    "de-DE": "Eckbälle — 1. Halbzeit",
    "tr-TR": "Toplam Korner — İlk Yarı",
    "zh-CN": "上半场角球总数",
    "th-TH": "เตะมุมรวม — ครึ่งแรก",
    "vi-VN": "Tổng Phạt Góc — Hiệp 1",
    "ko-KR": "코너 합계 — 전반",
    "en-SG": "Total Corners — First Half",
    "id-ID": "Total Tendangan Sudut — Babak 1",
    "nl-NL": "Hoekschoppen — 1e helft"
  },
  "corners over under": {
    "en-GB": "Corners — Over/Under",
    "ja-JP": "コーナー O/U",
    "it-IT": "Calci d'Angolo — Over/Under",
    "es-ES": "Córners — Más/Menos",
    "ru-RU": "Угловые — больше/меньше",
    "fr-FR": "Corners — Plus/Moins",
    "de-DE": "Ecken — Über/Unter",
    "tr-TR": "Korner — Alt/Üst",
    "zh-CN": "角球大小",
    "th-TH": "เตะมุม — สูง/ต่ำ",
    "vi-VN": "Tài Xỉu Phạt Góc",
    "ko-KR": "코너 — 오버/언더",
    "en-SG": "Corners — Over/Under",
    "id-ID": "Tendangan Sudut — O/U",
    "nl-NL": "Hoekschoppen — over/under"
  },
  "corners handicap": {
    "en-GB": "Corners Handicap",
    "ja-JP": "コーナーハンディキャップ",
    "it-IT": "Handicap Calci d'Angolo",
    "es-ES": "Hándicap de córners",
    "ru-RU": "Фора по угловым",
    "fr-FR": "Handicap corners",
    "de-DE": "Ecken-Handicap",
    "tr-TR": "Korner Handikapı",
    "zh-CN": "角球让分",
    "th-TH": "แฮนดิแคปเตะมุม",
    "vi-VN": "Cược Chấp Góc",
    "ko-KR": "코너 핸디캡",
    "en-SG": "Corners Handicap",
    "id-ID": "Handicap Tendangan Sudut",
    "nl-NL": "Hoekschoppen handicap"
  },
  "asian handicap first half": {
    "en-GB": "Asian Handicap — First Half",
    "ja-JP": "アジアンハンディ（前半）",
    "it-IT": "Asian Handicap — 1° Tempo",
    "es-ES": "Hándicap asiático — 1ª parte",
    "ru-RU": "Азиатская фора — 1-й тайм",
    "fr-FR": "Handicap asiatique — 1re mi-temps",
    "de-DE": "Asian Handicap — 1. Halbzeit",
    "tr-TR": "Asya Handikap — İlk Yarı",
    "zh-CN": "亚盘让球（上半场）",
    "th-TH": "เอเชียนแฮนดิแคป — ครึ่งแรก",
    "vi-VN": "Cược Chấp Châu Á — Hiệp 1",
    "ko-KR": "아시안 핸디 — 전반",
    "en-SG": "Asian Handicap — First Half",
    "id-ID": "Asian Handicap — Babak 1",
    "nl-NL": "Asian handicap — 1e helft"
  },
  "asian handicap second half": {
    "en-GB": "Asian Handicap — Second Half",
    "ja-JP": "アジアンハンディ（後半）",
    "it-IT": "Asian Handicap — 2° Tempo",
    "es-ES": "Hándicap asiático — 2ª parte",
    "ru-RU": "Азиатская фора — 2-й тайм",
    "fr-FR": "Handicap asiatique — 2e mi-temps",
    "de-DE": "Asian Handicap — 2. Halbzeit",
    "tr-TR": "Asya Handikap — İkinci Yarı",
    "zh-CN": "亚盘让球（下半场）",
    "th-TH": "เอเชียนแฮนดิแคป — ครึ่งหลัง",
    "vi-VN": "Cược Chấp Châu Á — Hiệp 2",
    "ko-KR": "아시안 핸디 — 후반",
    "en-SG": "Asian Handicap — Second Half",
    "id-ID": "Asian Handicap — Babak 2",
    "nl-NL": "Asian handicap — 2e helft"
  },
  "european handicap": {
    "en-GB": "European Handicap",
    "ja-JP": "ヨーロピアンハンディキャップ",
    "it-IT": "Handicap Europeo",
    "es-ES": "Hándicap europeo",
    "ru-RU": "Европейская фора",
    "fr-FR": "Handicap européen",
    "de-DE": "Europäisches Handicap",
    "tr-TR": "Avrupa Handikapı",
    "zh-CN": "欧盘让球",
    "th-TH": "แฮนดิแคปยุโรป",
    "vi-VN": "Chấp Châu Âu",
    "ko-KR": "유럽형 핸디캡",
    "en-SG": "European Handicap",
    "id-ID": "Handicap Eropa",
    "nl-NL": "Europees handicap"
  },
  "european handicap first half": {
    "en-GB": "European Handicap — First Half",
    "ja-JP": "ヨーロピアンハンディ（前半）",
    "it-IT": "Handicap Europeo — 1° Tempo",
    "es-ES": "Hándicap europeo — 1ª parte",
    "ru-RU": "Европейская фора — 1-й тайм",
    "fr-FR": "Handicap européen — 1re mi-temps",
    "de-DE": "Europäisches Handicap — 1. HZ",
    "tr-TR": "Avrupa Handikap — İlk Yarı",
    "zh-CN": "欧盘让球（上半场）",
    "th-TH": "แฮนดิแคปยุโรป — ครึ่งแรก",
    "vi-VN": "Cược Chấp Châu Âu — Hiệp 1",
    "ko-KR": "유럽 핸디 — 전반",
    "en-SG": "European Handicap — First Half",
    "id-ID": "Handicap Eropa — Babak 1",
    "nl-NL": "Europees handicap — 1e helft"
  },
  "european handicap second half": {
    "en-GB": "European Handicap — Second Half",
    "ja-JP": "ヨーロピアンハンディ（後半）",
    "it-IT": "Handicap Europeo — 2° Tempo",
    "es-ES": "Hándicap europeo — 2ª parte",
    "ru-RU": "Европейская фора — 2-й тайм",
    "fr-FR": "Handicap européen — 2e mi-temps",
    "de-DE": "Europäisches Handicap — 2. HZ",
    "tr-TR": "Avrupa Handikap — İkinci Yarı",
    "zh-CN": "欧盘让球（下半场）",
    "th-TH": "แฮนดิแคปยุโรป — ครึ่งหลัง",
    "vi-VN": "Cược Chấp Châu Âu — Hiệp 2",
    "ko-KR": "유럽 핸디 — 후반",
    "en-SG": "European Handicap — Second Half",
    "id-ID": "Handicap Eropa — Babak 2",
    "nl-NL": "Europees handicap — 2e helft"
  },
  "handicap result": {
    "en-GB": "Handicap Result",
    "ja-JP": "ハンディキャップ結果",
    "it-IT": "Risultato con Handicap",
    "es-ES": "Resultado con hándicap",
    "ru-RU": "Исход с форой",
    "fr-FR": "Résultat avec handicap",
    "de-DE": "Handicap-Ergebnis",
    "tr-TR": "Handikap Sonucu",
    "zh-CN": "让球赛果",
    "th-TH": "ผลแฮนดิแคป",
    "vi-VN": "Kết Quả Cược Chấp",
    "ko-KR": "핸디 결과",
    "en-SG": "Handicap Result",
    "id-ID": "Hasil Handicap",
    "nl-NL": "Handicapresultaat"
  },
  "handicap result first half": {
    "en-GB": "Handicap Result — First Half",
    "ja-JP": "ハンディ結果（前半）",
    "it-IT": "Risultato Handicap — 1° Tempo",
    "es-ES": "Resultado hándicap — 1ª parte",
    "ru-RU": "Исход с форой — 1-й тайм",
    "fr-FR": "Résultat handicap — 1re mi-temps",
    "de-DE": "Handicap-Ergebnis — 1. HZ",
    "tr-TR": "Handikap Sonucu — İlk Yarı",
    "zh-CN": "让球赛果（上半场）",
    "th-TH": "ผลแฮนดิแคป — ครึ่งแรก",
    "vi-VN": "Kết Quả Cược Chấp — Hiệp 1",
    "ko-KR": "핸디 결과 — 전반",
    "en-SG": "Handicap Result — First Half",
    "id-ID": "Hasil Handicap — Babak 1",
    "nl-NL": "Handicapresultaat — 1e helft"
  },
  "handicap result second half": {
    "en-GB": "Handicap Result — Second Half",
    "ja-JP": "ハンディ結果（後半）",
    "it-IT": "Risultato Handicap — 2° Tempo",
    "es-ES": "Resultado hándicap — 2ª parte",
    "ru-RU": "Исход с форой — 2-й тайм",
    "fr-FR": "Résultat handicap — 2e mi-temps",
    "de-DE": "Handicap-Ergebnis — 2. HZ",
    "tr-TR": "Handikap Sonucu — İkinci Yarı",
    "zh-CN": "让球赛果（下半场）",
    "th-TH": "ผลแฮนดิแคป — ครึ่งหลัง",
    "vi-VN": "Kết Quả Cược Chấp — Hiệp 2",
    "ko-KR": "핸디 결과 — 후반",
    "en-SG": "Handicap Result — Second Half",
    "id-ID": "Hasil Handicap — Babak 2",
    "nl-NL": "Handicapresultaat — 2e helft"
  },
  "odd even": {
    "en-GB": "Odd / Even",
    "ja-JP": "奇数 / 偶数",
    "it-IT": "Dispari / Pari",
    "es-ES": "Impar / Par",
    "ru-RU": "Нечёт / Чёт",
    "fr-FR": "Impair / Pair",
    "de-DE": "Gerade / Ungerade",
    "tr-TR": "Tek / Çift",
    "zh-CN": "单 / 双",
    "th-TH": "คี่ / คู่",
    "vi-VN": "Lẻ / Chẵn",
    "ko-KR": "홀 / 짝",
    "en-SG": "Odd / Even",
    "id-ID": "Ganjil / Genap",
    "nl-NL": "Oneven / even"
  },
  "home odd even": {
    "en-GB": "Home — Odd/Even",
    "ja-JP": "ホーム奇数/偶数",
    "it-IT": "Casa — Dispari/Pari",
    "es-ES": "Local — Impar/Par",
    "ru-RU": "Хозяева — нечёт/чёт",
    "fr-FR": "Domicile — Impair/Pair",
    "de-DE": "Heim — Gerade/Ungerade",
    "tr-TR": "Ev — Tek/Çift",
    "zh-CN": "主队单双",
    "th-TH": "เจ้าบ้าน — คี่/คู่",
    "vi-VN": "Chủ Nhà — Lẻ/Chẵn",
    "ko-KR": "홈 — 홀/짝",
    "en-SG": "Home — Odd/Even",
    "id-ID": "Kandang — Ganjil/Genap",
    "nl-NL": "Thuis — oneven/even"
  },
  "away odd even": {
    "en-GB": "Away — Odd/Even",
    "ja-JP": "アウェイ奇数/偶数",
    "it-IT": "Trasferta — Dispari/Pari",
    "es-ES": "Visitante — Impar/Par",
    "ru-RU": "Гости — нечёт/чёт",
    "fr-FR": "Extérieur — Impair/Pair",
    "de-DE": "Auswärts — Gerade/Ungerade",
    "tr-TR": "Deplasman — Tek/Çift",
    "zh-CN": "客队单双",
    "th-TH": "ทีมเยือน — คี่/คู่",
    "vi-VN": "Đội Khách — Lẻ/Chẵn",
    "ko-KR": "원정 — 홀/짝",
    "en-SG": "Away — Odd/Even",
    "id-ID": "Tandang — Ganjil/Genap",
    "nl-NL": "Uit — oneven/even"
  },
  "race to 2": {
    "en-GB": "Race To 2",
    "ja-JP": "先に2到達",
    "it-IT": "Corsa a 2",
    "es-ES": "Carrera a 2",
    "ru-RU": "Гонка до 2",
    "fr-FR": "Course à 2",
    "de-DE": "Rennen bis 2",
    "tr-TR": "2'ye Yarış",
    "zh-CN": "抢先得2分/球",
    "th-TH": "แข่งถึง 2",
    "vi-VN": "Đua đến 2",
    "ko-KR": "2 선점 레이스",
    "en-SG": "Race To 2",
    "id-ID": "Balapan ke 2",
    "nl-NL": "Race naar 2"
  },
  "race to 3": {
    "en-GB": "Race To 3",
    "ja-JP": "先に3到達",
    "it-IT": "Corsa a 3",
    "es-ES": "Carrera a 3",
    "ru-RU": "Гонка до 3",
    "fr-FR": "Course à 3",
    "de-DE": "Rennen bis 3",
    "tr-TR": "3'e Yarış",
    "zh-CN": "抢先得3",
    "th-TH": "แข่งถึง 3",
    "vi-VN": "Đua đến 3",
    "ko-KR": "3 선점 레이스",
    "en-SG": "Race To 3",
    "id-ID": "Balapan ke 3",
    "nl-NL": "Race naar 3"
  },
  "race to 5": {
    "en-GB": "Race To 5",
    "ja-JP": "先に5到達",
    "it-IT": "Corsa a 5",
    "es-ES": "Carrera a 5",
    "ru-RU": "Гонка до 5",
    "fr-FR": "Course à 5",
    "de-DE": "Rennen bis 5",
    "tr-TR": "5'e Yarış",
    "zh-CN": "抢先得5",
    "th-TH": "แข่งถึง 5",
    "vi-VN": "Đua đến 5",
    "ko-KR": "5 선점 레이스",
    "en-SG": "Race To 5",
    "id-ID": "Balapan ke 5",
    "nl-NL": "Race naar 5"
  },
  "race to 7": {
    "en-GB": "Race To 7",
    "ja-JP": "先に7到達",
    "it-IT": "Corsa a 7",
    "es-ES": "Carrera a 7",
    "ru-RU": "Гонка до 7",
    "fr-FR": "Course à 7",
    "de-DE": "Rennen bis 7",
    "tr-TR": "7'ye Yarış",
    "zh-CN": "抢先得7",
    "th-TH": "แข่งถึง 7",
    "vi-VN": "Đua đến 7",
    "ko-KR": "7 선점 레이스",
    "en-SG": "Race To 7",
    "id-ID": "Balapan ke 7",
    "nl-NL": "Race naar 7"
  },
  "race to 2 goals": {
    "en-GB": "Race To 2 Goals",
    "ja-JP": "先に2ゴール",
    "it-IT": "Corsa a 2 gol",
    "es-ES": "Carrera a 2 goles",
    "ru-RU": "Гонка до 2 голов",
    "fr-FR": "Course à 2 buts",
    "de-DE": "Rennen bis 2 Tore",
    "tr-TR": "2 Gole Yarış",
    "zh-CN": "抢先进2球",
    "th-TH": "แข่ง 2 ประตู",
    "vi-VN": "Đua đến 2 bàn",
    "ko-KR": "2골 선점",
    "en-SG": "Race To 2 Goals",
    "id-ID": "Balapan 2 Gol",
    "nl-NL": "Race naar 2 doelpunten"
  },
  "race to 3 goals": {
    "en-GB": "Race To 3 Goals",
    "ja-JP": "先に3ゴール",
    "it-IT": "Corsa a 3 gol",
    "es-ES": "Carrera a 3 goles",
    "ru-RU": "Гонка до 3 голов",
    "fr-FR": "Course à 3 buts",
    "de-DE": "Rennen bis 3 Tore",
    "tr-TR": "3 Gole Yarış",
    "zh-CN": "抢先进3球",
    "th-TH": "แข่ง 3 ประตู",
    "vi-VN": "Đua đến 3 bàn",
    "ko-KR": "3골 선점",
    "en-SG": "Race To 3 Goals",
    "id-ID": "Balapan 3 Gol",
    "nl-NL": "Race naar 3 doelpunten"
  },
  "race to 5 corners": {
    "en-GB": "Race To 5 Corners",
    "ja-JP": "先に5コーナー",
    "it-IT": "Corsa a 5 calci d'angolo",
    "es-ES": "Carrera a 5 córners",
    "ru-RU": "Гонка до 5 угловых",
    "fr-FR": "Course à 5 corners",
    "de-DE": "Rennen bis 5 Ecken",
    "tr-TR": "5 Korner Yarışı",
    "zh-CN": "抢先5个角球",
    "th-TH": "แข่งเตะมุม 5",
    "vi-VN": "Đua đến 5 phạt góc",
    "ko-KR": "코너 5개 선점",
    "en-SG": "Race To 5 Corners",
    "id-ID": "Balapan 5 Tendangan Sudut",
    "nl-NL": "Race naar 5 hoekschoppen"
  },
  "race to 3 corners": {
    "en-GB": "Race To 3 Corners",
    "ja-JP": "先に3コーナー",
    "it-IT": "Corsa a 3 calci d'angolo",
    "es-ES": "Carrera a 3 córners",
    "ru-RU": "Гонка до 3 угловых",
    "fr-FR": "Course à 3 corners",
    "de-DE": "Rennen bis 3 Ecken",
    "tr-TR": "3 Korner Yarışı",
    "zh-CN": "抢先3个角球",
    "th-TH": "แข่งเตะมุม 3",
    "vi-VN": "Đua đến 3 phạt góc",
    "ko-KR": "코너 3개 선점",
    "en-SG": "Race To 3 Corners",
    "id-ID": "Balapan 3 Tendangan Sudut",
    "nl-NL": "Race naar 3 hoekschoppen"
  },
  "race to 5 shots on target": {
    "en-GB": "Race To 5 Shots On Target",
    "ja-JP": "先に枠内5本",
    "it-IT": "Corsa a 5 tiri in porta",
    "es-ES": "Carrera a 5 disparos a puerta",
    "ru-RU": "Гонка до 5 ударов в створ",
    "fr-FR": "Course à 5 tirs cadrés",
    "de-DE": "Rennen bis 5 Schüsse aufs Tor",
    "tr-TR": "5 İsabetli Şut Yarışı",
    "zh-CN": "抢先5次射正",
    "th-TH": "แข่งยิงตรงกรอบ 5",
    "vi-VN": "Đua đến 5 cú sút trúng đích",
    "ko-KR": "유효슈팅 5개 선점",
    "en-SG": "Race To 5 Shots On Target",
    "id-ID": "Balapan 5 Tembakan On Target",
    "nl-NL": "Race naar 5 schoten op doel"
  },
  "correct score": {
    "en-GB": "Correct Score",
    "ja-JP": "正確スコア",
    "it-IT": "Risultato Esatto",
    "es-ES": "Marcador Exacto",
    "ru-RU": "Точный счёт",
    "fr-FR": "Score Exact",
    "de-DE": "Korrektes Ergebnis",
    "tr-TR": "Doğru Skor",
    "zh-CN": "波胆",
    "th-TH": "สกอร์ที่ถูกต้อง",
    "vi-VN": "Tỷ Số Chính Xác",
    "ko-KR": "정확한 스코어",
    "en-SG": "Correct Score",
    "id-ID": "Skor Tepat",
    "nl-NL": "Correcte score"
  },
  "correct score first half": {
    "en-GB": "Correct Score — First Half",
    "ja-JP": "正確スコア（前半）",
    "it-IT": "Risultato Esatto — 1° Tempo",
    "es-ES": "Marcador exacto — 1ª parte",
    "ru-RU": "Точный счёт — 1-й тайм",
    "fr-FR": "Score exact — 1re mi-temps",
    "de-DE": "Korrektes Ergebnis — 1. HZ",
    "tr-TR": "Doğru Skor — İlk Yarı",
    "zh-CN": "半场波胆",
    "th-TH": "สกอร์ครึ่งแรก",
    "vi-VN": "Tỷ Số Chính Xác — Hiệp 1",
    "ko-KR": "정확한 스코어 — 전반",
    "en-SG": "Correct Score — First Half",
    "id-ID": "Skor Tepat — Babak 1",
    "nl-NL": "Correcte score — 1e helft"
  },
  "correct score second half": {
    "en-GB": "Correct Score — Second Half",
    "ja-JP": "正確スコア（後半）",
    "it-IT": "Risultato Esatto — 2° Tempo",
    "es-ES": "Marcador exacto — 2ª parte",
    "ru-RU": "Точный счёт — 2-й тайм",
    "fr-FR": "Score exact — 2e mi-temps",
    "de-DE": "Korrektes Ergebnis — 2. HZ",
    "tr-TR": "Doğru Skor — İkinci Yarı",
    "zh-CN": "下半场波胆",
    "th-TH": "สกอร์ครึ่งหลัง",
    "vi-VN": "Tỷ Số Chính Xác — Hiệp 2",
    "ko-KR": "정확한 스코어 — 후반",
    "en-SG": "Correct Score — Second Half",
    "id-ID": "Skor Tepat — Babak 2",
    "nl-NL": "Correcte score — 2e helft"
  },
  "winning margin": {
    "en-GB": "Winning Margin",
    "ja-JP": "勝敗差",
    "it-IT": "Margine di Vittoria",
    "es-ES": "Margen de victoria",
    "ru-RU": "Маржа победы",
    "fr-FR": "Marge de victoire",
    "de-DE": "Siegermarge",
    "tr-TR": "Galibiyet Marjı",
    "zh-CN": "净胜分",
    "th-TH": "ต่างประตูชนะ",
    "vi-VN": "Cách Biệt Thắng",
    "ko-KR": "승리 마진",
    "en-SG": "Winning Margin",
    "id-ID": "Margin Kemenangan",
    "nl-NL": "Winnende marge"
  },
  "exact corners": {
    "en-GB": "Exact Corners",
    "ja-JP": "正確なコーナー数",
    "it-IT": "Calci d'Angolo Esatti",
    "es-ES": "Córners exactos",
    "ru-RU": "Точное число угловых",
    "fr-FR": "Corners exacts",
    "de-DE": "Exakte Eckbälle",
    "tr-TR": "Tam Korner Sayısı",
    "zh-CN": "精确角球数",
    "th-TH": "เตะมุมตรง",
    "vi-VN": "Số Phạt Góc Chính Xác",
    "ko-KR": "정확한 코너 수",
    "en-SG": "Exact Corners",
    "id-ID": "Tendangan Sudut Tepat",
    "nl-NL": "Exact aantal hoekschoppen"
  },
  "exact cards": {
    "en-GB": "Exact Cards",
    "ja-JP": "正確なカード数",
    "it-IT": "Cartellini Esatti",
    "es-ES": "Tarjetas exactas",
    "ru-RU": "Точное число карточек",
    "fr-FR": "Cartons exacts",
    "de-DE": "Exakte Karten",
    "tr-TR": "Tam Kart Sayısı",
    "zh-CN": "精确牌数",
    "th-TH": "ใบตัดสินตรง",
    "vi-VN": "Số Thẻ Chính Xác",
    "ko-KR": "정확한 카드 수",
    "en-SG": "Exact Cards",
    "id-ID": "Kartu Tepat",
    "nl-NL": "Exact aantal kaarten"
  },
  "second half exact goals number": {
    "en-GB": "Exact Goals — Second Half",
    "ja-JP": "後半の正確な得点数",
    "it-IT": "Gol Esatti — 2° Tempo",
    "es-ES": "Goles exactos — 2ª parte",
    "ru-RU": "Точное число голов — 2-й тайм",
    "fr-FR": "Buts exacts — 2e mi-temps",
    "de-DE": "Exakte Tore — 2. HZ",
    "tr-TR": "Tam Gol — İkinci Yarı",
    "zh-CN": "下半场精确进球数",
    "th-TH": "จำนวนประตูตรง — ครึ่งหลัง",
    "vi-VN": "Số Bàn Chính Xác — Hiệp 2",
    "ko-KR": "정확한 득점 — 후반",
    "en-SG": "Exact Goals — Second Half",
    "id-ID": "Gol Tepat — Babak 2",
    "nl-NL": "Exacte doelpunten — 2e helft"
  },
  "to qualify": {
    "en-GB": "To Qualify",
    "ja-JP": "予選突破",
    "it-IT": "Qualificazione",
    "es-ES": "Clasificar",
    "ru-RU": "Пройти дальше",
    "fr-FR": "Se qualifier",
    "de-DE": "Qualifikation",
    "tr-TR": "Elemeyi Geçme",
    "zh-CN": "晋级",
    "th-TH": "ผ่านเข้ารอบ",
    "vi-VN": "Đội Đi Tiếp",
    "ko-KR": "진출",
    "en-SG": "To Qualify",
    "id-ID": "Lolos",
    "nl-NL": "Kwalificeren"
  },
  qualify: {
    "en-GB": "Qualify",
    "ja-JP": "予選突破",
    "it-IT": "Qualificarsi",
    "es-ES": "Clasificar",
    "ru-RU": "Квалификация",
    "fr-FR": "Qualifier",
    "de-DE": "Qualifizieren",
    "tr-TR": "Elemeler",
    "zh-CN": "晋级",
    "th-TH": "ผ่านเข้ารอบ",
    "vi-VN": "Vượt Vòng Loại",
    "ko-KR": "예선 통과",
    "en-SG": "Qualify",
    "id-ID": "Kualifikasi",
    "nl-NL": "Kwalificatie"
  },
  "team to qualify": {
    "en-GB": "Team To Qualify",
    "ja-JP": "チーム予選突破",
    "it-IT": "Squadra che si Qualifica",
    "es-ES": "Equipo que clasifica",
    "ru-RU": "Команда проходит дальше",
    "fr-FR": "Équipe qualifiée",
    "de-DE": "Team qualifiziert sich",
    "tr-TR": "Takım Elemeyi Geçer",
    "zh-CN": "球队晋级",
    "th-TH": "ทีมผ่านเข้ารอบ",
    "vi-VN": "Đội Giành Vé Đi Tiếp",
    "ko-KR": "팀 진출",
    "en-SG": "Team To Qualify",
    "id-ID": "Tim Lolos",
    "nl-NL": "Team kwalificeert zich"
  },
  "lift the trophy": {
    "en-GB": "Lift The Trophy",
    "ja-JP": "トロフィー獲得",
    "it-IT": "Sollevare il Trofeo",
    "es-ES": "Levantar el trofeo",
    "ru-RU": "Поднять кубок",
    "fr-FR": "Soulever le trophée",
    "de-DE": "Pokal heben",
    "tr-TR": "Kupayı Kaldırma",
    "zh-CN": "夺冠",
    "th-TH": "ชูถ้วย",
    "vi-VN": "Giành cúp",
    "ko-KR": "트로피 들기",
    "en-SG": "Lift The Trophy",
    "id-ID": "Angkat Trofi",
    "nl-NL": "De trofee omhoog tillen"
  },
  "winner outright": {
    "en-GB": "Winner Outright",
    "ja-JP": "アウトライト優勝",
    "it-IT": "Vincitore Assoluto",
    "es-ES": "Ganador absoluto",
    "ru-RU": "Победитель (outright)",
    "fr-FR": "Vainqueur outright",
    "de-DE": "Gesamtsieger",
    "tr-TR": "Genel Kazanan",
    "zh-CN": "全程冠军",
    "th-TH": "ผู้ชนะแบบเอาท์ไรท์",
    "vi-VN": "Vô địch chung cuộc",
    "ko-KR": "아웃라이트 우승",
    "en-SG": "Winner Outright",
    "id-ID": "Pemenang Outright",
    "nl-NL": "Outright winnaar"
  },
  "outright winner": {
    "en-GB": "Outright Winner",
    "ja-JP": "アウトライト勝者",
    "it-IT": "Vincente Assoluto",
    "es-ES": "Ganador absoluto",
    "ru-RU": "Победитель (outright)",
    "fr-FR": "Vainqueur outright",
    "de-DE": "Outright-Sieger",
    "tr-TR": "Outright Kazanan",
    "zh-CN": "全程胜者",
    "th-TH": "ผู้ชนะเอาท์ไรท์",
    "vi-VN": "Nhà vô địch chung cuộc",
    "ko-KR": "아웃라이트 승자",
    "en-SG": "Outright Winner",
    "id-ID": "Pemenang Outright",
    "nl-NL": "Outright winnaar"
  },
  "tournament winner": {
    "en-GB": "Tournament Winner",
    "ja-JP": "大会優勝",
    "it-IT": "Vincitore del Torneo",
    "es-ES": "Ganador del torneo",
    "ru-RU": "Победитель турнира",
    "fr-FR": "Vainqueur du tournoi",
    "de-DE": "Turniersieger",
    "tr-TR": "Turnuva Kazananı",
    "zh-CN": "赛事冠军",
    "th-TH": "แชมป์ทัวร์นาเมนต์",
    "vi-VN": "Vô địch giải",
    "ko-KR": "토너먼트 우승",
    "en-SG": "Tournament Winner",
    "id-ID": "Juara Turnamen",
    "nl-NL": "Toernooiwinnaar"
  },
  "first goal method": {
    "en-GB": "First Goal Method",
    "ja-JP": "先制点の得点方法",
    "it-IT": "Modo Primo Gol",
    "es-ES": "Modo del primer gol",
    "ru-RU": "Способ первого гола",
    "fr-FR": "Mode du 1er but",
    "de-DE": "Art des 1. Tors",
    "tr-TR": "İlk Golün Şekli",
    "zh-CN": "首个进球方式",
    "th-TH": "วิธีประตูแรก",
    "vi-VN": "Cách Ghi Bàn Đầu Tiên",
    "ko-KR": "첫 골 방식",
    "en-SG": "First Goal Method",
    "id-ID": "Metode Gol Pertama",
    "nl-NL": "Manier eerste doelpunt"
  },
  "clean sheet": {
    "en-GB": "Clean Sheet",
    "ja-JP": "無失点",
    "it-IT": "Porta Inviolata",
    "es-ES": "Portería a cero",
    "ru-RU": "Сухой матч",
    "fr-FR": "Clean sheet",
    "de-DE": "Zu Null",
    "tr-TR": "Gol Yemeden",
    "zh-CN": "零封",
    "th-TH": "คลีนชีต",
    "vi-VN": "Giữ Sạch Lưới",
    "ko-KR": "클린시트",
    "en-SG": "Clean Sheet",
    "id-ID": "Clean Sheet",
    "nl-NL": "Clean sheet"
  },
  "clean sheet home": {
    "en-GB": "Clean Sheet — Home",
    "ja-JP": "ホーム無失点",
    "it-IT": "Porta Inviolata — Casa",
    "es-ES": "Portería a cero — Local",
    "ru-RU": "Сухой матч — хозяева",
    "fr-FR": "Clean sheet — Domicile",
    "de-DE": "Zu Null — Heim",
    "tr-TR": "Gol Yemeden — Ev",
    "zh-CN": "主队零封",
    "th-TH": "คลีนชีตเจ้าบ้าน",
    "vi-VN": "Chủ Nhà Giữ Sạch Lưới",
    "ko-KR": "홈 클린시트",
    "en-SG": "Clean Sheet — Home",
    "id-ID": "Clean Sheet — Kandang",
    "nl-NL": "Clean sheet — thuis"
  },
  "clean sheet away": {
    "en-GB": "Clean Sheet — Away",
    "ja-JP": "アウェイ無失点",
    "it-IT": "Porta Inviolata — Trasferta",
    "es-ES": "Portería a cero — Visitante",
    "ru-RU": "Сухой матч — гости",
    "fr-FR": "Clean sheet — Extérieur",
    "de-DE": "Zu Null — Auswärts",
    "tr-TR": "Gol Yemeden — Deplasman",
    "zh-CN": "客队零封",
    "th-TH": "คลีนชีตทีมเยือน",
    "vi-VN": "Đội Khách Giữ Sạch Lưới",
    "ko-KR": "원정 클린시트",
    "en-SG": "Clean Sheet — Away",
    "id-ID": "Clean Sheet — Tandang",
    "nl-NL": "Clean sheet — uit"
  },
  "home clean sheet": {
    "en-GB": "Home Clean Sheet",
    "ja-JP": "ホーム無失点",
    "it-IT": "Casa Porta Inviolata",
    "es-ES": "Portería a cero local",
    "ru-RU": "Сухой матч хозяев",
    "fr-FR": "Clean sheet domicile",
    "de-DE": "Heim zu Null",
    "tr-TR": "Ev Gol Yemeden",
    "zh-CN": "主队零封",
    "th-TH": "เจ้าบ้านคลีนชีต",
    "vi-VN": "Chủ Nhà Giữ Sạch Lưới",
    "ko-KR": "홈 클린시트",
    "en-SG": "Home Clean Sheet",
    "id-ID": "Clean Sheet Kandang",
    "nl-NL": "Thuis clean sheet"
  },
  "away clean sheet": {
    "en-GB": "Away Clean Sheet",
    "ja-JP": "アウェイ無失点",
    "it-IT": "Trasferta Porta Inviolata",
    "es-ES": "Portería a cero visitante",
    "ru-RU": "Сухой матч гостей",
    "fr-FR": "Clean sheet extérieur",
    "de-DE": "Auswärts zu Null",
    "tr-TR": "Deplasman Gol Yemeden",
    "zh-CN": "客队零封",
    "th-TH": "ทีมเยือนคลีนชีต",
    "vi-VN": "Đội Khách Giữ Sạch Lưới",
    "ko-KR": "원정 클린시트",
    "en-SG": "Away Clean Sheet",
    "id-ID": "Clean Sheet Tandang",
    "nl-NL": "Uit clean sheet"
  },
  "to keep a clean sheet": {
    "en-GB": "To Keep A Clean Sheet",
    "ja-JP": "無失点を守る",
    "it-IT": "Mantenere la Porta Inviolata",
    "es-ES": "Mantener la portería a cero",
    "ru-RU": "Сохранить сухой матч",
    "fr-FR": "Garder sa cage inviolée",
    "de-DE": "Zu Null spielen",
    "tr-TR": "Gol Yememek",
    "zh-CN": "保持零封",
    "th-TH": "เก็บคลีนชีต",
    "vi-VN": "Giữ Sạch Lưới",
    "ko-KR": "클린시트 유지",
    "en-SG": "To Keep A Clean Sheet",
    "id-ID": "Pertahankan Clean Sheet",
    "nl-NL": "De nul houden"
  },
  "cards over under": {
    "en-GB": "Cards — Over/Under",
    "ja-JP": "カード O/U",
    "it-IT": "Cartellini — Over/Under",
    "es-ES": "Tarjetas — Más/Menos",
    "ru-RU": "Карточки — больше/меньше",
    "fr-FR": "Cartons — Plus/Moins",
    "de-DE": "Karten — Über/Unter",
    "tr-TR": "Kart — Alt/Üst",
    "zh-CN": "牌数大小",
    "th-TH": "ใบเหลืองแดง — สูง/ต่ำ",
    "vi-VN": "Tài Xỉu Thẻ",
    "ko-KR": "카드 — 오버/언더",
    "en-SG": "Cards — Over/Under",
    "id-ID": "Kartu — O/U",
    "nl-NL": "Kaarten — over/under"
  },
  "cards over under first half": {
    "en-GB": "Cards — Over/Under — First Half",
    "ja-JP": "カード O/U（前半）",
    "it-IT": "Cartellini O/U — 1° Tempo",
    "es-ES": "Tarjetas O/U — 1ª parte",
    "ru-RU": "Карточки О/У — 1-й тайм",
    "fr-FR": "Cartons O/U — 1re mi-temps",
    "de-DE": "Karten O/U — 1. HZ",
    "tr-TR": "Kart O/U — İlk Yarı",
    "zh-CN": "上半场牌数大小",
    "th-TH": "ใบ — สูง/ต่ำ — ครึ่งแรก",
    "vi-VN": "Tài Xỉu Thẻ — Hiệp 1",
    "ko-KR": "카드 O/U — 전반",
    "en-SG": "Cards — O/U — First Half",
    "id-ID": "Kartu O/U — Babak 1",
    "nl-NL": "Kaarten O/U — 1e helft"
  },
  "cards over under second half": {
    "en-GB": "Cards — Over/Under — Second Half",
    "ja-JP": "カード O/U（後半）",
    "it-IT": "Cartellini O/U — 2° Tempo",
    "es-ES": "Tarjetas O/U — 2ª parte",
    "ru-RU": "Карточки О/У — 2-й тайм",
    "fr-FR": "Cartons O/U — 2e mi-temps",
    "de-DE": "Karten O/U — 2. HZ",
    "tr-TR": "Kart O/U — İkinci Yarı",
    "zh-CN": "下半场牌数大小",
    "th-TH": "ใบ — สูง/ต่ำ — ครึ่งหลัง",
    "vi-VN": "Tài Xỉu Thẻ — Hiệp 2",
    "ko-KR": "카드 O/U — 후반",
    "en-SG": "Cards — O/U — Second Half",
    "id-ID": "Kartu O/U — Babak 2",
    "nl-NL": "Kaarten O/U — 2e helft"
  },
  "total cards": {
    "en-GB": "Total Cards",
    "ja-JP": "カード総数",
    "it-IT": "Totale Cartellini",
    "es-ES": "Total tarjetas",
    "ru-RU": "Тотал карточек",
    "fr-FR": "Total cartons",
    "de-DE": "Gesamt Karten",
    "tr-TR": "Toplam Kart",
    "zh-CN": "总牌数",
    "th-TH": "ใบรวม",
    "vi-VN": "Tổng Thẻ",
    "ko-KR": "총 카드 수",
    "en-SG": "Total Cards",
    "id-ID": "Total Kartu",
    "nl-NL": "Totaal kaarten"
  },
  "total cards first half": {
    "en-GB": "Total Cards — First Half",
    "ja-JP": "カード総数（前半）",
    "it-IT": "Totale Cartellini — 1° Tempo",
    "es-ES": "Total tarjetas — 1ª parte",
    "ru-RU": "Тотал карточек — 1-й тайм",
    "fr-FR": "Total cartons — 1re mi-temps",
    "de-DE": "Karten gesamt — 1. HZ",
    "tr-TR": "Toplam Kart — İlk Yarı",
    "zh-CN": "上半场总牌数",
    "th-TH": "ใบรวม — ครึ่งแรก",
    "vi-VN": "Tổng Thẻ — Hiệp 1",
    "ko-KR": "총 카드 — 전반",
    "en-SG": "Total Cards — First Half",
    "id-ID": "Total Kartu — Babak 1",
    "nl-NL": "Totaal kaarten — 1e helft"
  },
  "total cards second half": {
    "en-GB": "Total Cards — Second Half",
    "ja-JP": "カード総数（後半）",
    "it-IT": "Totale Cartellini — 2° Tempo",
    "es-ES": "Total tarjetas — 2ª parte",
    "ru-RU": "Тотал карточек — 2-й тайм",
    "fr-FR": "Total cartons — 2e mi-temps",
    "de-DE": "Karten gesamt — 2. HZ",
    "tr-TR": "Toplam Kart — İkinci Yarı",
    "zh-CN": "下半场总牌数",
    "th-TH": "ใบรวม — ครึ่งหลัง",
    "vi-VN": "Tổng Thẻ — Hiệp 2",
    "ko-KR": "총 카드 — 후반",
    "en-SG": "Total Cards — Second Half",
    "id-ID": "Total Kartu — Babak 2",
    "nl-NL": "Totaal kaarten — 2e helft"
  },
  "cards handicap": {
    "en-GB": "Cards Handicap",
    "ja-JP": "カードハンディキャップ",
    "it-IT": "Handicap Cartellini",
    "es-ES": "Hándicap de tarjetas",
    "ru-RU": "Фора по карточкам",
    "fr-FR": "Handicap cartons",
    "de-DE": "Karten-Handicap",
    "tr-TR": "Kart Handikapı",
    "zh-CN": "牌数让分",
    "th-TH": "แฮนดิแคปใบ",
    "vi-VN": "Cược Chấp Thẻ",
    "ko-KR": "카드 핸디캡",
    "en-SG": "Cards Handicap",
    "id-ID": "Handicap Kartu",
    "nl-NL": "Kaartenhandicap"
  },
  "cards asian handicap": {
    "en-GB": "Cards — Asian Handicap",
    "ja-JP": "カード・アジアンハンディ",
    "it-IT": "Cartellini — Asian Handicap",
    "es-ES": "Tarjetas — Hándicap asiático",
    "ru-RU": "Карточки — азиатская фора",
    "fr-FR": "Cartons — handicap asiatique",
    "de-DE": "Karten — Asian Handicap",
    "tr-TR": "Kart — Asya Handikapı",
    "zh-CN": "牌数亚盘",
    "th-TH": "ใบ — เอเชียนแฮนดิแคป",
    "vi-VN": "Thẻ — Kèo Chấp Châu Á",
    "ko-KR": "카드 — 아시안 핸디",
    "en-SG": "Cards — Asian Handicap",
    "id-ID": "Kartu — Asian Handicap",
    "nl-NL": "Kaarten — Asian handicap"
  },
  "team cards": {
    "en-GB": "Team Cards",
    "ja-JP": "チームカード数",
    "it-IT": "Cartellini Squadra",
    "es-ES": "Tarjetas del equipo",
    "ru-RU": "Карточки команды",
    "fr-FR": "Cartons équipe",
    "de-DE": "Team-Karten",
    "tr-TR": "Takım Kartları",
    "zh-CN": "球队牌数",
    "th-TH": "ใบทีม",
    "vi-VN": "Thẻ Đội",
    "ko-KR": "팀 카드",
    "en-SG": "Team Cards",
    "id-ID": "Kartu Tim",
    "nl-NL": "Teamkaarten"
  },
  "home team cards": {
    "en-GB": "Home Team Cards",
    "ja-JP": "ホームのカード数",
    "it-IT": "Cartellini Casa",
    "es-ES": "Tarjetas local",
    "ru-RU": "Карточки хозяев",
    "fr-FR": "Cartons domicile",
    "de-DE": "Heim-Karten",
    "tr-TR": "Ev Sahibi Kartları",
    "zh-CN": "主队牌数",
    "th-TH": "ใบเจ้าบ้าน",
    "vi-VN": "Thẻ Chủ Nhà",
    "ko-KR": "홈팀 카드",
    "en-SG": "Home Team Cards",
    "id-ID": "Kartu Tim Kandang",
    "nl-NL": "Thuisploeg kaarten"
  },
  "away team cards": {
    "en-GB": "Away Team Cards",
    "ja-JP": "アウェイのカード数",
    "it-IT": "Cartellini Trasferta",
    "es-ES": "Tarjetas visitante",
    "ru-RU": "Карточки гостей",
    "fr-FR": "Cartons extérieur",
    "de-DE": "Auswärts-Karten",
    "tr-TR": "Deplasman Kartları",
    "zh-CN": "客队牌数",
    "th-TH": "ใบทีมเยือน",
    "vi-VN": "Thẻ Đội Khách",
    "ko-KR": "원정팀 카드",
    "en-SG": "Away Team Cards",
    "id-ID": "Kartu Tim Tandang",
    "nl-NL": "Uitploeg kaarten"
  },
  "yellow cards": {
    "en-GB": "Yellow Cards",
    "ja-JP": "イエローカード",
    "it-IT": "Cartellini Gialli",
    "es-ES": "Tarjetas amarillas",
    "ru-RU": "Жёлтые карточки",
    "fr-FR": "Cartons jaunes",
    "de-DE": "Gelbe Karten",
    "tr-TR": "Sarı Kartlar",
    "zh-CN": "黄牌",
    "th-TH": "ใบเหลือง",
    "vi-VN": "Thẻ vàng",
    "ko-KR": "옐로카드",
    "en-SG": "Yellow Cards",
    "id-ID": "Kartu Kuning",
    "nl-NL": "Gele kaarten"
  },
  "red card": {
    "en-GB": "Red Card",
    "ja-JP": "レッドカード",
    "it-IT": "Cartellino Rosso",
    "es-ES": "Tarjeta roja",
    "ru-RU": "Красная карточка",
    "fr-FR": "Carton rouge",
    "de-DE": "Rote Karte",
    "tr-TR": "Kırmızı Kart",
    "zh-CN": "红牌",
    "th-TH": "ใบแดง",
    "vi-VN": "Thẻ đỏ",
    "ko-KR": "레드카드",
    "en-SG": "Red Card",
    "id-ID": "Kartu Merah",
    "nl-NL": "Rode kaart"
  },
  "penalty awarded": {
    "en-GB": "Penalty Awarded",
    "ja-JP": "PK判定",
    "it-IT": "Rigore Assegnato",
    "es-ES": "Penalti señalado",
    "ru-RU": "Назначен пенальти",
    "fr-FR": "Penalty accordé",
    "de-DE": "Elfmeter gegeben",
    "tr-TR": "Penaltı Verildi",
    "zh-CN": "判罚点球",
    "th-TH": "ได้จุดโทษ",
    "vi-VN": "Được hưởng phạt đền",
    "ko-KR": "페널티 지정",
    "en-SG": "Penalty Awarded",
    "id-ID": "Penalti Diberikan",
    "nl-NL": "Strafschop gegeven"
  },
  "booking points": {
    "en-GB": "Booking Points",
    "ja-JP": "ブッキングポイント",
    "it-IT": "Punti Cartellini",
    "es-ES": "Puntos de tarjetas",
    "ru-RU": "Очки за карточки",
    "fr-FR": "Points cartons",
    "de-DE": "Booking Points",
    "tr-TR": "Kart Puanları",
    "zh-CN": "牌分",
    "th-TH": "คะแนนใบ",
    "vi-VN": "Điểm thẻ",
    "ko-KR": "부킹 포인트",
    "en-SG": "Booking Points",
    "id-ID": "Poin Booking",
    "nl-NL": "Bookingpunten"
  },
  "team total corners": {
    "en-GB": "Single Team Total Corners",
    "ja-JP": "単独チーム総コーナー",
    "it-IT": "Totale calci d'angolo squadra singola",
    "es-ES": "Total de córners de un solo equipo",
    "ru-RU": "Индивидуальный тотал угловых команды",
    "fr-FR": "Total corners équipe unique",
    "de-DE": "Einzelnes Team — Eckbälle gesamt",
    "tr-TR": "Tek Takım Toplam Korner",
    "zh-CN": "单队总角球",
    "th-TH": "เตะมุมรวมทีมเดี่ยว",
    "vi-VN": "Tổng Phạt Góc Đội Đơn",
    "ko-KR": "단일 팀 총 코너킥",
    "en-SG": "Single Team Total Corners",
    "id-ID": "Total Tendangan Sudut Tim Tunggal",
    "nl-NL": "Enkel team totaal hoekschoppen"
  },
  "team total corners over under": {
    "en-GB": "Single Team Total Corners — Over/Under",
    "ja-JP": "単独チームコーナー O/U",
    "it-IT": "Totale calci d'angolo squadra singola — O/U",
    "es-ES": "Total córners equipo único — Más/Menos",
    "ru-RU": "ИТ угловых — О/У",
    "fr-FR": "Total corners équipe unique — O/U",
    "de-DE": "Einzelnes Team — Ecken Über/Unter",
    "tr-TR": "Tek Takım Korner — Alt/Üst",
    "zh-CN": "单队角球大小",
    "th-TH": "เตะมุมทีมเดี่ยว — สูง/ต่ำ",
    "vi-VN": "Tài Xỉu Phạt Góc Đội Đơn",
    "ko-KR": "단일 팀 코너 — 오버/언더",
    "en-SG": "Single Team Total Corners — O/U",
    "id-ID": "Total Tendangan Sudut Tim Tunggal — O/U",
    "nl-NL": "Enkel team hoekschoppen — O/U"
  },
  "home corners over under": {
    "en-GB": "Home Corners — Over/Under",
    "ja-JP": "ホームコーナー O/U",
    "it-IT": "Calci d'Angolo Casa — O/U",
    "es-ES": "Córners local — Más/Menos",
    "ru-RU": "Угловые хозяев — О/У",
    "fr-FR": "Corners domicile — O/U",
    "de-DE": "Heim-Ecken — Über/Unter",
    "tr-TR": "Ev Korner — Alt/Üst",
    "zh-CN": "主队角球大小",
    "th-TH": "เตะมุมเจ้าบ้าน — สูง/ต่ำ",
    "vi-VN": "Tài Xỉu Phạt Góc Chủ Nhà",
    "ko-KR": "홈 코너 — 오버/언더",
    "en-SG": "Home Corners — O/U",
    "id-ID": "Tendangan Sudut Kandang — O/U",
    "nl-NL": "Thuis hoekschoppen — O/U"
  },
  "away corners over under": {
    "en-GB": "Away Corners — Over/Under",
    "ja-JP": "アウェイコーナー O/U",
    "it-IT": "Calci d'Angolo Trasferta — O/U",
    "es-ES": "Córners visitante — Más/Menos",
    "ru-RU": "Угловые гостей — О/У",
    "fr-FR": "Corners extérieur — O/U",
    "de-DE": "Auswärts-Ecken — Über/Unter",
    "tr-TR": "Deplasman Korner — Alt/Üst",
    "zh-CN": "客队角球大小",
    "th-TH": "เตะมุมทีมเยือน — สูง/ต่ำ",
    "vi-VN": "Tài Xỉu Phạt Góc Đội Khách",
    "ko-KR": "원정 코너 — 오버/언더",
    "en-SG": "Away Corners — O/U",
    "id-ID": "Tendangan Sudut Tandang — O/U",
    "nl-NL": "Uit hoekschoppen — O/U"
  },
  "total corners second half": {
    "en-GB": "Total Corners — Second Half",
    "ja-JP": "コーナー総数（後半）",
    "it-IT": "Totale Calci d'Angolo — 2° Tempo",
    "es-ES": "Total córners — 2ª parte",
    "ru-RU": "Тотал угловых — 2-й тайм",
    "fr-FR": "Total corners — 2e mi-temps",
    "de-DE": "Ecken gesamt — 2. HZ",
    "tr-TR": "Toplam Korner — İkinci Yarı",
    "zh-CN": "下半场角球总数",
    "th-TH": "เตะมุมรวม — ครึ่งหลัง",
    "vi-VN": "Tổng Phạt Góc — Hiệp 2",
    "ko-KR": "코너 합계 — 후반",
    "en-SG": "Total Corners — Second Half",
    "id-ID": "Total Tendangan Sudut — Babak 2",
    "nl-NL": "Totaal hoekschoppen — 2e helft"
  },
  "corner match bet": {
    "vi-VN": "Đội Thắng Phạt Góc",
    "en-GB": "Team to Win Most Corners",
    "ja-JP": "コーナー数勝者",
    "ko-KR": "코너킥 최다 팀",
    "zh-CN": "角球数获胜球队",
    "th-TH": "ทีมที่ได้เตะมุมมากกว่า",
    "id-ID": "Tim dengan tendangan sudut terbanyak",
    "fr-FR": "Équipe avec le plus de corners",
    "de-DE": "Team mit den meisten Ecken",
    "es-ES": "Equipo con más córners",
    "it-IT": "Squadra con più calci d'angolo",
    "tr-TR": "En çok korner alan takım",
    "ru-RU": "Команда с большим числом угловых",
    "nl-NL": "Team met de meeste hoekschoppen",
    "en-SG": "Team to Win Most Corners"
  },
  "corner winner": {
    "vi-VN": "Đội Thắng Phạt Góc",
    "en-GB": "Team to Win Most Corners",
    "ja-JP": "コーナー数勝者",
    "ko-KR": "코너킥 최다 팀",
    "zh-CN": "角球数获胜球队",
    "th-TH": "ทีมที่ได้เตะมุมมากกว่า",
    "id-ID": "Tim dengan tendangan sudut terbanyak",
    "fr-FR": "Équipe avec le plus de corners",
    "de-DE": "Team mit den meisten Ecken",
    "es-ES": "Equipo con más córners",
    "it-IT": "Squadra con più calci d'angolo",
    "tr-TR": "En çok korner alan takım",
    "ru-RU": "Команда с большим числом угловых",
    "nl-NL": "Team met de meeste hoekschoppen",
    "en-SG": "Team to Win Most Corners"
  },
  "corners 1x2": {
    "en-GB": "Corners 1X2",
    "ja-JP": "コーナー1X2",
    "it-IT": "Calci d'Angolo 1X2",
    "es-ES": "Córners 1X2",
    "ru-RU": "Угловые 1X2",
    "fr-FR": "Corners 1X2",
    "de-DE": "Ecken 1X2",
    "tr-TR": "Korner 1X2",
    "zh-CN": "角球1X2",
    "th-TH": "เตะมุม 1X2",
    "vi-VN": "Phạt góc 1X2",
    "ko-KR": "코너 1X2",
    "en-SG": "Corners 1X2",
    "id-ID": "Tendangan Sudut 1X2",
    "nl-NL": "Hoekschoppen 1X2"
  },
  "asian handicap corners": {
    "en-GB": "Asian Handicap Corners",
    "ja-JP": "アジアンハンディキャップ・コーナー",
    "it-IT": "Asian Handicap calci d'angolo",
    "es-ES": "Hándicap asiático de córners",
    "ru-RU": "Азиатская фора на угловые",
    "fr-FR": "Handicap asiatique corners",
    "de-DE": "Asian Handicap Ecken",
    "tr-TR": "Asya handikap korner",
    "zh-CN": "角球亚洲让球",
    "th-TH": "เอเชียนแฮนดิแคปเตะมุม",
    "vi-VN": "Cược Chấp Góc Châu Á",
    "ko-KR": "아시안 핸디캡 코너",
    "en-SG": "Asian Handicap Corners",
    "id-ID": "Asian handicap tendangan sudut",
    "nl-NL": "Asian handicap hoekschoppen"
  },
  "corners odd even": {
    "en-GB": "Corners — Odd/Even",
    "ja-JP": "コーナー奇偶",
    "it-IT": "Calci d'Angolo — Dispari/Pari",
    "es-ES": "Córners — Impar/Par",
    "ru-RU": "Угловые — нечёт/чёт",
    "fr-FR": "Corners — Impair/Pair",
    "de-DE": "Ecken — Gerade/Ungerade",
    "tr-TR": "Korner — Tek/Çift",
    "zh-CN": "角球单双",
    "th-TH": "เตะมุม — คี่/คู่",
    "vi-VN": "Phạt Góc — Lẻ/Chẵn",
    "ko-KR": "코너 — 홀/짝",
    "en-SG": "Corners — Odd/Even",
    "id-ID": "Tendangan Sudut — Ganjil/Genap",
    "nl-NL": "Hoekschoppen — oneven/even"
  },
  "total corners 3 way": {
    "en-GB": "Total Corners — 3-Way",
    "ja-JP": "コーナー総数（3択）",
    "it-IT": "Totale Calci d'Angolo — 3 Esiti",
    "es-ES": "Total córners — 3 vías",
    "ru-RU": "Тотал угловых — 3 исхода",
    "fr-FR": "Total corners — 3 issues",
    "de-DE": "Ecken gesamt — 3-Weg",
    "tr-TR": "Toplam Korner — 3 Yol",
    "zh-CN": "角球总数（三项）",
    "th-TH": "เตะมุมรวม — 3 ทาง",
    "vi-VN": "Tổng Phạt Góc — 3 Cửa",
    "ko-KR": "코너 합계 — 3웨이",
    "en-SG": "Total Corners — 3-Way",
    "id-ID": "Total Tendangan Sudut — 3 Arah",
    "nl-NL": "Totaal hoekschoppen — 3-way"
  },
  "scoring draw": {
    "en-GB": "Scoring Draw",
    "ja-JP": "両得点の引き分け",
    "it-IT": "Pareggio con Gol",
    "es-ES": "Empate con goles",
    "ru-RU": "Ничья с голами",
    "fr-FR": "Match nul avec buts",
    "de-DE": "Torreiches Unentschieden",
    "tr-TR": "Gollü Beraberlik",
    "zh-CN": "有进球平局",
    "th-TH": "เสมอมีสกอร์",
    "vi-VN": "Hòa Có Bàn",
    "ko-KR": "득점 무승부",
    "en-SG": "Scoring Draw",
    "id-ID": "Seri dengan Gol",
    "nl-NL": "Gelijkspel met doelpunten"
  },
  "goal line": {
    "en-GB": "Goal Line",
    "ja-JP": "ゴールライン",
    "it-IT": "Linea Gol",
    "es-ES": "Línea de goles",
    "ru-RU": "Голевая линия",
    "fr-FR": "Ligne de buts",
    "de-DE": "Torlinie",
    "tr-TR": "Gol Çizgisi",
    "zh-CN": "进球线",
    "th-TH": "เส้นโลกสกอร์",
    "vi-VN": "Kèo Tổng Bàn",
    "ko-KR": "골 라인",
    "en-SG": "Goal Line",
    "id-ID": "Garis Gol",
    "nl-NL": "Doellijn"
  },
  "goal line first half": {
    "en-GB": "Goal Line — First Half",
    "ja-JP": "ゴールライン（前半）",
    "it-IT": "Linea Gol — 1° Tempo",
    "es-ES": "Línea de goles — 1ª parte",
    "ru-RU": "Голевая линия — 1-й тайм",
    "fr-FR": "Ligne de buts — 1re mi-temps",
    "de-DE": "Torlinie — 1. HZ",
    "tr-TR": "Gol Çizgisi — İlk Yarı",
    "zh-CN": "上半场进球线",
    "th-TH": "เส้นสกอร์ — ครึ่งแรก",
    "vi-VN": "Kèo Tổng Bàn — Hiệp 1",
    "ko-KR": "골 라인 — 전반",
    "en-SG": "Goal Line — First Half",
    "id-ID": "Garis Gol — Babak 1",
    "nl-NL": "Doellijn — 1e helft"
  },
  "goal line second half": {
    "en-GB": "Goal Line — Second Half",
    "ja-JP": "ゴールライン（後半）",
    "it-IT": "Linea Gol — 2° Tempo",
    "es-ES": "Línea de goles — 2ª parte",
    "ru-RU": "Голевая линия — 2-й тайм",
    "fr-FR": "Ligne de buts — 2e mi-temps",
    "de-DE": "Torlinie — 2. HZ",
    "tr-TR": "Gol Çizgisi — İkinci Yarı",
    "zh-CN": "下半场进球线",
    "th-TH": "เส้นสกอร์ — ครึ่งหลัง",
    "vi-VN": "Kèo Tổng Bàn — Hiệp 2",
    "ko-KR": "골 라인 — 후반",
    "en-SG": "Goal Line — Second Half",
    "id-ID": "Garis Gol — Babak 2",
    "nl-NL": "Doellijn — 2e helft"
  },
  "goal line full time": {
    "en-GB": "Goal Line — Full Time",
    "ja-JP": "ゴールライン（フルタイム）",
    "it-IT": "Linea Gol — Full Time",
    "es-ES": "Línea de goles — tiempo completo",
    "ru-RU": "Голевая линия — основное время",
    "fr-FR": "Ligne de buts — temps réglementaire",
    "de-DE": "Torlinie — reguläre Spielzeit",
    "tr-TR": "Gol Çizgisi — Tam Süre",
    "zh-CN": "全场进球线",
    "th-TH": "เส้นสกอร์ — เต็มเวลา",
    "vi-VN": "Kèo Tổng Bàn — Cả Trận",
    "ko-KR": "골 라인 — 풀타임",
    "en-SG": "Goal Line — Full Time",
    "id-ID": "Garis Gol — Waktu Penuh",
    "nl-NL": "Doellijn — reguliere speeltijd"
  },
  "result/total goals": {
    "en-GB": "Result / Total Goals",
    "ja-JP": "結果 / 総得点",
    "it-IT": "Risultato / Totale Gol",
    "es-ES": "Resultado / Total goles",
    "ru-RU": "Исход / тотал голов",
    "fr-FR": "Résultat / Total buts",
    "de-DE": "Ergebnis / Tore gesamt",
    "tr-TR": "Sonuç / Toplam Gol",
    "zh-CN": "赛果 / 总进球",
    "th-TH": "ผล/สกอร์รวม",
    "vi-VN": "Kết Quả / Tổng Bàn",
    "ko-KR": "결과 / 총골",
    "en-SG": "Result / Total Goals",
    "id-ID": "Hasil / Total Gol",
    "nl-NL": "Uitslag / totaal doelpunten"
  },
  "result/total goals first half": {
    "en-GB": "Result / Total Goals — First Half",
    "ja-JP": "結果/総得点（前半）",
    "it-IT": "Risultato/Totale Gol — 1° Tempo",
    "es-ES": "Resultado/Total goles — 1ª parte",
    "ru-RU": "Исход/тотал — 1-й тайм",
    "fr-FR": "Résultat/Total buts — 1re mi-temps",
    "de-DE": "Ergebnis/Tore — 1. HZ",
    "tr-TR": "Sonuç/Toplam Gol — İlk Yarı",
    "zh-CN": "半场赛果/总进球",
    "th-TH": "ผล/สกอร์ — ครึ่งแรก",
    "vi-VN": "Kết Quả/Tổng Bàn — Hiệp 1",
    "ko-KR": "결과/총골 — 전반",
    "en-SG": "Result / Total Goals — First Half",
    "id-ID": "Hasil/Total Gol — Babak 1",
    "nl-NL": "Uitslag/totaal — 1e helft"
  },
  "result/total goals second half": {
    "en-GB": "Result / Total Goals — Second Half",
    "ja-JP": "結果/総得点（後半）",
    "it-IT": "Risultato/Totale Gol — 2° Tempo",
    "es-ES": "Resultado/Total goles — 2ª parte",
    "ru-RU": "Исход/тотал — 2-й тайм",
    "fr-FR": "Résultat/Total buts — 2e mi-temps",
    "de-DE": "Ergebnis/Tore — 2. HZ",
    "tr-TR": "Sonuç/Toplam Gol — İkinci Yarı",
    "zh-CN": "下半场赛果/总进球",
    "th-TH": "ผล/สกอร์ — ครึ่งหลัง",
    "vi-VN": "Kết Quả/Tổng Bàn — Hiệp 2",
    "ko-KR": "결과/총골 — 후반",
    "en-SG": "Result / Total Goals — Second Half",
    "id-ID": "Hasil/Total Gol — Babak 2",
    "nl-NL": "Uitslag/totaal — 2e helft"
  },
  "to score in both halves by teams": {
    "en-GB": "To Score In Both Halves (By Teams)",
    "ja-JP": "両ハーフで両チーム得点",
    "it-IT": "Segnare in Entrambi i Tempi (per squadre)",
    "es-ES": "Marcar en ambas partes (por equipos)",
    "ru-RU": "Забить в обоих таймах (по командам)",
    "fr-FR": "Marquer dans les deux mi-temps (par équipe)",
    "de-DE": "In beiden Halbzeiten treffen (Teams)",
    "tr-TR": "Her İki Yarıda Gol (Takımlar)",
    "zh-CN": "两队上下半场均进球",
    "th-TH": "ยิงทั้งสองครึ่ง (ทีม)",
    "vi-VN": "Hai Đội Ghi Bàn Cả Hai Hiệp",
    "ko-KR": "양팀 양 하프 득점",
    "en-SG": "To Score In Both Halves (By Teams)",
    "id-ID": "Gol di Kedua Babak (Per Tim)",
    "nl-NL": "Scoren in beide helften (per team)"
  },
  "both teams to score second half": {
    "en-GB": "Both Teams To Score — Second Half",
    "ja-JP": "両チーム得点（後半）",
    "it-IT": "Entrambe Segnano — 2° Tempo",
    "es-ES": "Ambos marcan — 2ª parte",
    "ru-RU": "Обе забьют — 2-й тайм",
    "fr-FR": "Les deux marquent — 2e mi-temps",
    "de-DE": "Beide treffen — 2. HZ",
    "tr-TR": "KG Var — İkinci Yarı",
    "zh-CN": "双方进球（下半场）",
    "th-TH": "คู่คี่ทั้งคู่ — ครึ่งหลัง",
    "vi-VN": "Cả Hai Đội Ghi Bàn — Hiệp 2",
    "ko-KR": "양팀 득점 — 후반",
    "en-SG": "Both Teams To Score — Second Half",
    "id-ID": "Kedua Tim Gol — Babak 2",
    "nl-NL": "Beide teams scoren — 2e helft"
  },
  "1x2 15 minutes": {
    "en-GB": "1X2 — 15 Minutes",
    "ja-JP": "1X2（15分）",
    "it-IT": "1X2 — 15 Minuti",
    "es-ES": "1X2 — 15 minutos",
    "ru-RU": "1X2 — 15 минут",
    "fr-FR": "1X2 — 15 minutes",
    "de-DE": "1X2 — 15 Minuten",
    "tr-TR": "1X2 — 15 Dakika",
    "zh-CN": "1X2（15分钟）",
    "th-TH": "1X2 — 15 นาที",
    "vi-VN": "1X2 — 15 phút",
    "ko-KR": "1X2 — 15분",
    "en-SG": "1X2 — 15 Minutes",
    "id-ID": "1X2 — 15 Menit",
    "nl-NL": "1X2 — 15 minuten"
  },
  "run line": {
    "en-GB": "Run Line",
    "ja-JP": "ランライン",
    "it-IT": "Run Line",
    "es-ES": "Línea de carreras",
    "ru-RU": "Фора с линией",
    "fr-FR": "Ligne de course",
    "de-DE": "Run Line",
    "tr-TR": "Run Line",
    "zh-CN": "让分盘",
    "th-TH": "รันไลน์",
    "vi-VN": "Kèo Chấp",
    "ko-KR": "런라인",
    "en-SG": "Run Line",
    "id-ID": "Run Line",
    "nl-NL": "Run Line"
  },
  "puck line": {
    "en-GB": "Puck Line",
    "ja-JP": "パックライン",
    "it-IT": "Puck Line",
    "es-ES": "Línea de puck",
    "ru-RU": "Пак-лайн",
    "fr-FR": "Ligne de puck",
    "de-DE": "Puck Line",
    "tr-TR": "Puck Line",
    "zh-CN": "冰球让分",
    "th-TH": "PUCK LINE",
    "vi-VN": "Kèo Chấp",
    "ko-KR": "퍽 라인",
    "en-SG": "Puck Line",
    "id-ID": "Puck Line",
    "nl-NL": "Puck Line"
  },
  "point spread": {
    "en-GB": "Point Spread",
    "ja-JP": "ポイントスプレッド",
    "it-IT": "Svantaggio punti",
    "es-ES": "Hándicap de puntos",
    "ru-RU": "Фора (очки)",
    "fr-FR": "Écart de points",
    "de-DE": "Punkte-Handicap",
    "tr-TR": "Sayı Handikapı",
    "zh-CN": "让分",
    "th-TH": "แฮนดิแคปคะแนน",
    "vi-VN": "Kèo Chấp",
    "ko-KR": "포인트 스프레드",
    "en-SG": "Point Spread",
    "id-ID": "Handicap Poin",
    "nl-NL": "Puntenspread"
  },
  spread: {
    "en-GB": "Spread",
    "ja-JP": "スプレッド",
    "it-IT": "Spread",
    "es-ES": "Hándicap",
    "ru-RU": "Фора",
    "fr-FR": "Écart",
    "de-DE": "Spread",
    "tr-TR": "Handikap",
    "zh-CN": "让分",
    "th-TH": "สเปรด",
    "vi-VN": "Kèo Chấp",
    "ko-KR": "스프레드",
    "en-SG": "Spread",
    "id-ID": "Spread",
    "nl-NL": "Spread"
  },
  "total goals": {
    "en-GB": "Total Goals",
    "ja-JP": "総得点",
    "it-IT": "Totale Gol",
    "es-ES": "Total de goles",
    "ru-RU": "Тотал голов",
    "fr-FR": "Total de buts",
    "de-DE": "Tore gesamt",
    "tr-TR": "Toplam Gol",
    "zh-CN": "总进球",
    "th-TH": "ประตูรวม",
    "vi-VN": "Tổng Bàn Thắng",
    "ko-KR": "총 득점",
    "en-SG": "Total Goals",
    "id-ID": "Total Gol",
    "nl-NL": "Totaal doelpunten"
  },
  "team total goals": {
    "en-GB": "Single Team Total Goals",
    "ja-JP": "単独チーム総ゴール",
    "it-IT": "Totale gol squadra singola",
    "es-ES": "Total de goles de un solo equipo",
    "ru-RU": "Индивидуальный тотал голов команды",
    "fr-FR": "Total buts équipe unique",
    "de-DE": "Einzelnes Team — Gesamttore",
    "tr-TR": "Tek Takım Toplam Gol",
    "zh-CN": "单队总进球",
    "th-TH": "ประตูรวมทีมเดี่ยว",
    "vi-VN": "Tổng Bàn Thắng Đội Đơn",
    "ko-KR": "단일 팀 총득점",
    "en-SG": "Single Team Total Goals",
    "id-ID": "Total Gol Tim Tunggal",
    "nl-NL": "Enkel team totaal doelpunten"
  },
  "total runs": {
    "en-GB": "Total Runs",
    "ja-JP": "総得点（ラン）",
    "it-IT": "Totale Corse",
    "es-ES": "Total de carreras",
    "ru-RU": "Тотал ранов",
    "fr-FR": "Total points (MLB)",
    "de-DE": "Runs gesamt",
    "tr-TR": "Toplam Run",
    "zh-CN": "总得分",
    "th-TH": "รันสกอร์รวม",
    "vi-VN": "Tài/Xỉu Tổng",
    "ko-KR": "총 득점",
    "en-SG": "Total Runs",
    "id-ID": "Total Run",
    "nl-NL": "Totaal runs"
  },
  "total games": {
    "en-GB": "Total Games",
    "ja-JP": "総ゲーム数",
    "it-IT": "Totale giochi",
    "es-ES": "Total de juegos",
    "ru-RU": "Тотал геймов",
    "fr-FR": "Total Jeux",
    "de-DE": "Gesamtzahl der Spiele",
    "tr-TR": "Toplam Oyun",
    "zh-CN": "总局数",
    "th-TH": "จำนวนเกมรวม",
    "vi-VN": "Tài/Xỉu Tổng",
    "ko-KR": "총 게임 수",
    "en-SG": "Total Games",
    "id-ID": "Total Game",
    "nl-NL": "Totaal Games"
  },
  "total sets": {
    "en-GB": "Total Sets",
    "ja-JP": "総セット数",
    "it-IT": "Totale set",
    "es-ES": "Total de sets",
    "ru-RU": "Тотал сетов",
    "fr-FR": "Total Sets",
    "de-DE": "Gesamtzahl der Sätze",
    "tr-TR": "Toplam Set",
    "zh-CN": "总盘数",
    "th-TH": "จำนวนเซตรวม",
    "vi-VN": "Tổng Số Set",
    "ko-KR": "총 세트 수",
    "en-SG": "Total Sets",
    "id-ID": "Total Set",
    "nl-NL": "Totaal Sets"
  },
  "set handicap": {
    "en-GB": "Set Handicap",
    "ja-JP": "セットハンディキャップ",
    "it-IT": "Handicap Set",
    "es-ES": "Hándicap de sets",
    "ru-RU": "Фора по сетам",
    "fr-FR": "Handicap Sets",
    "de-DE": "Satz-Handicap",
    "tr-TR": "Set Handikapı",
    "zh-CN": "盘让分",
    "th-TH": "แฮนดิแคปเซ็ต",
    "vi-VN": "Chấp Set",
    "ko-KR": "세트 핸디캡",
    "en-SG": "Set Handicap",
    "id-ID": "Handicap Set",
    "nl-NL": "Set handicap"
  },
  "set moneyline": {
    "en-GB": "Set Winner",
    "ja-JP": "セット勝者",
    "it-IT": "Vincente del set",
    "es-ES": "Ganador del set",
    "ru-RU": "Победитель сета",
    "fr-FR": "Vainqueur du set",
    "de-DE": "Satzsieger",
    "tr-TR": "Set Kazananı",
    "zh-CN": "盘胜者",
    "th-TH": "ผู้ชนะเซต",
    "vi-VN": "Kèo Thắng Set",
    "ko-KR": "세트 승자",
    "en-SG": "Set Winner",
    "id-ID": "Pemenang Set",
    "nl-NL": "Setwinnaar"
  },
  "set winner": {
    "en-GB": "Set Winner",
    "ja-JP": "セット勝者",
    "it-IT": "Vincente del set",
    "es-ES": "Ganador del set",
    "ru-RU": "Победитель сета",
    "fr-FR": "Vainqueur du set",
    "de-DE": "Satzsieger",
    "tr-TR": "Set Kazananı",
    "zh-CN": "盘胜者",
    "th-TH": "ผู้ชนะเซต",
    "vi-VN": "Kèo Thắng Set",
    "ko-KR": "세트 승자",
    "en-SG": "Set Winner",
    "id-ID": "Pemenang Set",
    "nl-NL": "Setwinnaar"
  },
  "game handicap": {
    "en-GB": "Game Handicap",
    "ja-JP": "ゲームハンディキャップ",
    "it-IT": "Handicap Game",
    "es-ES": "Hándicap de juegos",
    "ru-RU": "Фора по геймам",
    "fr-FR": "Handicap Jeux",
    "de-DE": "Spiel-Handicap",
    "tr-TR": "Oyun Handikapı",
    "zh-CN": "局让分",
    "th-TH": "แฮนดิแคปเกม",
    "vi-VN": "Cược Chấp Game",
    "ko-KR": "게임 핸디캡",
    "en-SG": "Game Handicap",
    "id-ID": "Handicap Game",
    "nl-NL": "Game handicap"
  },
  "player points": {
    "en-GB": "Player Points",
    "ja-JP": "選手ポイント",
    "it-IT": "Punti Giocatore",
    "es-ES": "Puntos del jugador",
    "ru-RU": "Очки игрока",
    "fr-FR": "Points joueur",
    "de-DE": "Spieler-Punkte",
    "tr-TR": "Oyuncu Sayı",
    "zh-CN": "球员得分",
    "th-TH": "คะแนนผู้เล่น",
    "vi-VN": "Điểm Cầu Thủ",
    "ko-KR": "선수 득점",
    "en-SG": "Player Points",
    "id-ID": "Poin Pemain",
    "nl-NL": "Spelerpunten"
  },
  "player rebounds": {
    "en-GB": "Player Rebounds",
    "ja-JP": "選手リバウンド",
    "it-IT": "Rimbalzi Giocatore",
    "es-ES": "Rebotes del jugador",
    "ru-RU": "Подборы игрока",
    "fr-FR": "Rebonds joueur",
    "de-DE": "Spieler-Rebounds",
    "tr-TR": "Oyuncu Ribaund",
    "zh-CN": "球员篮板",
    "th-TH": "รีบาวน์ผู้เล่น",
    "vi-VN": "Rebounds Cầu Thủ",
    "ko-KR": "선수 리바운드",
    "en-SG": "Player Rebounds",
    "id-ID": "Rebound Pemain",
    "nl-NL": "Speler rebounds"
  },
  "player assists": {
    "en-GB": "Player Assists",
    "ja-JP": "選手アシスト",
    "it-IT": "Assist Giocatore",
    "es-ES": "Asistencias del jugador",
    "ru-RU": "Передачи игрока",
    "fr-FR": "Passes décisives joueur",
    "de-DE": "Spieler-Assists",
    "tr-TR": "Oyuncu Asist",
    "zh-CN": "球员助攻",
    "th-TH": "แอสซิสต์ผู้เล่น",
    "vi-VN": "Assists Cầu Thủ",
    "ko-KR": "선수 어시스트",
    "en-SG": "Player Assists",
    "id-ID": "Assist Pemain",
    "nl-NL": "Speler assists"
  },
  "player made threes": {
    "en-GB": "Player Made Threes",
    "ja-JP": "選手3ポイント成功数",
    "it-IT": "Triple segnate dal giocatore",
    "es-ES": "Triples anotados (jugador)",
    "ru-RU": "Трёхочковые игрока",
    "fr-FR": "Paniers à 3 points (joueur)",
    "de-DE": "Spieler-Dreier (gemacht)",
    "tr-TR": "Oyuncu üçlük (sayı)",
    "zh-CN": "球员三分球命中",
    "th-TH": "ผู้เล่นทำคะแนนสามคะแนน",
    "vi-VN": "3 Điểm Thành Công Cầu Thủ",
    "ko-KR": "선수 3점슛 성공",
    "en-SG": "Player Made Threes",
    "id-ID": "Tembakan tiga poin pemain",
    "nl-NL": "Speler gemaakte driepunters"
  }
};

const MARKET_ALIASES = {
  "1x2": "1x2",
  "1 x 2": "1x2",
  "match odds": "1x2",
  "full time result": "1x2",
  "fulltime result": "1x2",
  "full-time result": "1x2",

  h2h: "moneyline",
  moneyline: "moneyline",
  "moneyline 3 way": "moneyline 3 way",
  /* Some feeds omit the space before `3` after normalizing underscores. */
  "moneyline 3way": "moneyline 3 way",
  "3 way moneyline": "moneyline 3 way",
  "ml 3 way": "moneyline 3 way",
  "3 way winner": "moneyline 3 way",
  "3-way winner": "moneyline 3 way",

  "match winner": "match winner",
  winner: "winner",

  "map moneyline": "map moneyline",
  "map winner": "map winner",
  "set winner": "set winner",

  handicap: "handicap",
  spread: "spread",
  "point spread": "point spread",
  "game spread": "spread",

  "asian handicap": "asian handicap",
  "ah": "asian handicap",

  overunder: "over/under",
  "over/under": "over/under",
  totals: "totals",
  total: "totals",
  "total goals": "total goals",
  "goals total": "total goals",
  "total rounds": "totals",
  "rounds total": "totals",
  "total points": "total points",
  "totals points": "totals",
  "team totals": "team total",

  btts: "btts",
  "both teams to score": "btts",
  "both teams score": "btts",
  "both teams to score?": "btts",

  dnb: "draw no bet",
  "draw no bet": "draw no bet",
  "drawnobet": "draw no bet",
  "draw no bet 1st half": "draw no bet first half",
  "draw no bet first half": "draw no bet first half",
  "draw no bet 2nd half": "draw no bet second half",
  "draw no bet second half": "draw no bet second half",
  "double chance": "double chance",
  dc: "double chance",
  "first half winner": "first half winner",
  "second half winner": "second half winner",
  "1st half winner": "first half winner",
  "2nd half winner": "second half winner",
  "first 10 min winner": "first 10 min winner",
  "first 10 minutes winner": "first 10 min winner",
  "first 15 min winner": "first 15 min winner",
  "first 15 minutes winner": "first 15 min winner",
  "first 5 min winner": "first 5 min winner",
  "first 5 minutes winner": "first 5 min winner",
  "1x2 30 minutes": "1x2 30 minutes",
  "1x2 30 mins": "1x2 30 minutes",
  "1x2 60 minutes": "1x2 60 minutes",
  "1x2 60 mins": "1x2 60 minutes",
  "to win either half": "to win either half",
  "win either half": "to win either half",
  "win both halves": "win both halves",
  "home win both halves": "home win both halves",
  "away win both halves": "away win both halves",
  "team to score first": "team to score first",
  "team to score last": "team to score last",
  "first team to score": "first team to score",
  "first team to score 3 way 1st half": "first team to score 3 way first half",
  "first team to score 3-way 1st half": "first team to score 3 way first half",
  "first team to score 3 way first half": "first team to score 3 way first half",
  "home team score a goal": "home team score a goal",
  "away team score a goal": "away team score a goal",
  "home team will score in both halves": "home team will score in both halves",
  "away team will score in both halves": "away team will score in both halves",
  "to score in both halves": "to score in both halves",
  "both teams to score 1st half": "both teams to score first half",
  "both teams to score first half": "both teams to score first half",
  "btts 1st half": "both teams to score first half",
  "total goals/both teams to score": "total goals/both teams to score",
  "total goals / both teams to score": "total goals/both teams to score",
  "total goals and both teams to score": "total goals/both teams to score",
  "win to nil": "win to nil",
  "winto nil": "win to nil",
  "win to nil home": "win to nil home",
  "win to nil away": "win to nil away",
  "team total": "team total",
  "team total goals": "team total goals",
  // Period embedded in provider market name (period/scope shown once via splitMarketPeriodForTranslation).
  "first half total goals": "total goals",
  "second half total goals": "total goals",
  "full time total goals": "total goals",
  "full time team total goals": "team total goals",
  "full time point spread": "point spread",
  "full time asian handicap": "asian handicap",
  "full time totals": "totals",
  "full time over/under": "over/under",
  "full time over under": "over/under",
  "full time total corners": "total corners",
  "full time corners over/under": "corners over under",
  "full time corners over under": "corners over under",
  "first half team total goals": "team total goals",
  "second half team total goals": "team total goals",
  "first half point spread": "point spread",
  "second half point spread": "point spread",
  "first half asian handicap": "asian handicap",
  "second half asian handicap": "asian handicap",
  "first half totals": "totals",
  "second half totals": "totals",
  "first half over/under": "over/under",
  "second half over/under": "over/under",
  "first half over under": "over/under",
  "second half over under": "over/under",
  "first half total corners": "total corners",
  "second half total corners": "total corners",
  "first half corners over/under": "corners over under",
  "second half corners over/under": "corners over under",
  "first half corners over under": "corners over under",
  "second half corners over under": "corners over under",
  "first set total games": "total games",
  "second set total games": "total games",
  "first set moneyline": "set moneyline",
  "second set moneyline": "set moneyline",
  "first quarter moneyline": "moneyline",
  "second quarter moneyline": "moneyline",
  "1st quarter moneyline": "moneyline",
  "2nd quarter moneyline": "moneyline",
  "first half moneyline": "moneyline",
  "second half moneyline": "moneyline",
  "1st half moneyline": "moneyline",
  "2nd half moneyline": "moneyline",
  "first quarter team total": "team total",
  "second quarter team total": "team total",
  "1st quarter team total": "team total",
  "2nd quarter team total": "team total",
  "first half team total": "team total",
  "second half team total": "team total",
  "1st half team total": "team total",
  "2nd half team total": "team total",
  "team total points first quarter": "team total",
  "team total points second quarter": "team total",
  "winner at 60 minutes": "moneyline",
  "moneyline at 60 minutes": "moneyline",
  "3-way winner at 60 minutes": "moneyline 3 way",
  "3 way winner at 60 minutes": "moneyline 3 way",
  "winner 60 minutes": "moneyline",
  "moneyline 60 minutes": "moneyline",
  "1x2 at 60 minutes": "1x2 60 minutes",
  "total runs": "total runs",
  "total games": "total games",
  "games total": "total games",
  "total sets": "total sets",
  "sets total": "total sets",
  "games handicap": "game handicap",
  "set spread": "set handicap",
  "set handicap": "set handicap",
  "game handicap": "game handicap",
  "player points": "player points",
  "player rebounds": "player rebounds",
  "player assists": "player assists",
  "run line": "run line",
  "puck line": "puck line",
  "team total o/u": "team total over under",
  "team total over under": "team total over under",
  "home team exact goals number": "home team exact goals number",
  "away team exact goals number": "away team exact goals number",
  "exact goals number": "exact goals number",
  "1st half exact goals number": "first half exact goals number",
  "first half exact goals number": "first half exact goals number",
  "total corners": "total corners",
  "total corners 1st half": "total corners first half",
  "total corners first half": "total corners first half",
  "corners over under": "corners over under",
  "corners o/u": "corners over under",
  "corners handicap": "corners handicap",
  "asian handicap 1st half": "asian handicap first half",
  "asian handicap first half": "asian handicap first half",
  "asian handicap 2nd half": "asian handicap second half",
  "asian handicap second half": "asian handicap second half",
  "european handicap": "european handicap",
  "eur handicap": "european handicap",
  "european handicap 1st half": "european handicap first half",
  "european handicap first half": "european handicap first half",
  "european handicap 2nd half": "european handicap second half",
  "european handicap second half": "european handicap second half",
  "handicap result": "handicap result",
  "handicap result 1st half": "handicap result first half",
  "handicap result first half": "handicap result first half",
  "handicap result 2nd half": "handicap result second half",
  "handicap result second half": "handicap result second half",
  "odd/even": "odd even",
  "odd even": "odd even",
  "home odd/even": "home odd even",
  "home odd even": "home odd even",
  "away odd/even": "away odd even",
  "away odd even": "away odd even",

  "race to 2": "race to 2",
  "race to 3": "race to 3",
  "race to 5": "race to 5",
  "race to 7": "race to 7",
  "race to 2 goals": "race to 2 goals",
  "race to 3 goals": "race to 3 goals",
  "race to 5 corners": "race to 5 corners",
  "race to 3 corners": "race to 3 corners",
  "race to 5 shots on target": "race to 5 shots on target",
  "correct score": "correct score",
  "correct score 1st half": "correct score first half",
  "correct score first half": "correct score first half",
  "correct score 2nd half": "correct score second half",
  "correct score second half": "correct score second half",
  "winning margin": "winning margin",
  "exact corners": "exact corners",
  "exact cards": "exact cards",
  "2nd half exact goals number": "second half exact goals number",
  "second half exact goals number": "second half exact goals number",
  "to qualify": "to qualify",
  qualify: "qualify",
  "team to qualify": "team to qualify",
  "lift the trophy": "lift the trophy",
  "winner outright": "winner outright",
  "outright winner": "outright winner",
  "tournament winner": "tournament winner",
  "first goal method": "first goal method",
  "clean sheet": "clean sheet",
  "clean sheet home": "clean sheet home",
  "clean sheet away": "clean sheet away",
  "home clean sheet": "home clean sheet",
  "away clean sheet": "away clean sheet",
  "to keep a clean sheet": "to keep a clean sheet",
  "cards over/under": "cards over under",
  "cards o/u": "cards over under",
  "cards over under": "cards over under",
  "total cards": "total cards",
  "total cards 1st half": "total cards first half",
  "total cards first half": "total cards first half",
  "total cards 2nd half": "total cards second half",
  "total cards second half": "total cards second half",
  "cards handicap": "cards handicap",
  "cards asian handicap": "cards asian handicap",
  "team cards": "team cards",
  "home team cards": "home team cards",
  "away team cards": "away team cards",
  "yellow cards": "yellow cards",
  "red card": "red card",
  "penalty awarded": "penalty awarded",
  "booking points": "booking points",
  "team total corners": "team total corners",
  "team total corners o/u": "team total corners over under",
  "team total corners over/under": "team total corners over under",
  "team total corners over under": "team total corners over under",
  "home corners over/under": "home corners over under",
  "home corners over under": "home corners over under",
  "away corners over/under": "away corners over under",
  "away corners over under": "away corners over under",
  "total corners 2nd half": "total corners second half",
  "total corners second half": "total corners second half",
  "corners 1x2": "corners 1x2",
  "corners 1X2": "corners 1x2",
  "corner match bet": "corner match bet",
  corner_match_bet: "corner match bet",
  "match bet corners": "corner match bet",
  "corner winner": "corner winner",
  "corners winner": "corner winner",
  "asian handicap corners": "asian handicap corners",
  asian_handicap_corners: "asian handicap corners",
  "corners asian handicap": "asian handicap corners",
  "set moneyline": "set moneyline",
  set_moneyline: "set moneyline",
  "player made threes": "player made threes",
  player_made_threes: "player made threes",
  "player 3 pointers": "player made threes",
  "player 3-pointers": "player made threes",
  "corners odd/even": "corners odd even",
  "corners odd even": "corners odd even",
  "total corners 3 way": "total corners 3 way",
  "total corners 3-way": "total corners 3 way",
  "scoring draw": "scoring draw",
  "goal line": "goal line",
  "goal line 1st half": "goal line first half",
  "goal line first half": "goal line first half",
  "goal line 2nd half": "goal line second half",
  "goal line second half": "goal line second half",
  "goal line full time": "goal line full time",
  "result/total goals": "result/total goals",
  "result / total goals": "result/total goals",
  "result/total goals 1st half": "result/total goals first half",
  "result/total goals first half": "result/total goals first half",
  "result/total goals 2nd half": "result/total goals second half",
  "result/total goals second half": "result/total goals second half",
  "to score in both halves by teams": "to score in both halves by teams",
  "both teams to score 2nd half": "both teams to score second half",
  "both teams to score second half": "both teams to score second half",
  "1x2 15 minutes": "1x2 15 minutes",
  "1x2 15 mins": "1x2 15 minutes",
  "dnb 1h": "draw no bet first half",
  "dnb 2h": "draw no bet second half",
  "ah 1h": "asian handicap first half",
  "ah 2h": "asian handicap second half",
  "eh 1h": "european handicap first half",
  "eh 2h": "european handicap second half",
  "btts 1h": "both teams to score first half",
  "btts 2h": "both teams to score second half",
  "ht draw no bet": "draw no bet first half",
  "ft goal line": "goal line full time",

  /* Sharp / provider phrases → canonical keys (lookup keys use normalized spacing). */
  "home team total": "team total",
  "away team total": "team total",
  "team total runs": "team total",
  "team total points": "team total",
  "team corners": "team total corners",
  "game total": "totals",
  "game total points": "total points",
  "game total runs": "total runs",
  "match total": "totals",
  "match total goals": "total goals",
  "match total points": "total points",
  "match total runs": "total runs",
  "full time total": "totals",
  "full time spread": "point spread",
  "full time run line": "run line",

  "1st half team total goals": "team total goals",
  "2nd half team total goals": "team total goals",
  "1st half total goals": "total goals",
  "2nd half total goals": "total goals",
  "1st half total points": "total points",
  "2nd half total points": "total points",
  "1st half total runs": "total runs",
  "2nd half total runs": "total runs",
  "1st half point spread": "point spread",
  "2nd half point spread": "point spread",
  "1st half run line": "run line",
  "2nd half run line": "run line",
  "1st half total corners": "total corners",
  "2nd half total corners": "total corners",
  "1st half corners over/under": "corners over under",
  "2nd half corners over/under": "corners over under",
  "1st half corners over under": "corners over under",
  "2nd half corners over under": "corners over under",

  "first half team total runs": "team total",
  "second half team total runs": "team total",
  "first half team total points": "team total",
  "second half team total points": "team total",
  "first half total runs": "total runs",
  "second half total runs": "total runs",
  "first half total points": "total points",
  "second half total points": "total points",
  "first half puck line": "puck line",
  "second half puck line": "puck line",

  "3rd quarter": "3rd quarter",
  "third quarter": "3rd quarter",
  "q3": "3rd quarter",
  "4th quarter": "4th quarter",
  "fourth quarter": "4th quarter",
  "q4": "4th quarter",

  "1st period": "1st period",
  "first period": "1st period",
  "p1": "1st period",
  "2nd period": "2nd period",
  "second period": "2nd period",
  "p2": "2nd period",
  "3rd period": "3rd period",
  "third period": "3rd period",
  "p3": "3rd period",

  future: "future",
  futures: "future",
  outright: "future",

  "game prop": "game prop"
};

const SPORT_TRANSLATIONS_MAP = buildNormalizedMap(SPORT_TRANSLATIONS);
const MARKET_TRANSLATIONS_MAP = buildNormalizedMap(MARKET_TRANSLATIONS);
const SELECTION_TRANSLATIONS_MAP = buildNormalizedMap(SELECTION_TRANSLATIONS);
const LEAGUE_TRANSLATIONS_MAP = buildNormalizedLeagueMap(LEAGUE_TRANSLATIONS);

function peekTranslatedMapValue(map, key, lang, fallback) {
  const normalized = normalizeLookupKey(key);
  const compact = compactLookupKey(key);
  const found = map[normalized] || map[compact];
  if (!found) return null;
  return found[lang] || found[ANALYSIS_DEFAULT_LANG] || fallback;
}

function getTranslatedMapValue(map, key, lang, fallback, type = "value") {
  const normalized = normalizeLookupKey(key);
  const compact = compactLookupKey(key);
  const found = map[normalized] || map[compact];

  if (!found) {
    warnUnmapped(type, key, normalized);
    return fallback;
  }

  return found[lang] || found[ANALYSIS_DEFAULT_LANG] || fallback;
}

function getLeagueTranslatedMapValue(map, key, lang, fallback, type = "league") {
  const normalized = normalizeLeagueLookupKey(key);
  const compact = compactLeagueLookupKey(key);
  const found = map[normalized] || map[compact];

  if (!found) {
    warnUnmapped(type, key, normalized);
    return fallback;
  }

  return found[lang] || found[ANALYSIS_DEFAULT_LANG] || fallback;
}

function getSelectionTokenTranslation(token, lang) {
  const translated =
    SELECTION_TRANSLATIONS[token]?.[lang] ||
    SELECTION_TRANSLATIONS[token]?.[ANALYSIS_DEFAULT_LANG];

  return translated || token;
}

function getCountryTranslation(countryKey, lang) {
  const found = COUNTRY_NAME_TRANSLATIONS[countryKey];
  if (!found) return countryKey;
  return found[lang] || found[ANALYSIS_DEFAULT_LANG] || countryKey;
}

function getLeaguePatternTranslation(patternKey, lang) {
  const found = LEAGUE_PATTERN_TRANSLATIONS[patternKey];
  if (!found) return patternKey;
  return found[lang] || found[ANALYSIS_DEFAULT_LANG] || patternKey;
}

function buildPatternLeagueLabel(countryKey, patternKey, lang) {
  const countryLabel = getCountryTranslation(countryKey, lang);
  const patternLabel = getLeaguePatternTranslation(patternKey, lang);

  if (!countryLabel || !patternLabel) return "";

  if (lang === "vi-VN") {
    return `${patternLabel} ${countryLabel}`.trim();
  }

  if (lang === "ja-JP" || lang === "zh-CN" || lang === "ko-KR") {
    return `${countryLabel} ${patternLabel}`.trim();
  }

  return `${countryLabel} ${patternLabel}`.trim();
}

function matchCountryPrefixOrSuffix(raw) {
  const normalized = normalizeLeagueLookupKey(raw);
  const aliasKeys = Object.keys(COUNTRY_ALIASES).sort((a, b) => b.length - a.length);

  for (const alias of aliasKeys) {
    if (normalized === alias) return { countryKey: COUNTRY_ALIASES[alias], remainder: "" };

    // Allow `country - remainder` and `country: remainder` (both normalize to `country - remainder`).
    const prefixDash = `${alias} - `;
    if (normalized.startsWith(prefixDash)) {
      return {
        countryKey: COUNTRY_ALIASES[alias],
        remainder: normalized.slice(prefixDash.length).trim()
      };
    }

    const prefixSpace = `${alias} `;
    if (normalized.startsWith(prefixSpace)) {
      return {
        countryKey: COUNTRY_ALIASES[alias],
        remainder: normalized.slice(prefixSpace.length).trim()
      };
    }

    const suffixDash = ` - ${alias}`;
    if (normalized.endsWith(suffixDash)) {
      return {
        countryKey: COUNTRY_ALIASES[alias],
        remainder: normalized.slice(0, normalized.length - suffixDash.length).trim()
      };
    }

    const suffixSpace = ` ${alias}`;
    if (normalized.endsWith(suffixSpace)) {
      return {
        countryKey: COUNTRY_ALIASES[alias],
        remainder: normalized.slice(0, normalized.length - suffixSpace.length).trim()
      };
    }
  }

  return null;
}

function isAmbiguousGenericLeaguePatternKey(canonicalRemainder) {
  const n = normalizeLeagueLookupKey(String(canonicalRemainder || ""));
  return /^(cup|league|super league|reserve league|reserve|women|division 1|division 2|division 3|division 4|youth)$/i.test(
    n
  );
}

function leaguePatternAllowedForCountry(canonicalRemainder, countryKey) {
  const n = normalizeLeagueLookupKey(String(canonicalRemainder || ""));
  const ck = String(countryKey || "");
  if (n === "championship" || n === "league one" || n === "league two") return ck === "England";
  if (n === "serie a" || n === "serie b" || n === "serie c") return ck === "Italy";
  if (n === "bundesliga 2") return ck === "Germany";
  if (n === "ligue 2") return ck === "France";
  if (n === "laliga 2") return ck === "Spain";
  return true;
}

function translateLeagueByPattern(raw, lang) {
  const matched = matchCountryPrefixOrSuffix(raw);
  if (!matched || !matched.countryKey) return "";

  const remainder = normalizeLeagueLookupKey(matched.remainder);
  if (!remainder) return "";

  if (/\bprofessional\s+development\s+league\b/i.test(remainder)) return "";
  if (/\bdevelopment\s+league\b/i.test(remainder)) return "";

  const canonicalRemainder =
    LEAGUE_PATTERN_ALIASES[remainder] ||
    LEAGUE_PATTERN_ALIASES[compactLeagueLookupKey(remainder)] ||
    remainder;

  const directPattern =
    LEAGUE_PATTERN_TRANSLATIONS[canonicalRemainder] ||
    LEAGUE_PATTERN_TRANSLATIONS[compactLeagueLookupKey(canonicalRemainder)];

  if (directPattern) {
    if (isAmbiguousGenericLeaguePatternKey(canonicalRemainder)) return "";
    if (!leaguePatternAllowedForCountry(canonicalRemainder, matched.countryKey)) return "";
    return buildPatternLeagueLabel(matched.countryKey, canonicalRemainder, lang);
  }

  const ck = matched.countryKey;

  if (/^league one$/.test(remainder)) {
    if (ck !== "England") return "";
    return buildPatternLeagueLabel(ck, "league one", lang);
  }

  if (/^league two$/.test(remainder)) {
    if (ck !== "England") return "";
    return buildPatternLeagueLabel(ck, "league two", lang);
  }

  if (/^championship$/.test(remainder)) {
    if (ck !== "England") return "";
    return buildPatternLeagueLabel(ck, "championship", lang);
  }

  if (/^division\s*1$/.test(remainder) || /^1st division$/.test(remainder)) return "";
  if (/^division\s*2$/.test(remainder) || /^2nd division$/.test(remainder)) return "";
  if (/^division\s*3$/.test(remainder) || /^3rd division$/.test(remainder)) return "";
  if (/^division\s*4$/.test(remainder) || /^4th division$/.test(remainder)) return "";

  if (/^serie\s*a$/.test(remainder)) {
    if (ck !== "Italy") return "";
    return buildPatternLeagueLabel(ck, "serie a", lang);
  }

  if (/^serie\s*b$/.test(remainder)) {
    if (ck !== "Italy") return "";
    return buildPatternLeagueLabel(ck, "serie b", lang);
  }

  if (/^serie\s*c$/.test(remainder)) {
    if (ck !== "Italy") return "";
    return buildPatternLeagueLabel(ck, "serie c", lang);
  }

  if (/^bundesliga\s*2$/.test(remainder) || /^2\.bundesliga$/.test(remainder)) {
    if (ck !== "Germany") return "";
    return buildPatternLeagueLabel(ck, "bundesliga 2", lang);
  }

  if (/^ligue\s*2$/.test(remainder)) {
    if (ck !== "France") return "";
    return buildPatternLeagueLabel(ck, "ligue 2", lang);
  }

  if (/^laliga\s*2$/.test(remainder) || /^la liga\s*2$/.test(remainder)) {
    if (ck !== "Spain") return "";
    return buildPatternLeagueLabel(ck, "laliga 2", lang);
  }

  if (/^super league$/.test(remainder)) return "";
  if (/^reserve league$/.test(remainder)) return "";
  if (/^reserve$/.test(remainder)) return "";
  if (/^cup$/.test(remainder)) return "";
  if (/^league$/.test(remainder)) return "";
  if (/^women$/.test(remainder)) return "";
  if (/^women super league$/.test(remainder)) return "";
  if (/^u19 league$/.test(remainder)) return "";
  if (/^u20 league$/.test(remainder)) return "";
  if (/^u21 league$/.test(remainder)) return "";
  if (/^u23 league$/.test(remainder)) return "";

  return "";
}

function replaceLeadingToken(raw, token, translatedToken) {
  return raw
    .replace(new RegExp(`^${token}(?=$|\\b|\\s|\\(|[+-]|\\d)`, "i"), translatedToken)
    .replace(/\s+/g, " ")
    .trim();
}

function expandMarketShorthand(value) {
  const raw = String(value || "").trim();
  if (!raw) return raw;
  const s = normalizeLookupKey(raw);

  const exact = [
    [/^ht\s+draw\s+no\s+bet$/, "draw no bet first half"],
    [/^ft\s+goal\s+line$/, "goal line full time"],
    [/^dnb\s+1h$/, "draw no bet first half"],
    [/^dnb\s+2h$/, "draw no bet second half"],
    [/^dnb$/, "draw no bet"],
    [/^ah\s+1h$/, "asian handicap first half"],
    [/^ah\s+2h$/, "asian handicap second half"],
    [/^eh\s+1h$/, "european handicap first half"],
    [/^eh\s+2h$/, "european handicap second half"],
    [/^btts\s+1h$/, "both teams to score first half"],
    [/^btts\s+2h$/, "both teams to score second half"],
    [/^ml$/, "moneyline"],
    [/^ou$/, "over/under"],
    [/^o\/u$/, "over/under"]
  ];

  for (const [re, canon] of exact) {
    if (re.test(s)) return canon;
  }

  return raw;
}

function normalizeMarketPhraseForLookup(value) {
  let s = normalizeLookupKey(value);
  s = s.replace(/\b1st\s+15\s+min(?:utes)?\b/gi, "first 15 min");
  s = s.replace(/\b1st\s+10\s+min(?:utes)?\b/gi, "first 10 min");
  s = s.replace(/\b1st\s+5\s+min(?:utes)?\b/gi, "first 5 min");
  s = s.replace(/\b15\s+minutes\b/gi, "15 minutes");
  s = s.replace(/\b1st\s+half\b/gi, "first half");
  s = s.replace(/\b2nd\s+half\b/gi, "second half");
  s = s.replace(/\b1st\s+quarter\b/gi, "first quarter");
  s = s.replace(/\b2nd\s+quarter\b/gi, "second quarter");
  s = s.replace(/\bhalf\s*time\b/gi, "first half");
  s = s.replace(/\b1st\s+set\b/gi, "first set");
  s = s.replace(/\b2nd\s+set\b/gi, "second set");
  s = s.replace(/\b1st\s+map\b/gi, "first map");
  s = s.replace(/\b2nd\s+map\b/gi, "second map");
  s = s.replace(/\b3rd\s+map\b/gi, "third map");
  s = s.replace(/\bset\s+1\b/gi, "first set");
  s = s.replace(/\bset\s+2\b/gi, "second set");
  s = s.replace(/\bmap\s+1\b/gi, "first map");
  s = s.replace(/\bmap\s+2\b/gi, "second map");
  s = s.replace(/\bmap\s+3\b/gi, "third map");
  s = s.replace(/\b3-way\b|\b3way\b/gi, "3 way");
  s = s.replace(/\bshots\s+on\s+target\b/gi, "shots on target");
  s = s.replace(/\bfulltime\b|\bfull-time\b/gi, "full time");
  return s.replace(/\s+/g, " ").trim();
}

function detectMarketPeriodScope(rawMarket) {
  const raw = String(rawMarket ?? "").trim();
  if (!raw) return null;

  const n0 = normalizeLookupKey(raw)
    .replace(/[()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const n = normalizeMarketPhraseForLookup(n0);

  const has = (re) => re.test(n) || re.test(n0);

  // Short-window scopes should win over half/full time.
  if (has(/\b(first\s*10\s*(?:min|mins|minutes)?)\b/i) || has(/\b10\s*minutes\b/i)) return "first10Min";
  if (has(/\b(first\s*15\s*(?:min|mins|minutes)?)\b/i) || has(/\b15\s*minutes\b/i)) return "first15Min";
  if (has(/\b30\s*minutes\b/i) || has(/\b30\s*mins\b/i) || has(/\b30min\b/i)) return "thirtyMinutes";
  if (has(/\b60\s*minutes\b/i) || has(/\b60\s*mins\b/i) || has(/\b60min\b/i)) return "sixtyMinutes";

  // Basketball quarters (explicit phrases only).
  if (has(/\b(first\s*quarter|1st\s*quarter)\b/i) || has(/\bq\s*1\b/i)) return "firstQuarter";
  if (has(/\b(second\s*quarter|2nd\s*quarter)\b/i) || has(/\bq\s*2\b/i)) return "secondQuarter";
  if (has(/\b(third\s*quarter|3rd\s*quarter)\b/i) || has(/\bq\s*3\b/i)) return "thirdQuarter";
  if (has(/\b(fourth\s*quarter|4th\s*quarter)\b/i) || has(/\bq\s*4\b/i)) return "fourthQuarter";

  // Tennis / set-level scopes (before half to avoid cross-matches).
  if (has(/\b(first\s*set|1st\s*set)\b/i)) return "firstSet";
  if (has(/\b(second\s*set|2nd\s*set)\b/i)) return "secondSet";

  // Half-time scopes (explicit phrases only; do not infer HT from bare "half").
  if (has(/\b(first\s*half)\b/i) || has(/\b1st\s*half\b/i) || has(/\b1h\b/i)) return "firstHalf";
  if (has(/\b(second\s*half)\b/i) || has(/\b2nd\s*half\b/i) || has(/\b2h\b/i)) return "secondHalf";

  // Full-time scopes before HT abbreviation (FT vs HT disambiguation).
  if (has(/\bfull\s+match\b/i)) return "fullTime";
  if (has(/\bfull\s*time\b/i) || has(/\bfulltime\b/i) || has(/\bft\b/i)) return "fullTime";

  // Some markets include "HT" for half-time (first half).
  if (has(/\bht\b/i)) return "firstHalf";

  return null;
}

function tryPatternBasedMarketTranslation(raw, lang) {
  const expanded = expandMarketShorthand(raw);
  const n = normalizeMarketPhraseForLookup(expanded);
  const variants = new Set([n, normalizeMarketPhraseForLookup(raw), normalizeLookupKey(raw), normalizeLookupKey(expanded)]);
  variants.add(n.replace(/\btotal\s+goals\s+both\s+teams\s+to\s+score\b/gi, "total goals/both teams to score"));
  variants.add(n.replace(/\s*\/\s*/g, "/"));

  for (const key of variants) {
    if (!key) continue;
    const t = peekTranslatedMapValue(MARKET_TRANSLATIONS_MAP, key, lang, null);
    if (t) return t;
  }

  const playerMade = /^player\s+made\s+(.+)$/i.exec(n);
  if (playerMade) {
    const tail = normalizeLookupKey(playerMade[1]).replace(/\s+/g, " ").trim();
    const tailMap = {
      threes: "player made threes",
      "3 pointers": "player made threes",
      "3 pointers made": "player made threes",
      "3 point field goals": "player made threes",
      "3 point field goals made": "player made threes",
      "3pt field goals": "player made threes",
      "3pt field goals made": "player made threes",
      "3 points": "player made threes"
    };
    const canon = tailMap[tail];
    if (canon) {
      const tr = peekTranslatedMapValue(MARKET_TRANSLATIONS_MAP, canon, lang, null);
      if (tr) return tr;
    }
    const prettyTail = prettifyUnknownMarketKey(playerMade[1]);
    return `Player Made ${prettyTail}`;
  }

  const raceM = /^race\s+to\s+(\d+)(?:\s+(goals|corners|shots on target))?$/i.exec(n);
  if (raceM) {
    const num = raceM[1];
    const kind = (raceM[2] || "").toLowerCase();
    let key = `race to ${num}`;
    if (kind === "goals") key = `race to ${num} goals`;
    else if (kind === "corners") key = `race to ${num} corners`;
    else if (kind === "shots on target") key = `race to ${num} shots on target`;
    const tr = peekTranslatedMapValue(MARKET_TRANSLATIONS_MAP, key, lang, null);
    if (tr) return tr;
  }

  const compose = [
    [/^draw\s+no\s+bet\s+(first half|second half)$/i, (m) => `draw no bet ${m[1]}`],
    [/^asian\s+handicap\s+(first half|second half)$/i, (m) => `asian handicap ${m[1]}`],
    [/^european\s+handicap\s+(first half|second half)$/i, (m) => `european handicap ${m[1]}`],
    [/^handicap\s+result\s+(first half|second half)$/i, (m) => `handicap result ${m[1]}`],
    [/^correct\s+score\s+(first half|second half)$/i, (m) => `correct score ${m[1]}`],
    [/^goal\s+line\s+(first half|second half|full time)$/i, (m) => {
      const p = m[1].toLowerCase();
      if (p.includes("first")) return "goal line first half";
      if (p.includes("second")) return "goal line second half";
      return "goal line full time";
    }],
    [/^result\/total\s+goals(?:\s+(first half|second half))?$/i, (m) => {
      const p = (m[1] || "").toLowerCase();
      if (p.includes("first")) return "result/total goals first half";
      if (p.includes("second")) return "result/total goals second half";
      return "result/total goals";
    }],
    [/^cards\s+over\s*\/\s*under(?:\s+(first half|second half))?$/i, (m) => {
      const p = (m[1] || "").toLowerCase();
      if (p.includes("first")) return "cards over under first half";
      if (p.includes("second")) return "cards over under second half";
      return "cards over under";
    }],
    [/^total\s+cards(?:\s+(first half|second half))?$/i, (m) => {
      const p = (m[1] || "").toLowerCase();
      if (p.includes("first")) return "total cards first half";
      if (p.includes("second")) return "total cards second half";
      return "total cards";
    }],
    [/^home\s+corners\s+over\s*\/\s*under$/i, () => "home corners over under"],
    [/^away\s+corners\s+over\s*\/\s*under$/i, () => "away corners over under"],
    [/^home\s+clean\s+sheet$/i, () => "home clean sheet"],
    [/^away\s+clean\s+sheet$/i, () => "away clean sheet"],
    [/^clean\s+sheet\s+home$/i, () => "clean sheet home"],
    [/^clean\s+sheet\s+away$/i, () => "clean sheet away"]
  ];

  for (const [re, builder] of compose) {
    const m = re.exec(n);
    if (m) {
      const key = builder(m);
      const t = peekTranslatedMapValue(MARKET_TRANSLATIONS_MAP, key, lang, null);
      if (t) return t;
    }
  }

  return "";
}

function translatePrefixedSelection(raw, lang) {
  const s0 = String(raw || "").trim();
  if (!s0) return raw;

  const underscoredToSpace = s0.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  const homeAwayOu = /^(home|away)\s+(over|under)$/i.exec(underscoredToSpace);
  if (homeAwayOu) {
    const sideT = getSelectionTokenTranslation(homeAwayOu[1].toLowerCase(), lang);
    const ouT = getSelectionTokenTranslation(homeAwayOu[2].toLowerCase(), lang);
    return `${sideT} ${ouT}`.replace(/\s+/g, " ").trim();
  }

  const homeAwayOuLine = /^(home|away)\s+(over|under)\s+([-+]?\d+(?:\.\d+)?)(?:\s+(ft|ht|2h|1h))?\s*$/i.exec(
    underscoredToSpace
  );
  if (homeAwayOuLine) {
    const sideT = getSelectionTokenTranslation(homeAwayOuLine[1].toLowerCase(), lang);
    const ouT = getSelectionTokenTranslation(homeAwayOuLine[2].toLowerCase(), lang);
    const line = homeAwayOuLine[3];
    const per = String(homeAwayOuLine[4] || "").trim().toUpperCase();
    const tail = per ? ` ${per}` : "";
    return `${sideT} ${ouT} ${line}${tail}`.replace(/\s+/g, " ").trim();
  }

  const ouLinePeriod = /^(over|under)\s+([-+]?\d+(?:\.\d+)?)(?:\s+(ft|ht|2h|1h))?\s*$/i.exec(underscoredToSpace);
  if (ouLinePeriod) {
    const ouT = getSelectionTokenTranslation(ouLinePeriod[1].toLowerCase(), lang);
    const line = ouLinePeriod[2];
    const per = String(ouLinePeriod[3] || "").trim().toUpperCase();
    const tail = per ? ` ${per}` : "";
    return `${ouT} ${line}${tail}`.replace(/\s+/g, " ").trim();
  }

  const shouldTranslateLeadingToken = (input, token) => {
    const re = new RegExp(`^${token}\\b`, "i");
    const m = re.exec(input);
    if (!m) return false;
    const rest = String(input.slice(m[0].length) || "").trimStart();
    if (!rest) return true;
    const first = rest[0];
    if (first === "(") return true;
    // Only translate when followed by a numeric/handicap/line-like value.
    return /^[+-]?\d/.test(rest);
  };

  const dnb = /^(draw\s+no\s+bet)([\s\S]*)$/i.exec(s0);
  if (dnb) {
    const tail = String(dnb[2] || "").trim();
    const dnbLabel =
      peekTranslatedMapValue(MARKET_TRANSLATIONS_MAP, "draw no bet", lang, null) || "Draw No Bet";
    if (!tail) return dnbLabel;
    return `${dnbLabel} ${tail}`.replace(/\s+/g, " ").trim();
  }

  const hoeSlash = /^(home|away)\s+odd\s*\/\s*even\s*$/i.exec(s0);
  if (hoeSlash) {
    const side = getSelectionTokenTranslation(hoeSlash[1].toLowerCase(), lang);
    const oe =
      peekTranslatedMapValue(MARKET_TRANSLATIONS_MAP, "odd even", lang, null) || "Odd / Even";
    return `${side} — ${oe}`;
  }

  const hoe = /^(home|away)\s+(odd|even)\s*$/i.exec(s0);
  if (hoe) {
    const side = getSelectionTokenTranslation(hoe[1].toLowerCase(), lang);
    const bit = getSelectionTokenTranslation(hoe[2].toLowerCase(), lang);
    return `${side} — ${bit}`;
  }

  const sideLine = /^(home|away|draw)\s+([+-]?\d+(?:\.\d+)?)\s*$/i.exec(s0);
  if (sideLine) {
    const t = getSelectionTokenTranslation(sideLine[1].toLowerCase(), lang);
    return `${t} ${sideLine[2]}`;
  }

  const patterns = [
    ["over", "over"],
    ["under", "under"],
    ["home", "home"],
    ["away", "away"],
    ["draw", "draw"],
    ["yes", "yes"],
    ["no", "no"],
    ["odd", "odd"],
    ["even", "even"]
  ];

  for (const [pattern, token] of patterns) {
    if (shouldTranslateLeadingToken(s0, pattern)) {
      return replaceLeadingToken(s0, pattern, getSelectionTokenTranslation(token, lang));
    }
  }

  return raw;
}

const LEAGUE_FALLBACK_WORD_ABBR = {
  mlb: "MLB",
  nhl: "NHL",
  nba: "NBA",
  nfl: "NFL",
  ncaaf: "NCAAF",
  wnba: "WNBA",
  ahl: "AHL",
  atp: "ATP",
  wta: "WTA",
  itf: "ITF",
  mls: "MLS",
  ufc: "UFC",
  pga: "PGA",
  uefa: "UEFA",
  fifa: "FIFA",
  concacaf: "CONCACAF",
  conmebol: "CONMEBOL",
  ncaa: "NCAA",
  ncaab: "NCAAB"
};

function prettifyLeagueCodeFallback(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const cleaned = normalizeLeagueLookupKey(s).replace(/\s+/g, " ").trim();
  if (!/[a-z]{2,}/i.test(cleaned)) return "";
  return cleaned
    .split(/\s+/)
    .map((w) => {
      const t = w.toLowerCase();
      if (LEAGUE_FALLBACK_WORD_ABBR[t]) return LEAGUE_FALLBACK_WORD_ABBR[t];
      if (t === "women" || t === "men") return t.charAt(0).toUpperCase() + t.slice(1);
      return t.charAt(0).toUpperCase() + t.slice(1);
    })
    .join(" ");
}

function prettifyUnknownMarketKey(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const n = s
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!n) return "";
  return n
    .split(/\s+/)
    .map((w) => {
      const t = w.toLowerCase();
      if (t === "btts") return "BTTS";
      if (t === "ou" || t === "ml" || t === "ah" || t === "eh" || t === "dnb") return t.toUpperCase();
      return t.charAt(0).toUpperCase() + t.slice(1);
    })
    .join(" ");
}

function normalizeSharpMarketKeyForDisplay(rawMarket) {
  const raw = String(rawMarket ?? "").trim();
  if (!raw) return "";
  return raw
    .toLowerCase()
    .replace(/[‐-‒–—−]/g, "-")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\s*\/\s*/g, "/")
    .trim();
}

/**
 * Sharp catalog + exact canonical names → `LEAGUE_TRANSLATIONS` key.
 * Single source of truth; checked before aliases/fuzzy in `translateLeagueDisplay`,
 * and merged into `resolveLeagueKey` after `LEAGUE_ALIASES`.
 */
const EXACT_KNOWN_SHARP_LEAGUE_ENTRIES = {
  nba: "NBA",
  ncaab: "NCAAB",
  wnba: "WNBA",
  nfl: "NFL",
  ncaaf: "NCAAF",
  nhl: "NHL",
  mlb: "MLB",
  epl: "English Premier League",
  la_liga: "La Liga",
  serie_a: "Serie A",
  bundesliga: "Bundesliga",
  mls: "MLS",
  atp: "ATP",
  wta: "WTA",
  ufc: "UFC",
  pga: "PGA Tour",
  "english premier league": "English Premier League",
  "la liga": "La Liga",
  "serie a": "Serie A",
  "pga tour": "PGA Tour"
};

const EXACT_KNOWN_SHARP_LEAGUES_MAP = buildNormalizedLeagueMap(EXACT_KNOWN_SHARP_LEAGUE_ENTRIES);

function resolveExactKnownSharpLeagueCanonical(normalized, compact) {
  return EXACT_KNOWN_SHARP_LEAGUES_MAP[normalized] || EXACT_KNOWN_SHARP_LEAGUES_MAP[compact] || null;
}

/** Bare "Premier League" is ambiguous globally — do not use the EPL translation table by direct key match. */
const AMBIGUOUS_BARE_PREMIER_LEAGUE_LOOKUP = new Set(["premier league", "premierleague"]);

function isRegionalNonEplPremierLeague(normalized) {
  return /\b(northern|southern|isthmian|welsh|scottish|irish|maltese|gibraltar|faroe|iceland|luxembourg|andorra|san\s*marino|australian|nsw|victorian|belarus|ukrainian|kazakh|bosnian|russian|azerbaijan|armenian|georgian|albanian|moldovan|montenegrin|north\s*macedonian|estonian|latvian|lithuanian)\s+premier\s+league\b/i.test(
    normalized
  );
}

function isStrongEnglishPremierLeagueEvidence(normalized) {
  if (/\benglish\s+premier\s+league\b/i.test(normalized)) return true;
  if (/\bbarclays\s+premier\s+league\b/i.test(normalized)) return true;
  if (/\bengland\b/i.test(normalized) && /\bpremier\s+league\b/i.test(normalized)) return true;
  if (
    /\benglish\b/i.test(normalized) &&
    /\bpremier\s+league\b/i.test(normalized) &&
    !/\b(northern|southern|irish|scottish|welsh)\b/i.test(normalized)
  ) {
    return true;
  }
  return false;
}

function isLikelyItalianSerieATopTier(normalized) {
  if (!/\bserie\s*a\b/i.test(normalized)) return false;
  if (/\b(brazil|brasil|brasileir[aã]o|brazilian)\b/i.test(normalized)) return false;
  return true;
}

function resolveLeagueKey(raw) {
  const normalized = normalizeLeagueLookupKey(raw);
  const compact = compactLeagueLookupKey(raw);

  if (LEAGUE_TRANSLATIONS_MAP[normalized] || LEAGUE_TRANSLATIONS_MAP[compact]) {
    return raw;
  }

  const alias = LEAGUE_ALIASES[normalized] || LEAGUE_ALIASES[compact];
  if (alias) return alias;

  const sharpCanon = resolveExactKnownSharpLeagueCanonical(normalized, compact);
  if (sharpCanon) return sharpCanon;

  // Conservative fuzzy resolution — avoid substring false positives (e.g. "Northern Premier League" → EPL).
  if (/\bpremier\s+league\b/i.test(normalized)) {
    if (isRegionalNonEplPremierLeague(normalized)) return null;
    if (/\bprofessional\s+development\s+league\b/i.test(normalized)) return null;
    if (/\bdevelopment\s+league\b/i.test(normalized)) return null;
    if (/\b(youth|reserve|women)\b/i.test(normalized)) return null;
    if (/\bu\s*\d{1,2}\b/i.test(normalized)) return null;
    if (/\bpremier\s+league\s*(?:2|ii)\b/i.test(normalized)) return null;
    if (/^premier\s+league$/i.test(normalized.trim())) return null;
    if (isStrongEnglishPremierLeagueEvidence(normalized)) return "English Premier League";
    return null;
  }

  if (/\bla\s+liga\b/i.test(normalized)) {
    if (/\blaliga\s*2\b|\bla\s+liga\s*2\b/i.test(normalized)) return null;
    if (/\bsegunda\b/i.test(normalized)) return null;
    return "La Liga";
  }

  if (/\bbundesliga\b/i.test(normalized)) {
    if (/\b2\.\s*bundesliga\b|\bbundesliga\s*2\b|\b2\s*nd\s+bundesliga\b/i.test(normalized)) return null;
    return "Bundesliga";
  }

  if (isLikelyItalianSerieATopTier(normalized)) return "Serie A";

  if (/\bligue\s*1\b/i.test(normalized)) return "Ligue 1";

  if (/\bchampions\s+league\b/i.test(normalized)) {
    if (/\b(afc|asia|caf|concacaf|conmebol|ofc)\b/i.test(normalized)) return null;
    return "Champions League";
  }

  if (/\beuropa\s+league\b/i.test(normalized)) return "Europa League";

  if (/\bconference\s+league\b/i.test(normalized)) return "Conference League";

  if (/\bmls\b/i.test(normalized) || /\bmajor\s+league\s+soccer\b/i.test(normalized)) return "MLS";

  if (/\b(?:efl\s+championship|english\s+championship)\b/i.test(normalized)) return "EFL Championship";

  if (/\bj1\s+league\b/i.test(normalized) || normalized === "j1") return "J1 League";

  if (/\bserie\s*b\s*brazil\b/i.test(normalized) || /\bbrazil\s+serie\s*b\b/i.test(normalized)) return "Serie B Brazil";

  if (normalized === "nba") return "NBA";
  if (normalized === "atp") return "ATP";
  if (normalized === "wta") return "WTA";
  if (normalized === "mlb") return "MLB";
  if (normalized === "nhl") return "NHL";
  if (normalized === "ahl") return "AHL";
  if (normalized === "wnba") return "WNBA";
  if (normalized === "ncaab") return "NCAAB";
  if (normalized === "nfl") return "NFL";
  if (normalized === "ncaaf") return "NCAAF";
  if (normalized === "ufc") return "UFC";

  return null;
}

function resolveMarketCanonicalKey(raw) {
  const normalized = normalizeLookupKey(raw);
  const compact = compactLookupKey(raw);
  const phrase = normalizeMarketPhraseForLookup(raw);
  const phraseCompact = phrase !== normalized ? compactLookupKey(phrase) : "";

  if (MARKET_TRANSLATIONS_MAP[normalized] || MARKET_TRANSLATIONS_MAP[compact]) {
    return normalized;
  }

  if (phrase !== normalized && (MARKET_TRANSLATIONS_MAP[phrase] || MARKET_TRANSLATIONS_MAP[phraseCompact])) {
    return phrase;
  }

  let alias = MARKET_ALIASES[normalized] || MARKET_ALIASES[compact];
  if (!alias && phrase !== normalized) {
    alias = MARKET_ALIASES[phrase] || MARKET_ALIASES[phraseCompact];
  }
  if (alias) return alias;

  if (/^1\s*x\s*2$/i.test(raw)) return "1x2";
  if (/moneyline\s+3\s+way|3\s+way\s+moneyline|3\s*way\s+moneyline/i.test(normalized)) return "moneyline 3 way";

  // Esports — map/set winner markets often include an ordinal prefix (Map 1/2/3, Set 1/2/3, etc).
  // Keep the ordinal handling in the shared translator; canonicalize here to the family key.
  if (/\bmap\s+(moneyline|winner)\b/i.test(normalized)) {
    if (/\bmap\s+moneyline\b/i.test(normalized)) return "map moneyline";
    return "map winner";
  }
  if (/\bset\s+(moneyline|winner)\b/i.test(normalized)) {
    if (/\bset\s+moneyline\b/i.test(normalized)) return "set moneyline";
    return "set winner";
  }

  // Tennis — set moneyline phrasing still containing ordinal + moneyline.
  if (/\b(1st|2nd|first|second)\s+set\s+moneyline\b/i.test(normalized)) return "set moneyline";
  // Match-level tennis totals / handicaps (aliases cover games total, sets total, games handicap, set spread).
  if (/^total\s+games\b/i.test(normalized) || /^games\s+total\b/i.test(normalized)) return "total games";
  if (/^total\s+sets\b/i.test(normalized) || /^sets\s+total\b/i.test(normalized)) return "total sets";
  if (/\bgames\s+handicap\b/i.test(normalized)) return "game handicap";
  if (/\bset\s+spread\b/i.test(normalized)) return "set handicap";

  // Corners — resolve distinct families (order: team totals → O/U → plain totals → handicap).
  if (/\bteam\s+total\s+corners?\s*(?:over\s*\/\s*under|o\/u)\b/i.test(normalized)) return "team total corners over under";
  if (/\bteam\s+total\s+corners?\b/i.test(normalized)) return "team total corners";
  if (/\btotal\s+corners?\s*(?:over\s*\/\s*under|o\/u)\b/i.test(normalized)) return "corners over under";
  if (/\bcorners?\s+over\s*\/\s*under\b/i.test(normalized) || /\bcorners?\s+o\/u\b/i.test(normalized)) {
    return "corners over under";
  }
  if (
    (/^total\s+corners?\b/i.test(normalized) || /^corner\s+total(?:s)?\b/i.test(normalized) || /^corners?\s+total(?:s)?\b/i.test(normalized)) &&
    !/\bgoal\b/i.test(normalized)
  ) {
    return "total corners";
  }
  if (/\basian\s+handicap\s+corners\b/i.test(normalized) || /\bcorners?\s+asian\s+handicap\b/i.test(normalized)) {
    return "asian handicap corners";
  }
  if (/\bcorners?\s+handicap\b/i.test(normalized) && !/asian/i.test(normalized)) return "corners handicap";
  if (/\bcorner\s+match\s+bet\b/i.test(normalized)) return "corner match bet";
  if (/\bcorner\s+winner\b/i.test(normalized)) return "corner winner";

  if (/over\s*\/\s*under/i.test(raw) && !/team\s+total/i.test(normalized)) return "over/under";
  if (/^totals?$/i.test(raw)) return "totals";
  if (/^total\s+points$/i.test(raw)) return "total points";

  if (/\b(?:first|1st)\s+quarter\s+moneyline\b/i.test(normalized)) return "moneyline";
  if (/\b(?:second|2nd)\s+quarter\s+moneyline\b/i.test(normalized)) return "moneyline";
  if (/\b(?:first|1st)\s+half\s+moneyline\b/i.test(normalized)) return "moneyline";
  if (/\b(?:second|2nd)\s+half\s+moneyline\b/i.test(normalized)) return "moneyline";
  if (/\b(?:first|1st)\s+quarter\s+team\s+total\b/i.test(normalized)) return "team total";
  if (/\b(?:second|2nd)\s+quarter\s+team\s+total\b/i.test(normalized)) return "team total";
  if (/\bwinner\s+at\s+60\s*minutes\b/i.test(normalized)) return "moneyline";
  if (/\bmoneyline\s+at\s+60\s*minutes\b/i.test(normalized)) return "moneyline";
  if (/\b3[\s-]*way\s+winner\s+at\s+60\s*minutes\b/i.test(normalized)) return "moneyline 3 way";
  if (/\b1x2\s+at\s+60\s*minutes\b/i.test(normalized)) return "1x2 60 minutes";
  if (/\b3[\s-]*way\s+winner\b/i.test(normalized)) return "moneyline 3 way";
  if (/^team\s+total$/i.test(normalized) || /^team\s+totals$/i.test(normalized)) return "team total";
  if (/^match\s*winner$/i.test(raw)) return "match winner";
  if (/^winner$/i.test(raw)) return "winner";
  if (/^moneyline$/i.test(raw)) return "moneyline";
  if (/^h2h$/i.test(raw)) return "moneyline";
  if (/^match\s*odds$/i.test(raw)) return "1x2";
  if (/^full[\s-]*time\s*result$/i.test(raw)) return "1x2";
  if (/^both\s+teams\s+to\s+score\??$/i.test(raw)) return "btts";
  if (/^both\s+teams\s+score$/i.test(raw)) return "btts";
  if (/^btts$/i.test(raw)) return "btts";
  if (/^point\s+spread$/i.test(normalized)) return "point spread";
  if (/^game\s+spread$/i.test(normalized)) return "spread";
  if (/^spread$/i.test(normalized)) return "spread";
  if (/^puck\s+line$/i.test(normalized)) return "puck line";
  if (/^set\s+moneyline$/i.test(normalized)) return "set moneyline";
  if (/^player\s+made\s+threes$/i.test(normalized)) return "player made threes";

  if (/^team\s+total\s+runs$/i.test(normalized)) return "team total";
  if (/^team\s+total\s+points$/i.test(normalized)) return "team total";
  if (/^team\s+total\s+goals$/i.test(normalized)) return "team total goals";
  if (/^1st\s+half\s+team\s+total\s+runs$/i.test(normalized)) return "team total";
  if (/^2nd\s+half\s+team\s+total\s+runs$/i.test(normalized)) return "team total";
  if (/^1st\s+half\s+team\s+total\s+points$/i.test(normalized)) return "team total";
  if (/^2nd\s+half\s+team\s+total\s+points$/i.test(normalized)) return "team total";

  if (/^total\s+runs$/i.test(normalized)) return "total runs";
  if (/^total\s+goals$/i.test(normalized)) return "total goals";
  if (/^run\s+line$/i.test(normalized)) return "run line";

  if (/^1st\s+half\s+team\s+total\s+goals$/i.test(normalized)) return "team total goals";
  if (/^2nd\s+half\s+team\s+total\s+goals$/i.test(normalized)) return "team total goals";
  if (/^1st\s+half\s+total\s+goals$/i.test(normalized)) return "total goals";
  if (/^2nd\s+half\s+total\s+goals$/i.test(normalized)) return "total goals";
  if (/^1st\s+half\s+total\s+points$/i.test(normalized)) return "total points";
  if (/^2nd\s+half\s+total\s+points$/i.test(normalized)) return "total points";
  if (/^1st\s+half\s+total\s+runs$/i.test(normalized)) return "total runs";
  if (/^2nd\s+half\s+total\s+runs$/i.test(normalized)) return "total runs";
  if (/^1st\s+half\s+point\s+spread$/i.test(normalized)) return "point spread";
  if (/^2nd\s+half\s+point\s+spread$/i.test(normalized)) return "point spread";
  if (/^1st\s+half\s+run\s+line$/i.test(normalized)) return "run line";
  if (/^2nd\s+half\s+run\s+line$/i.test(normalized)) return "run line";
  if (/^1st\s+half\s+puck\s+line$/i.test(normalized)) return "puck line";
  if (/^2nd\s+half\s+puck\s+line$/i.test(normalized)) return "puck line";
  if (/^1st\s+half\s+total\s+corners?$/i.test(normalized)) return "total corners";
  if (/^2nd\s+half\s+total\s+corners?$/i.test(normalized)) return "total corners";
  if (/^1st\s+half\s+corners?\s+over\s*\/\s*under$/i.test(normalized)) return "corners over under";
  if (/^2nd\s+half\s+corners?\s+over\s*\/\s*under$/i.test(normalized)) return "corners over under";

  return null;
}

function leagueHintsSoccerContext(text) {
  const n = normalizeLeagueLookupKey(text);
  if (!n) return false;
  return /\b(epl|premier\s*league|la\s*liga|bundesliga|serie\s*a\b|ligue\s*1|mls|uefa|concacaf|conmebol|champions\s*league|europa\s*league|eredivisie|primeira|world\s*cup|fifa|copa\s*libertadores|brasileir|efl|championship|scottish\s*premiership|super\s*lig|saudi\s*pro|pro\s*league|j1\s*league|k\s*league|brasileir[aã]o)\b/i.test(
    n
  );
}

/**
 * Classifies a raw sport string (optionally with league/match hints) into a visible Analysis tab bucket.
 * Returns "unsupported" for sports not mapped to football / tennis / basketball / esports.
 */
const RE_TABLE_TENNIS_OR_PING_PONG = /\btable\s*tennis\b|\bping\s*pong\b/i;
const RE_GRIDIRON_FOOTBALL_TOKENS =
  /\b(?:american\s*football|canadian\s*football|college\s*football|nfl|ncaaf|cfb|cfl|ufl|xfl|usfl|super\s*bowl)\b/i;
const RE_OTHER_UNSUPPORTED_SPORT_TOKENS =
  /\b(?:baseball|mlb\b|hockey\b|ice\s*hockey|nhl\b|ahl\b|echl\b|khl\b|mma\b|ufc\b|golf\b|pga\b|lpga\b|boxing\b|nascar\b|lacrosse\b|volleyball|rugby|cricket|darts|snooker|formula\s*1|\bf1\b)\b/i;
const RE_TENNIS_TOKENS = /\b(?:tennis|atp|wta|itf|challenger|davis\s*cup|billie\s*jean)\b/i;
const RE_BASKETBALL_TOKENS = /\b(?:basketball|nba|wnba|ncaab|euroleague|eurocup|gleague|nba\s*g\s*league)\b/i;
const RE_ESPORTS_TOKENS =
  /\b(?:esports?|e-?sports|cs2|csgo|counter-?strike|dota|lol\b|league\s*of\s*legends|valorant|overwatch|rocket\s*league|r6s?|rainbow|starcraft|pubg|fortnite|cod\b|call\s*of\s*duty|apex)\b/i;
const RE_SOCCER_TOKENS = /\b(?:soccer|futsal|association\s*football)\b/i;

function classifySportForAnalysisTab(rawSport, extra = {}) {
  const league = String(extra.league ?? "").trim();
  const match = String(extra.match ?? "").trim();
  const sportAlone = String(rawSport ?? "").trim();
  const sportNorm = normalizeLookupKey(sportAlone);
  const combined = [sportAlone, league, match].filter(Boolean).join(" ");
  const n = normalizeLookupKey(combined);

  if (!n && !sportAlone) return "unsupported";

  // ---- Step 1: classify using ONLY the explicit rawSport text. An explicit,
  // unambiguous rawSport token always wins over league/match — even when
  // league/match carries a conflicting hint. E.g. sport="Table Tennis" with
  // league="ATP" must stay unsupported; sport="Basketball" with league="ATP"
  // must stay basketball; sport="American Football" with league="NBA" must
  // stay unsupported; sport="Soccer" with league="NBA" must stay football.
  if (sportNorm) {
    if (RE_TABLE_TENNIS_OR_PING_PONG.test(sportNorm)) return "unsupported";
    if (RE_GRIDIRON_FOOTBALL_TOKENS.test(sportNorm)) return "unsupported";
    if (RE_OTHER_UNSUPPORTED_SPORT_TOKENS.test(sportNorm)) return "unsupported";
    if (RE_TENNIS_TOKENS.test(sportNorm)) return "tennis";
    if (RE_BASKETBALL_TOKENS.test(sportNorm)) return "basketball";
    if (sportNorm === "ncaa" || /^ncaa$/i.test(sportAlone)) {
      if (/\b(?:basketball|cbb|hoops)\b/i.test(n)) return "basketball";
      return "unsupported";
    }
    if (RE_ESPORTS_TOKENS.test(sportNorm)) return "esports";
    if (RE_SOCCER_TOKENS.test(sportNorm)) return "football";
  }

  // ---- Step 2: rawSport gave no definitive answer. League/match are used as
  // a fallback ONLY when rawSport is empty, or rawSport is a bare/ambiguous
  // "football" token (Soccer vs. American/Canadian/college football cannot
  // be told apart from the word "football" alone).
  const sportIsBareFootball = /\bfootball\b/.test(sportNorm);
  if (sportAlone && !sportIsBareFootball) {
    // rawSport was explicit but unrecognized — do not let an unrelated
    // league/match hint reclassify this row into a fixed tab.
    return "unsupported";
  }

  if (sportIsBareFootball) {
    // Ambiguous "football": only league/match can confirm real Soccer vs.
    // American/Canadian/college gridiron football.
    if (RE_GRIDIRON_FOOTBALL_TOKENS.test(n)) return "unsupported";
    if (leagueHintsSoccerContext(league) || leagueHintsSoccerContext(match)) return "football";
    return "unsupported";
  }

  // rawSport is empty — classify purely from league/match context.
  const ln = league ? normalizeLeagueLookupKey(league) : "";
  if (ln) {
    if (/\bmlb\b/i.test(ln)) return "unsupported";
    if (/\b(?:nhl|ahl|echl|khl)\b/i.test(ln)) return "unsupported";
    if (/\bnba\b/i.test(ln)) return "basketball";
    if (/\bwnba\b/i.test(ln)) return "basketball";
    if (/\batp\b/i.test(ln)) return "tennis";
    if (/\bwta\b/i.test(ln)) return "tennis";
    if (/\bitf\s*(?:women|womens|men)\b/i.test(ln)) return "tennis";
  }

  if (RE_TABLE_TENNIS_OR_PING_PONG.test(n)) return "unsupported";
  if (RE_GRIDIRON_FOOTBALL_TOKENS.test(n)) return "unsupported";
  if (RE_OTHER_UNSUPPORTED_SPORT_TOKENS.test(n)) return "unsupported";
  if (RE_TENNIS_TOKENS.test(n)) return "tennis";
  if (RE_BASKETBALL_TOKENS.test(n)) return "basketball";
  if (RE_ESPORTS_TOKENS.test(n)) return "esports";
  if (RE_SOCCER_TOKENS.test(n)) return "football";
  if (leagueHintsSoccerContext(league) || leagueHintsSoccerContext(match)) return "football";

  return "unsupported";
}

function stripPeriodScopeFromMarketText(rawMarket, scopeKey) {
  let s = String(rawMarket ?? "").trim();
  if (!s || !scopeKey) return s;
  const patterns = {
    first10Min: /\b(?:first\s*10\s*(?:min|mins|minutes)?|10\s*minutes)\b/gi,
    first15Min: /\b(?:first\s*15\s*(?:min|mins|minutes)?|15\s*minutes)\b/gi,
    thirtyMinutes: /\b30\s*(?:minutes|mins|min)\b/gi,
    sixtyMinutes: /\b60\s*(?:minutes|mins|min)\b/gi,
    firstQuarter: /\b(?:first\s*quarter|1st\s*quarter|q\s*1)\b/gi,
    secondQuarter: /\b(?:second\s*quarter|2nd\s*quarter|q\s*2)\b/gi,
    thirdQuarter: /\b(?:third\s*quarter|3rd\s*quarter|q\s*3)\b/gi,
    fourthQuarter: /\b(?:fourth\s*quarter|4th\s*quarter|q\s*4)\b/gi,
    firstSet: /\b(?:first\s*set|1st\s*set|set\s*1)\b/gi,
    secondSet: /\b(?:second\s*set|2nd\s*set|set\s*2)\b/gi,
    firstHalf: /\b(?:first\s*half|1st\s*half|half\s*time|1h|ht)\b/gi,
    secondHalf: /\b(?:second\s*half|2nd\s*half|2h)\b/gi,
    fullTime: /\b(?:full\s*time|fulltime|full\s+match|ft)\b/gi
  };
  const re = patterns[scopeKey];
  if (!re) return s;
  s = s.replace(re, " ").replace(/\s+/g, " ").trim();
  return s;
}

function stripTrailingPeriodScopeAlias(rawMarket, scopeKey) {
  let s = String(rawMarket ?? "").trim();
  if (!s || !scopeKey) return s;

  // Only strip well-formed trailing aliases (conservative):
  // - "... - HT", "... (HT)", "... · HT", "... | HT"
  // - "... - Set 1"
  const suffixes = {
    fullTime: /\s*(?:[-–—·|/]\s*)?(?:\(\s*)?(?:FT)(?:\s*\))?\s*$/i,
    firstHalf: /\s*(?:[-–—·|/]\s*)?(?:\(\s*)?(?:HT|1H)(?:\s*\))?\s*$/i,
    secondHalf: /\s*(?:[-–—·|/]\s*)?(?:\(\s*)?(?:2H)(?:\s*\))?\s*$/i,
    firstSet: /\s*(?:[-–—·|/]\s*)?(?:\(\s*)?(?:Set\s*1)(?:\s*\))?\s*$/i,
    secondSet: /\s*(?:[-–—·|/]\s*)?(?:\(\s*)?(?:Set\s*2)(?:\s*\))?\s*$/i,
    firstQuarter: /\s*(?:[-–—·|/]\s*)?(?:\(\s*)?(?:Q1)(?:\s*\))?\s*$/i,
    secondQuarter: /\s*(?:[-–—·|/]\s*)?(?:\(\s*)?(?:Q2)(?:\s*\))?\s*$/i,
    thirdQuarter: /\s*(?:[-–—·|/]\s*)?(?:\(\s*)?(?:Q3)(?:\s*\))?\s*$/i,
    fourthQuarter: /\s*(?:[-–—·|/]\s*)?(?:\(\s*)?(?:Q4)(?:\s*\))?\s*$/i,
    first10Min: /\s*(?:[-–—·|/]\s*)?(?:\(\s*)?(?:10m)(?:\s*\))?\s*$/i,
    first15Min: /\s*(?:[-–—·|/]\s*)?(?:\(\s*)?(?:15m)(?:\s*\))?\s*$/i,
    thirtyMinutes: /\s*(?:[-–—·|/]\s*)?(?:\(\s*)?(?:30m)(?:\s*\))?\s*$/i,
    sixtyMinutes: /\s*(?:[-–—·|/]\s*)?(?:\(\s*)?(?:60m)(?:\s*\))?\s*$/i
  };

  const re = suffixes[scopeKey];
  if (!re) return s;
  return s.replace(re, "").replace(/\s+/g, " ").trim();
}

function cleanupMarketSeparatorsAfterScopeStrip(rawMarket) {
  let s = String(rawMarket ?? "").trim();
  if (!s) return s;

  // Normalize dash variants first so we can safely trim.
  s = s.replace(/[‐-‒–—−]/g, "-");

  // Remove empty parentheses left behind by stripping (HT) etc.
  s = s.replace(/\(\s*\)/g, " ");

  // Trim leading/trailing separators that commonly remain after scope stripping.
  s = s.replace(/^\s*[-·|/]+\s*/g, "");
  s = s.replace(/\s*[-·|/]+\s*$/g, "");

  // Collapse repeated separators and whitespace.
  s = s.replace(/\s*-\s*-\s*/g, " - ");
  s = s.replace(/\s+/g, " ").trim();

  // If we ended up with a trailing joiner like "Total Goals -", remove it.
  s = s.replace(/\s*-\s*$/g, "").trim();
  s = s.replace(/^\s*-\s*/g, "").trim();

  return s;
}

/**
 * Pulls a trailing handicap / total line out of a raw market string when `item.line` is missing.
 */
function extractEmbeddedLineFromRawMarket(raw) {
  const s0 = String(raw ?? "").trim();
  if (!s0) return { line: "", remainder: s0 };
  const n = normalizeLookupKey(s0).replace(/\s+/g, " ").trim();
  if (/\brace\s+to\b/i.test(n)) return { line: "", remainder: s0 };

  // Score-like tails (correct score, result markets) — not handicap lines.
  if (/\b\d+\s*-\s*\d+\s*$/i.test(n)) return { line: "", remainder: s0 };

  const asianSplit = /\s+([-+]?\d+(?:\.\d+)?)\/([-+]?\d+(?:\.\d+)?)\s*$/i.exec(n);
  if (asianSplit) {
    const line = `${asianSplit[1]}/${asianSplit[2]}`;
    const remainder = n.slice(0, asianSplit.index).trim();
    return { line, remainder };
  }

  const dec = /\s+([-+]?\d+\.\d+)\s*$/i.exec(n);
  if (dec) {
    const remainder = n.slice(0, dec.index).trim();
    if (/\b3\s*way\b/i.test(remainder) || /\b3\s+way\b/i.test(remainder)) return { line: "", remainder: s0 };
    if (/\bcorrect\s+score\b/i.test(n)) return { line: "", remainder: s0 };
    return { line: dec[1], remainder };
  }

  const signedInt = /\s+([-+]?\d+)\s*$/i.exec(n);
  if (signedInt) {
    const remainder = n.slice(0, signedInt.index).trim();
    if (/\b3\s*way\b/i.test(remainder) || /\b3\s+way\b/i.test(remainder)) return { line: "", remainder: s0 };
    if (/\bcorrect\s+score\b/i.test(n)) return { line: "", remainder: s0 };
    if (/\b(handicap|asian|spread|set|game)\b/i.test(n)) {
      return { line: signedInt[1], remainder };
    }
  }

  const intM = /\s+(\d{1,2})\s*$/i.exec(n);
  if (intM) {
    const remainder = n.slice(0, intM.index).trim();
    if (/\b3\s*way\b/i.test(remainder) || /\b3\s+way\b/i.test(remainder)) return { line: "", remainder: s0 };
    if (/\bcorrect\s+score\b/i.test(n)) return { line: "", remainder: s0 };
    if (/\b(over|under|total|handicap|spread|corner|goal|o\/u|u\/o|asian|line|puck|run|game|set|games)\b/i.test(n)) {
      return { line: intM[1], remainder };
    }
  }

  return { line: "", remainder: s0 };
}

function isReliableNumericLineToken(value) {
  const t = String(value ?? "").trim();
  if (!t || t === "—" || t === "-") return false;
  return true;
}

/**
 * Parses a handicap / total line from bookmaker selection text (Arb/Middles legs).
 */
function extractLineTokenFromPickSelectionText(sel) {
  const s = String(sel ?? "").trim();
  if (!s) return "";

  if (/^\d+\s*-\s*\d+\s*$/.test(s)) return "";

  if (/^[+-]?\d+(?:\.\d+)?$/.test(s)) return s;
  if (/^[+-]?\d+(?:\.\d+)?\s*\/\s*[+-]?\d+(?:\.\d+)?$/.test(s)) return s;

  let m = /^(?:home|away|draw)\s+(?:over|under)\s+([-+]?\d+(?:\.\d+)?)\s*$/i.exec(s);
  if (m && isReliableNumericLineToken(m[1])) return m[1];
  m = /^(?:over|under)\s+([-+]?\d+(?:\.\d+)?)\s*$/i.exec(s);
  if (m && isReliableNumericLineToken(m[1])) return m[1];

  m = /\b(?:home|away)\s+(?:over|under)\s+([-+]?\d+(?:\.\d+)?)\s*$/i.exec(s);
  if (m && isReliableNumericLineToken(m[1])) return m[1];

  m = /\b(?:over|under)\s+([-+]?\d+(?:\.\d+)?)\s*$/i.exec(s);
  if (m && isReliableNumericLineToken(m[1]) && /\b(over|under|corner|goal|handicap|spread|total|player|game|set)\b/i.test(s)) {
    return m[1];
  }

  m = /\bplayer\s+(?:over|under)\s+([-+]?\d+(?:\.\d+)?)\s*$/i.exec(s);
  if (m && isReliableNumericLineToken(m[1])) return m[1];

  m = /\s([+-]\d+(?:\.\d+)?)\s*$/.exec(s);
  if (m && isReliableNumericLineToken(m[1]) && !/^\d+\s*-\s*\d+/.test(s)) return m[1];

  return "";
}

/**
 * Fallback line for Arb/Middles when `item.line` is empty: leg `line` fields, then Over/Under N in selection text.
 */
function extractLineFromArbMiddleItemLegs(item) {
  const buckets = [item?.picks, item?.legs];
  for (let b = 0; b < buckets.length; b += 1) {
    const bucket = buckets[b];
    if (!Array.isArray(bucket)) continue;
    for (let i = 0; i < bucket.length; i += 1) {
      const p = bucket[i];
      const ln =
        p?.line ??
        p?.handicap ??
        p?.total ??
        p?.point ??
        p?.lineA ??
        p?.lineB ??
        p?.overLine ??
        p?.underLine;
      if (isReliableNumericLineToken(ln)) return String(ln).trim();
    }
  }
  for (let b = 0; b < buckets.length; b += 1) {
    const bucket = buckets[b];
    if (!Array.isArray(bucket)) continue;
    for (let i = 0; i < bucket.length; i += 1) {
      const p = bucket[i];
      const sel = String(p?.sel ?? "").trim();
      const sel2 = String(p?.selection ?? "").trim();
      const fromSel = extractLineTokenFromPickSelectionText(sel);
      if (isReliableNumericLineToken(fromSel)) return fromSel;
      const fromSel2 = extractLineTokenFromPickSelectionText(sel2);
      if (isReliableNumericLineToken(fromSel2)) return fromSel2;
    }
  }
  return "";
}

function inferImplicitFullTimeScopeArbMiddle(baseRaw, sportTab) {
  const key = resolveMarketCanonicalKey(baseRaw) || "";
  const nk = normalizeLookupKey(key || baseRaw || "");
  const implicitFullTimeAllSports = new Set([
    "total corners",
    "corners over under",
    "team total corners",
    "team total corners over under",
    "asian handicap corners",
    "corner match bet",
    "corner winner",
    "total goals",
    "team total goals",
    "total games",
    "total sets",
    "totals",
    "total points",
    "over/under",
    "team total",
    "team total over under",
    "set handicap",
    "game handicap",
    "point spread",
    "asian handicap",
    "spread",
    "puck line",
    "run line"
  ]);
  if (implicitFullTimeAllSports.has(nk)) return "fullTime";
  if (sportTab !== "football") return null;
  if (nk === "moneyline 3 way") return "fullTime";
  if (new Set(["moneyline", "match winner", "winner"]).has(nk)) return "fullTime";
  return null;
}

function isLineBasedMarketForArbMiddle(baseRaw, rawForContext) {
  const nk = normalizeLookupKey(resolveMarketCanonicalKey(baseRaw) || baseRaw || "");
  const nkCtx = normalizeLookupKey(resolveMarketCanonicalKey(rawForContext || "") || rawForContext || baseRaw || "");
  const nonLine = new Set([
    "moneyline 3 way",
    "1x2",
    "btts",
    "draw no bet",
    "double chance",
    "correct score",
    "corners 1x2",
    "total corners 3 way",
    "exact corners",
    "exact goals number",
    "moneyline",
    "match winner",
    "winner",
    "corner winner",
    "set moneyline",
  ]);
  if (nonLine.has(nk) || nonLine.has(nkCtx)) return false;

  const lineFamilies = [
    "total goals",
    "team total goals",
    "total games",
    "total sets",
    "total points",
    "total corners",
    "corners over under",
    "team total corners",
    "team total corners over under",
    "asian handicap corners",
    "corners handicap",
    "point spread",
    "asian handicap",
    "spread",
    "totals",
    "team total",
    "team total over under",
    "over/under",
    "goal line",
    "handicap result",
    "european handicap",
    "set handicap",
    "game handicap",
    "puck line",
    "run line",
    "player made threes",
    "player points",
    "player rebounds",
    "player assists",
  ];
  for (const p of lineFamilies) {
    if (nk === p || nkCtx === p) return true;
  }
  for (const p of lineFamilies) {
    if (nk.startsWith(`${p} `) || nkCtx.startsWith(`${p} `)) return true;
  }
  if (/\b(over\s*\/\s*under|o\/u)\b/i.test(nkCtx) && !/moneyline/i.test(nkCtx)) return true;
  if (/\b(handicap|spread)\b/i.test(nkCtx) && !nonLine.has(nk)) return true;
  return false;
}

/**
 * Detects a time/period scope on the raw market and returns a stripped base string suitable for translation.
 */
function splitMarketPeriodForTranslation(rawMarket) {
  const raw = String(rawMarket ?? "").trim();
  if (!raw) return { baseRaw: "", scopeKey: null };
  const scopeKey = detectMarketPeriodScope(raw);
  if (!scopeKey) return { baseRaw: raw, scopeKey: null };
  // Some providers include the scope twice, e.g. "1st Half Total Goals - HT".
  // First remove a well-formed trailing alias token, then strip the explicit phrase.
  const withoutSuffix = stripTrailingPeriodScopeAlias(raw, scopeKey);
  const stripped = stripPeriodScopeFromMarketText(withoutSuffix, scopeKey);
  let baseTrim = cleanupMarketSeparatorsAfterScopeStrip(stripped);
  if (!baseTrim) return { baseRaw: raw, scopeKey: null };
  if (scopeKey === "firstSet" || scopeKey === "secondSet") {
    const bt = baseTrim.replace(/\s+/g, " ").trim();
    if (/^(moneyline|ml)$/i.test(bt) || /^set\s+moneyline$/i.test(bt)) {
      baseTrim = "set moneyline";
    }
  }
  return { baseRaw: baseTrim, scopeKey };
}

function translateLeagueDisplay(value, lang = ANALYSIS_DEFAULT_LANG) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const normalized = normalizeLeagueLookupKey(raw);
  const compact = compactLeagueLookupKey(raw);

  const sharpExactKey = resolveExactKnownSharpLeagueCanonical(normalized, compact);
  if (sharpExactKey) {
    return getLeagueTranslatedMapValue(LEAGUE_TRANSLATIONS_MAP, sharpExactKey, lang, raw, "league");
  }

  const direct = LEAGUE_TRANSLATIONS_MAP[normalized] || LEAGUE_TRANSLATIONS_MAP[compact];
  const skipAmbiguousBarePremier =
    AMBIGUOUS_BARE_PREMIER_LEAGUE_LOOKUP.has(normalized) || AMBIGUOUS_BARE_PREMIER_LEAGUE_LOOKUP.has(compact);
  const effectiveDirect = skipAmbiguousBarePremier ? null : direct;

  const resolvedKey = effectiveDirect ? raw : resolveLeagueKey(raw);
  const keepAsIs = shouldKeepLeagueAsIs(resolvedKey);

  // Base label (existing pipeline, unchanged behavior).
  let baseLabel = "";
  if (effectiveDirect) {
    baseLabel = effectiveDirect[lang] || effectiveDirect[ANALYSIS_DEFAULT_LANG] || raw;
  } else if (resolvedKey) {
    baseLabel = getLeagueTranslatedMapValue(LEAGUE_TRANSLATIONS_MAP, resolvedKey, lang, raw, "league");
  } else {
    const patterned = translateLeagueByPattern(raw, lang);
    if (patterned && String(patterned).trim()) baseLabel = patterned;
    else {
      const prettyLeague = prettifyLeagueCodeFallback(raw);
      if (prettyLeague) baseLabel = prettyLeague;
      else baseLabel = raw;
    }
  }

  if (keepAsIs) return baseLabel;

  // Country suffix layer (conservative; only when country is confidently known).
  const matchedCountry = matchCountryPrefixOrSuffix(raw);
  if (matchedCountry && matchedCountry.countryKey) {
    const remainderLabel = translateRemainderLeagueLabel(matchedCountry.remainder, lang);
    const leagueLabel = remainderLabel || baseLabel;
    const suffixed = translateCountrySuffixedLeagueLabel(leagueLabel, matchedCountry.countryKey, lang);
    if (suffixed) return suffixed;
  }

  const inferredCountryKey = inferDomesticCountryKeyFromLeague(raw);
  if (inferredCountryKey) {
    const suffixed = translateCountrySuffixedLeagueLabel(baseLabel, inferredCountryKey, lang);
    if (suffixed) return suffixed;
  }

  // Preserve prior fallback/warn behavior.
  if (!effectiveDirect && !resolvedKey) warnUnmapped("league", raw, normalized);
  return baseLabel;
}

function translateSportDisplay(value, lang = ANALYSIS_DEFAULT_LANG) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const tab = classifySportForAnalysisTab(raw, {});
  if (tab === "unsupported") {
    return prettifyUnknownMarketKey(raw) || raw;
  }
  if (tab === "football") {
    return getTranslatedMapValue(SPORT_TRANSLATIONS_MAP, "football", lang, raw, "sport");
  }
  if (tab === "tennis") {
    return getTranslatedMapValue(SPORT_TRANSLATIONS_MAP, "tennis", lang, raw, "sport");
  }
  if (tab === "basketball") {
    return getTranslatedMapValue(SPORT_TRANSLATIONS_MAP, "basketball", lang, raw, "sport");
  }
  if (tab === "esports") {
    return getTranslatedMapValue(SPORT_TRANSLATIONS_MAP, "esports", lang, raw, "sport");
  }

  return prettifyUnknownMarketKey(raw) || raw;
}

function mapFixtureBetToLabel(value, lang = ANALYSIS_DEFAULT_LANG) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const normalized = normalizeLookupKey(raw);
  const compact = compactLookupKey(raw);
  const direct = SELECTION_TRANSLATIONS_MAP[normalized] || SELECTION_TRANSLATIONS_MAP[compact];

  if (direct) {
    return direct[lang] || direct[ANALYSIS_DEFAULT_LANG] || raw;
  }

  const translatedPrefixed = translatePrefixedSelection(raw, lang);
  if (translatedPrefixed !== raw) return translatedPrefixed;

  warnUnmapped("selection", raw, normalized);
  return raw;
}

function translateMarketDisplay(value, lang = ANALYSIS_DEFAULT_LANG) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const shorthandExpanded = expandMarketShorthand(raw);
  const source = shorthandExpanded !== raw ? shorthandExpanded : raw;

  const normalized = normalizeLookupKey(source);
  const compact = compactLookupKey(source);
  let direct = MARKET_TRANSLATIONS_MAP[normalized] || MARKET_TRANSLATIONS_MAP[compact];

  if (direct) {
    return direct[lang] || direct[ANALYSIS_DEFAULT_LANG] || raw;
  }

  const phraseNorm = normalizeMarketPhraseForLookup(source);
  if (phraseNorm !== normalized) {
    const pc = compactLookupKey(phraseNorm);
    direct = MARKET_TRANSLATIONS_MAP[phraseNorm] || MARKET_TRANSLATIONS_MAP[pc];
    if (direct) {
      return direct[lang] || direct[ANALYSIS_DEFAULT_LANG] || raw;
    }
  }

  let resolvedKey = resolveMarketCanonicalKey(source);
  if (resolvedKey) {
    const tr = peekTranslatedMapValue(MARKET_TRANSLATIONS_MAP, resolvedKey, lang, null);
    if (tr) return tr;
  }

  if (phraseNorm !== normalizeLookupKey(source)) {
    resolvedKey = resolveMarketCanonicalKey(phraseNorm);
    if (resolvedKey) {
      const tr = peekTranslatedMapValue(MARKET_TRANSLATIONS_MAP, resolvedKey, lang, null);
      if (tr) return tr;
    }
  }

  const patterned = tryPatternBasedMarketTranslation(source, lang);
  if (patterned) return patterned;

  warnUnmapped("market", raw, normalized);
  return prettifyUnknownMarketKey(raw) || raw;
}

function translateSelectionDisplay(value, lang = ANALYSIS_DEFAULT_LANG) {
  return mapFixtureBetToLabel(value, lang);
}

module.exports = {
  normalizeLookupKey,
  normalizeEsportsGameLabel,
  detectMarketPeriodScope,
  normalizeSharpMarketKeyForDisplay,
  resolveMarketCanonicalKey,
  classifySportForAnalysisTab,
  stripPeriodScopeFromMarketText,
  extractEmbeddedLineFromRawMarket,
  extractLineFromArbMiddleItemLegs,
  inferImplicitFullTimeScopeArbMiddle,
  isLineBasedMarketForArbMiddle,
  splitMarketPeriodForTranslation,
  translateLeagueDisplay,
  translateSportDisplay,
  mapFixtureBetToLabel,
  translateMarketDisplay,
  translateSelectionDisplay,
};